import {
  asId,
  type ElementSegmentId,
  type JobRoleId,
  type OpeningId,
  type PileCapTypeId,
  type ProjectId,
  type RoomId,
  type ShaftId,
  type UnitPositionId,
  type StructuralElementId,
  type StructuralElementTypeId,
  type StructuralPieceId,
  type UnitTypeId,
  type UserId,
  type WallId,
} from '@obraplan/core/domain/shared/ids';
import {
  createRoom,
  GEOMETRY_DECIMALS,
  type Room,
  type RoomKind,
} from '@obraplan/core/domain/takeoff/room';
import {
  DEFAULT_RAILING_HEIGHT,
  EDGE_KIND_LABEL,
  edgeKindAt,
  edgeWithoutWallAt,
  normalizeEdgesWithoutWall,
  withEdgeKind,
  type EdgeKind,
  type EdgeWithoutWall,
} from '@obraplan/core/domain/takeoff/room-edge';
import {
  createOpening,
  createWall,
  joinWall,
  spanOf,
  splitWall,
  wallSideOf,
  withEdgeIndex,
  withEdgeSpan,
  type EdgeSpan,
  type Opening,
  type OpeningKind,
  type Wall,
  type WallSide,
} from '@obraplan/core/domain/takeoff/wall';
import {
  checkElementOutline,
  createElementSegment,
  createStructuralElement,
  type ElementOrientation,
  type ElementSegment,
  type StructuralElement,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import type { UnitTypeKind } from '@obraplan/core/domain/takeoff/unit-type';
import { createShaft, type Shaft } from '@obraplan/core/domain/takeoff/shaft';
import {
  checkGroundBeamFields,
  checkPileCapFields,
  type GroundBeam,
  type PileCap,
} from '@obraplan/core/domain/takeoff/foundation';
import {
  checkPileCapTypeFields,
  type PileCapType,
} from '@obraplan/core/domain/takeoff/pile-cap-type';
import {
  checkElementTypeFields,
  checkElementTypeOf,
  type StructuralElementType,
} from '@obraplan/core/domain/takeoff/element-type';
import {
  checkSlabFields,
  type Slab,
  type SlabKind,
  type SlabRegion,
} from '@obraplan/core/domain/takeoff/slab';
import {
  checkRetainingColumnFields,
  checkRetainingWallFields,
  isColumnInWall,
  retainingColumnsOverlap,
  type RetainingColumn,
  type RetainingWall,
  wallAngle,
} from '@obraplan/core/domain/takeoff/retaining';
import {
  checkStairFields,
  type Stair,
  type StairFlight,
  type StairLanding,
} from '@obraplan/core/domain/takeoff/stair';
import {
  checkLengthOverride,
  checkPileFields,
  type PieceMark,
  type PileType,
  type StructuralPiece,
} from '@obraplan/core/domain/takeoff/pile';
import type { IndexAverage } from '@obraplan/core/domain/takeoff/index-origin';
import type { ItemFactor, RoomKindAverage } from '@obraplan/core/domain/takeoff/own-factor';
import { checkItemFactors, withOwnFactor } from '@obraplan/core/domain/takeoff/own-factor';
import {
  isValidOutline,
  polygonEdges,
  segmentLength,
  sharedEdges,
  sharedRun,
  WALL_PAIRING,
  type Point,
  type Segment,
} from '@obraplan/core/domain/takeoff/polygon';
import { MIN_WALL_PIECE, spanLength, subSegment } from '@obraplan/core/domain/takeoff/wall-span';
import { placeWalls, rememberWallEdges, sideAlong } from './plan-tracer';
import { itensParaPropagarCodigo, outroItemComCodigo } from './codigo-de-compras';
import { comAListaDaTaxa, listaDaTaxa } from './taxa-por-elemento';
import type { BitolaDaMediaNoTipo, LinhaDaMediaNoTipo } from './media-no-tipo';
import {
  casarComCorte,
  cortarParede,
  corteParaCasar,
  descasarEJuntar,
  juntarPedacos,
  mantendoOLugar,
  medidaDoTrecho,
  medirNaQueFica,
  reajustarCasada,
  semOsPedacosQueSairam,
  SEM_TRECHO_COMUM,
  trechoEmpurrado,
  trechosLivres,
} from './casar-paredes';
import {
  alwaysHasUnits,
  FLOOR_PLAN_KIND_ORDER,
  planFloorPlans,
  type FloorPlanKind,
  type FloorPlanSpec,
} from '@obraplan/core/domain/takeoff/floor-plan';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import type { MeasureBy } from '@obraplan/core/domain/takeoff/geometry';
import { resolveAppliesTo, type AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import type { ItemKind, OverrideEffect } from '@obraplan/core/domain/takeoff/takeoff-line';
import { roundQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import { decideItemOverride, itemQuantityKey } from '@obraplan/core/domain/takeoff/consolidation';
import {
  addDiscipline,
  addService,
  emptyCatalog,
  itemDescription,
  guessItemKind,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  isSteelPurchaseService,
  normalizeSteelRates,
  parseSteelGauge,
  type SteelGauge,
  type SteelRate,
  type SteelRateAverage,
} from '@obraplan/core/domain/takeoff/steel';
import {
  decideInServiceUnit,
  decideMeasured,
  serviceMeasuring,
  type ServiceQuantityDecision,
} from '@obraplan/core/domain/takeoff/service-quantity';
import {
  juntarQuantidadesPorPeca,
  nomeDaPeca,
  pecasImportadas,
  renomearPecaNasQuantidades,
  semQuantidadesPorPeca,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';

/**
 * O rascunho da tipologia — estado da tela, ainda sem persistência.
 *
 * Existe separado do componente porque é aqui que mora a parte que pode errar: gerar as
 * quatro paredes de um ambiente, **casar** duas delas numa só e desfazer o casamento. O
 * componente desenha; isto decide. E, sendo função pura, o teste percorre o fluxo inteiro
 * sem montar tela nenhuma.
 *
 * A tela é um **protótipo de uso**: serve para descobrir, com o engenheiro, se montar seis
 * tipologias na mão é tolerável — que é o risco que o plano da Fase 14 aponta antes de
 * valer a pena escrever migração.
 */

/**
 * O comprimento da parede que nasce de um lado do contorno.
 *
 * Arredondado às duas casas da geometria: o traço é clique de mouse convertido por uma
 * escala, e `5,6 − 3,4` em ponto flutuante dá 2,1999999999999997. O número entra no
 * cadastro e reaparece no campo da parede com sete casas — uma medida que ninguém digitou
 * e que nenhuma trena tem.
 */
function comprimentoDoLado(lado: Segment): number {
  return roundQuantity(segmentLength(lado), GEOMETRY_DECIMALS);
}

/** Rótulo das quatro paredes que nascem com o ambiente. Só orienta quem monta. */
export const WALL_FACES = ['Norte', 'Leste', 'Sul', 'Oeste'] as const;
export type WallFace = (typeof WALL_FACES)[number];

export const DEFAULT_WALL_HEIGHT = 2.9;
export const DEFAULT_WALL_THICKNESS = 0.15;
export const DEFAULT_CEILING_HEIGHT = 2.6;
/** A mureta da varanda — a parede baixa que leva o guarda-corpo em cima (D25). */
export const DEFAULT_MURETA_HEIGHT = 1.1;

/**
 * O que a tela oferece para cada lado do contorno.
 *
 * A mureta aparece como escolha ao lado das outras três, mas não é tipo de lado: é parede,
 * com a altura dela e o guarda-corpo em cima marcado na parede (`Wall.railingHeight`). Para
 * quem monta, são quatro jeitos de fechar a varanda, e é assim que a lista os mostra.
 */
export type EdgeChoice = EdgeKind | 'MURETA';

export const EDGE_CHOICE_LABEL: Readonly<Record<EdgeChoice, string>> = {
  PAREDE: EDGE_KIND_LABEL.PAREDE,
  MURETA: 'Mureta com guarda-corpo',
  GUARDA_CORPO: EDGE_KIND_LABEL.GUARDA_CORPO,
  ABERTO: EDGE_KIND_LABEL.ABERTO,
};

/**
 * Como a parede se chama.
 *
 * `a` e `b` são os lados, montados sozinhos a partir dos ambientes; `custom` é o nome que
 * a pessoa deu. O automático continua guardado por baixo — apagar o nome livre devolve
 * "Sala · Leste ↔ Cozinha · Oeste" em vez de deixar a parede sem identificação nenhuma.
 */
export interface WallLabel {
  readonly a: string;
  readonly b?: string;
  readonly custom?: string;
}

export interface TypologyDraft {
  readonly unitTypeId: UnitTypeId;
  readonly name: string;
  /**
   * Apartamento ou área comum.
   *
   * A área comum é uma tipologia como as outras, com outro tipo — escada e circulação são
   * ambientes como quarto e sala. Ter o campo aqui é o que evita a tela inventar um
   * segundo caminho para montar o hall.
   */
  readonly kind: UnitTypeKind;
  /**
   * A que **pavimento** esta tipologia pertence.
   *
   * O garden, a cobertura, o térreo e a garagem não são o pavimento tipo: têm desenho
   * próprio, ambientes próprios e acontecem um número próprio de vezes. Sem este campo,
   * toda tipologia pertencia implicitamente ao tipo, e a única saída para a cobertura era
   * gravar "acontece 1 vez" na mão — um número solto, que ninguém conseguia conferir
   * contra a estrutura.
   *
   * Com ele, o multiplicador de cada uma **sai da estrutura**: quantos pavimentos daquele
   * tipo a torre tem. Um garden dá um; doze pavimentos tipo dão doze.
   */
  readonly floorKind: FloorPlanKind;
  /**
   * O desenho de pavimento a que a tipologia pertence — vem da posição no servidor, como o
   * `floorKind`. É ele que separa dois desenhos do mesmo tipo (o "Pavimento tipo" e um
   * segundo desenho de tipo para o 6º andar): agrupar só pelo tipo os juntava num só, com as
   * mesmas tipologias e a mesma contagem de pavimentos. Ausente (rascunho sem servidor, ou
   * tipologia ainda sem posição), vale o tipo, como antes.
   */
  readonly floorPlanId?: string;
  /**
   * Quantas vezes esta tipologia acontece — o multiplicador do total (RN-167).
   *
   * **Ausente é o caso comum**, e significa "repete com os pavimentos tipo": o número sai
   * da estrutura, que é quem sabe quantos pavimentos tipo a torre tem. Digitá-lo à mão
   * era convidar a divergência — a estrutura dizia doze e o multiplicador continuava no
   * vinte com que a tipologia nasceu, sem nada na tela denunciando.
   *
   * Presente é a exceção que não repete: a cobertura acontece **uma vez**, por mais
   * pavimentos tipo que o prédio tenha.
   */
  readonly floors?: number;
  /**
   * Quantas vezes ela acontece **dentro de um pavimento** — a garagem com quarenta vagas.
   *
   * Ausente é uma, e é todo apartamento e toda área comum: eles existem um por posição,
   * porque a divisa entre o apto 01 e o 02 precisa ser de alguém (RN-163). Vaga não tem
   * essa necessidade — vaga é vaga —, e cadastrar quarenta tipologias para dizer um
   * número seria trabalho por nada.
   *
   * Vem da **posição no servidor**, como o `floorKind`, e não do rascunho: é identidade,
   * e identidade não mora no navegador.
   */
  readonly unitsPerFloor?: number;
  /**
   * A aba da planilha importada que orça **esta** tipologia.
   *
   * É o que torna a comparação possível: a aba "Apartamento Tipo 1" diz 508,85 m² de
   * assentamento, e o contorno traçado diz outra coisa. Sem o vínculo, os dois números
   * existem na mesma tela sem se olharem — e a pergunta que a fase inteira existe para
   * responder ("o orçamento que já rodou bate com o desenho?") continua sem resposta.
   *
   * Ausente é o caso comum enquanto não há planilha, e não erro.
   */
  readonly sheetName?: string;
  /**
   * A quantidade de cada item, quando alguém discordou da planilha.
   *
   * Por `itemQuantityKey(serviceKey, itemKey)`, e **só o que foi decidido**: a ausência
   * significa "segue a planilha", que é o caso comum. Guardar uma cópia de cada
   * quantidade importada transformaria "ninguém mexeu nisto" em "alguém confirmou isto",
   * e as duas se resolvem de formas diferentes na hora de reimportar o arquivo.
   *
   * Mora na **tipologia**, e não no catálogo, porque é ela quem lança: o catálogo diz
   * quais itens o serviço usa; a tipologia diz quanto (RN-167).
   */
  readonly itemQuantities?: Readonly<Record<string, number>>;
  /**
   * Por que o número digitado discorda do **calculado** (Fase 24, fatia 6), pela mesma
   * chave de `itemQuantities`. Só existe ao lado de um digitado, e só quando havia calculado
   * a discordar (D9).
   */
  readonly itemReasons?: Readonly<Record<string, string>>;
  /**
   * O número de cada serviço que esta tipologia decidiu (Fase 24, fatia 5), pela chave do
   * serviço.
   *
   * **Só o decidido**, como a quantidade do item: ausente é "segue o traçado" no serviço
   * que o desenho mede, e "ainda sem número" no que ele não mede. É por tipologia porque o
   * traçado também é (D2) — a cobertura corrige o reboco dela, e o apartamento tipo não.
   */
  readonly serviceQuantities?: Readonly<Record<string, ServiceQuantityDecision>>;
  /**
   * A quantidade **pronta** de cada peça importada pela planilha por peça (Fase 28, RN-384) —
   * por serviço e por item, e dentro deles pelo nome da peça ("B24").
   *
   * Não é índice nem desenho: é o número da planilha, e ele conta já, antes de a peça ser
   * posicionada (D3). Na conta do item soma-se ao calculado e perde para o digitado; a peça
   * desenhada com o mesmo nome sai da medida do desenho (`semPecasImportadas`). Reimportar
   * troca as peças que vieram e deixa as outras (RN-386, `IMPORT_PIECE_QUANTITIES`).
   *
   * **Presente, mesmo vazio, quer dizer "esta tela descreve as peças desta tipologia"**: é o
   * que põe a tipologia em `pieceQuantityUnitTypes` na gravação (RN-390), e é assim que tirar
   * o último serviço com peça apaga as do servidor. Ausente é a tipologia que nunca teve peça
   * importada — e a gravação não mexe nas dela, nem que outra aba as tenha importado agora.
   */
  readonly pieceQuantities?: QuantidadesPorPeca;
  /**
   * A lembrança das peças renomeadas (20/10/2026, RN-458): o nome que a planilha escreve
   * (comparável, `nomeDaPeca`) → o nome da peça hoje. A reimportação traduz um no outro.
   * Vem do servidor, que é quem renomeia; a gravação dos itens não a leva de volta.
   */
  readonly pieceNameAliases?: Readonly<Record<string, string>>;
  readonly rooms: readonly Room[];
  readonly walls: readonly Wall[];
  readonly openings: readonly Opening[];
  /**
   * Pilar e viga do desenho (Fase 15).
   *
   * Ficam ao lado das paredes, e não dentro delas: a viga atravessa três paredes e tem uma
   * seção só (ADR-115). É `elementSegments` que diz onde cada uma cruza.
   */
  readonly elements: readonly StructuralElement[];
  readonly elementSegments: readonly ElementSegment[];
  /**
   * Os buracos na laje (Fase 24, D21) — cada um no ambiente de cujo piso e teto desconta.
   *
   * Opcional porque o rascunho guardado antes do shaft não tem a chave, e ausente é o mesmo
   * que nenhum.
   */
  readonly shafts?: readonly Shaft[];
  /**
   * As peças da fundação desenhadas nesta tipologia — por enquanto, as estacas (Fase 27).
   *
   * Só a área comum do desenho de Fundação ou Contenção as tem (RN-361). A estaca sem
   * `center` está na fila do "faltam N" e não conta (RN-364).
   */
  readonly pieces?: readonly StructuralPiece[];
  /** As estacas marcadas para cada serviço medido por `MARKED` (RN-365). */
  readonly pieceMarks?: readonly PieceMark[];
  /** Os blocos desenhados — contorno e altura (Fase 27, etapa 2, D3). */
  readonly pileCaps?: readonly PileCap[];
  /** As vigas baldrame — eixo e seção; o trecho dentro do bloco não conta (RN-368). */
  readonly groundBeams?: readonly GroundBeam[];
  /**
   * O radier e a laje — contorno, espessura e regiões (Fase 27, etapa 3, D7). O radier é da
   * área comum da Fundação; a laje, da área comum do desenho do andar (F3).
   */
  readonly slabs?: readonly Slab[];
  /** Os muros de contenção e as cortinas — eixo, altura e espessura (Fase 27, etapa 4, D9). */
  readonly retainingWalls?: readonly RetainingWall[];
  /** Os pilares de contenção — centro, seção, altura e o ângulo do muro (F6, RN-372). */
  readonly retainingColumns?: readonly RetainingColumn[];
  /**
   * As escadas de concreto — lances e patamares (Fase 27, etapa 5, D10). Na área comum do
   * desenho do andar, por cima do ambiente ESCADA, que mede o acabamento (F17).
   */
  readonly stairs?: readonly Stair[];
  readonly labels: Readonly<Record<string, WallLabel>>;
  /** A última recusa do domínio, para a tela dizer o que aconteceu. */
  readonly lastError?: string;
}

export type DraftAction =
  | { readonly type: 'SET_NAME'; readonly name: string }
  | {
      readonly type: 'ADD_ROOM';
      readonly name: string;
      readonly kind: RoomKind;
      readonly width: number;
      readonly length: number;
      /**
       * Altura da parede — **até a laje**, e não até o forro.
       *
       * É ela que mede a alvenaria: comprimento × altura. Era uma constante de 2,90 que
       * a tela não mostrava e ninguém podia mudar, enquanto o pé-direito digitado media
       * só o revestimento — dois números diferentes para a mesma parede, e um deles
       * escondido. Ausente é o padrão, para quem não quiser pensar nisso.
       */
      readonly wallHeight?: number;
      readonly ceilingHeight: number;
    }
  | { readonly type: 'UPDATE_ROOM'; readonly roomId: RoomId; readonly patch: Partial<Room> }
  | { readonly type: 'REMOVE_ROOM'; readonly roomId: RoomId }
  | { readonly type: 'RESET_ROOM_GEOMETRY'; readonly roomId: RoomId }
  | {
      /**
       * O fator próprio de um item neste cômodo, nos serviços que descem a cômodo, por cima do
       * índice do item (Fase 39, D14, RN-512) — sem motivo (RN-513). `factor` ausente é a volta
       * ao índice.
       */
      readonly type: 'SET_ROOM_FACTOR';
      readonly roomId: RoomId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
      /**
       * A média do tipo de cômodo (Fase 40, RN-528): `MEDIA`, com a foto. Ausente é alguém
       * digitando — `DIGITADO`, com a foto da média que havia mantida ao lado.
       */
      readonly origin?: 'MEDIA';
      readonly average?: RoomKindAverage;
    }
  | {
      readonly type: 'ADD_TRACED_ROOM';
      readonly name: string;
      readonly kind: RoomKind;
      /** A altura da alvenaria, como no ambiente digitado. Ausente é o padrão. */
      readonly wallHeight?: number;
      readonly ceilingHeight: number;
      readonly outline: readonly Point[];
    }
  | {
      /**
       * Delimita na planta um ambiente que **já existe**.
       *
       * É o caminho de quem cadastrou o ambiente primeiro e vai à prancha depois: escolhe
       * o ambiente na lista e desenha o contorno dele. Sem isto, traçar sempre criava um
       * ambiente novo, e quem quisesse delimitar a Sala que já estava lá ficava com duas.
       */
      readonly type: 'SET_ROOM_OUTLINE';
      readonly roomId: RoomId;
      readonly outline: readonly Point[];
    }
  | {
      /**
       * O mesmo desenho, com um lado no lugar certo — **sem refazer parede nenhuma**.
       *
       * É o empurrão que se dá na parede depois de traçar por cima da prancha: o clique
       * caiu meio metro fora, e o `SET_ROOM_OUTLINE` corrigiria isso apagando as paredes
       * livres do ambiente — e com elas a porta, o pilar e o nome de cada uma. Aqui o
       * contorno muda e as paredes **continuam sendo as mesmas**: só o comprimento delas
       * acompanha o lado que se moveu.
       *
       * Exige o mesmo número de vértices, que é o que garante que cada lado continua
       * sendo o mesmo lado. Acrescentar ou tirar canto é redesenhar, e redesenhar tem a
       * outra ação.
       */
      readonly type: 'RESHAPE_ROOM';
      readonly roomId: RoomId;
      readonly outline: readonly Point[];
    }
  | {
      /** Desfaz o traçado — o ambiente continua, a área volta a sair das paredes. */
      readonly type: 'CLEAR_ROOM_OUTLINE';
      readonly roomId: RoomId;
    }
  | {
      /**
       * O que um lado do contorno é: parede, mureta com guarda-corpo, guarda-corpo ou aberto
       * (Fase 24, D25).
       *
       * Guarda-corpo e aberto **tiram** a parede daquele lado, e com ela os vãos e as peças
       * que estavam nela — quem pede a confirmação é a tela. Parede e mureta a trazem de
       * volta, nova, se o lado estava sem. Divisa é sempre parede (RN-269).
       */
      readonly type: 'SET_EDGE_KIND';
      readonly roomId: RoomId;
      readonly edgeIndex: number;
      readonly kind: EdgeChoice;
    }
  | {
      /** A altura do guarda-corpo do lado sem parede. A do de cima da mureta é da parede. */
      readonly type: 'SET_EDGE_RAILING_HEIGHT';
      readonly roomId: RoomId;
      readonly edgeIndex: number;
      readonly height: number;
    }
  | { readonly type: 'ADD_WALL'; readonly roomId: RoomId }
  | { readonly type: 'RENAME_WALL'; readonly wallId: WallId; readonly name: string }
  | { readonly type: 'UPDATE_WALL'; readonly wallId: WallId; readonly patch: Partial<Wall> }
  | {
      readonly type: 'MERGE_WALLS';
      readonly keepId: WallId;
      readonly dropId: WallId;
      /**
       * Interno: as duas já chegam cortadas no trecho comum, e casam como estão. Quem corta
       * é `casarComCorte`, que despacha o casamento de sempre com isto ligado (RN-305).
       */
      readonly cortadas?: boolean;
    }
  | {
      readonly type: 'UNMERGE_WALL';
      readonly wallId: WallId;
      /** Interno: descasa sem juntar os pedaços vizinhos — quem junta é `descasarEJuntar`. */
      readonly semJuntar?: boolean;
    }
  | { readonly type: 'TOGGLE_JOIN'; readonly wallId: WallId }
  | {
      readonly type: 'ADD_OPENING';
      readonly wallId: WallId;
      readonly kind: OpeningKind;
      /** Metros. Ausentes, o padrão do tipo — 0,80 × 2,10 a porta, 1,20 × 1,20 a janela. */
      readonly width?: number;
      readonly height?: number;
      /** Onde o vão foi posto na parede, pela prancha — metros do começo dela. */
      readonly offset?: number;
    }
  | {
      readonly type: 'UPDATE_OPENING';
      readonly openingId: OpeningId;
      readonly patch: Partial<Opening>;
    }
  | { readonly type: 'REMOVE_OPENING'; readonly openingId: OpeningId }
  | {
      /**
       * Põe um pilar ou uma viga **nesta** parede.
       *
       * Nasce com a seção que a obra mais usa e com a corrida da própria parede — altura
       * até a laje para o pilar, comprimento para a viga. São os dois números que quem
       * cadastra ia digitar iguais aos da parede em nove de cada dez vezes.
       */
      readonly type: 'ADD_ELEMENT';
      readonly wallId: WallId;
      readonly kind: StructuralElementKind;
    }
  | {
      /**
       * A peça lida da prancha — o segundo caminho de entrada (fatia 5).
       *
       * Chega com os trechos **já resolvidos**: é o traço que sabe em quais paredes a peça
       * caiu, e refazer a conta aqui seria refazê-la sem a prancha. O que o desenho não
       * mostra — a altura do pilar, a altura da seção da viga — vem digitado junto, e não
       * com um palpite: é a altura da viga que come a alvenaria (RN-183).
       */
      readonly type: 'ADD_TRACED_ELEMENT';
      readonly kind: StructuralElementKind;
      readonly sectionWidth: number;
      readonly sectionDepth: number;
      readonly length: number;
      /**
       * O contorno traçado, em metros (06/10/2026) — o pilar ou a viga em polígono, ou o
       * retângulo que não encostou em parede nenhuma. Com ele, `segments` pode vir vazio: a
       * peça sabe onde está sem parede. Sem ele, continua sendo preciso ao menos um trecho.
       */
      readonly outline?: readonly Point[];
      readonly segments: readonly {
        readonly wallId: WallId;
        readonly orientation: ElementOrientation;
        readonly length?: number;
        /** Onde a peça caiu nesta parede, em metros do começo dela. */
        readonly offset?: number;
        /** Quanto ela ficou fora do eixo da parede, em metros, com sinal. */
        readonly lateral?: number;
      }[];
    }
  | {
      readonly type: 'UPDATE_ELEMENT';
      readonly elementId: StructuralElementId;
      readonly patch: Partial<StructuralElement>;
    }
  | {
      readonly type: 'UPDATE_ELEMENT_SEGMENT';
      readonly segmentId: ElementSegmentId;
      readonly patch: Partial<ElementSegment>;
    }
  | {
      /**
       * A mesma viga também cruza **esta** parede (fatia 4).
       *
       * É o que impede o cadastro de três vigas para uma viga: ela tem uma seção só, e o
       * comprimento dela passa a ser a soma dos trechos. Nasce atravessando, que é o caso
       * de quem acrescenta uma parede no caminho de uma viga que já corria.
       */
      readonly type: 'ADD_ELEMENT_SEGMENT';
      readonly elementId: StructuralElementId;
      readonly wallId: WallId;
    }
  | {
      /** Tira a viga de uma parede. Era o último trecho? Então o elemento vai com ele. */
      readonly type: 'REMOVE_ELEMENT_SEGMENT';
      readonly segmentId: ElementSegmentId;
    }
  | { readonly type: 'REMOVE_ELEMENT'; readonly elementId: StructuralElementId }
  | {
      /**
       * O shaft traçado na prancha: dois cantos opostos, em metros, e o ambiente em que ele
       * caiu (Fase 24, D21). O ambiente vem resolvido pelo traço, como os trechos da peça —
       * é a prancha que sabe onde o buraco está.
       */
      readonly type: 'ADD_SHAFT';
      readonly roomId: RoomId;
      readonly from: Point;
      readonly to: Point;
      /** Quantos iguais. Ausente é um. */
      readonly count?: number;
    }
  | {
      /** A quantidade de um shaft — o lugar é o traço, e mudar de lugar é traçar de novo. */
      readonly type: 'SET_SHAFT_COUNT';
      readonly shaftId: ShaftId;
      readonly count: number;
    }
  | { readonly type: 'REMOVE_SHAFT'; readonly shaftId: ShaftId }
  | {
      /**
       * A estaca traçada na prancha: o centro, em metros, o tipo e o nome (Fase 27, D1).
       * O nome não se repete no desenho (RN-376) e o tipo é obrigatório (RN-362).
       */
      readonly type: 'ADD_PILE';
      readonly name: string;
      readonly pileTypeId: PileType['id'];
      readonly center: Point;
    }
  | {
      /** A estaca da fila do "faltam N" ganha o lugar dela (D5). Ela passa a contar (RN-364). */
      readonly type: 'PLACE_PILE';
      readonly pieceId: StructuralPieceId;
      readonly center: Point;
    }
  | {
      /** A estaca arrastada para outro lugar — só o centro muda. */
      readonly type: 'MOVE_PILE';
      readonly pieceId: StructuralPieceId;
      readonly center: Point;
    }
  | {
      /**
       * Corrigir a estaca: o nome, o tipo ou o comprimento — este com motivo (RN-362).
       *
       * Ausente é "não mexe". Para desfazer a correção do comprimento, vale o do tipo:
       * igual ao dele não é correção, e o motivo sai junto.
       */
      readonly type: 'UPDATE_PILE';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly pileTypeId?: PileType['id'];
      readonly lengthOverride?: number;
      readonly lengthReason?: string;
    }
  | {
      /**
       * O fator próprio de um item nesta estaca, por cima do fator do tipo (Fase 39, D14,
       * RN-511) — sem motivo (RN-513). `factor` ausente é a volta ao do tipo.
       */
      readonly type: 'SET_PIECE_FACTOR';
      readonly pieceId: StructuralPieceId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
    }
  | {
      /** Sai a estaca, e as marcas dela vão junto — marca de quem não existe mente na conta. */
      readonly type: 'REMOVE_PILE';
      readonly pieceId: StructuralPieceId;
    }
  | {
      /** A estaca entra ou sai da marca de um serviço (D4, RN-365). Só a posicionada. */
      readonly type: 'TOGGLE_PIECE_MARK';
      readonly pieceId: StructuralPieceId;
      readonly serviceKey: string;
    }
  | {
      /**
       * Marca as estacas posicionadas para um serviço — com o tipo, só as dele (D4).
       * A que está na fila não conta e não se marca.
       */
      readonly type: 'MARK_ALL_PIECES';
      readonly serviceKey: string;
      readonly pileTypeId?: PileType['id'];
    }
  | { readonly type: 'UNMARK_ALL_PIECES'; readonly serviceKey: string }
  | {
      /**
       * O bloco traçado na prancha: o contorno em metros — retângulo por dois cantos ou
       * polígono livre — e a altura (Fase 27, etapa 2, D3). O nome não se repete entre os
       * blocos do desenho.
       */
      readonly type: 'ADD_PILE_CAP';
      readonly name: string;
      readonly outline: readonly Point[];
      readonly height: number;
    }
  | {
      /** Corrigir o bloco: o nome, o contorno (arrastar o bloco inteiro) ou a altura. */
      readonly type: 'UPDATE_PILE_CAP';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly outline?: readonly Point[];
      readonly height?: number;
    }
  | { readonly type: 'REMOVE_PILE_CAP'; readonly pieceId: StructuralPieceId }
  | {
      /**
       * Dar o tipo de bloco a um ou a vários blocos de uma vez (Fase 41, RN-537) — o card dá a
       * um, a lista marca vários. `typeId` ausente tira o tipo: o bloco volta a seguir a obra.
       * O bloco da planilha por peça não recebe tipo (RN-540), mas tirar continua valendo.
       */
      readonly type: 'SET_PILE_CAP_TYPE_OF';
      readonly pieceIds: readonly StructuralPieceId[];
      readonly typeId?: PileCapTypeId;
    }
  | {
      /**
       * O fator próprio de um item no bloco, no baldrame ou no radier, por cima do tipo (no
       * bloco) ou do índice da obra (Fase 41, RN-538) — sem motivo (D9). `factor` ausente é a
       * volta ao de baixo. A laje do pavimento não leva: o fator dela é o do apartamento.
       * Desde a Fase 42 (etapa 3 — G3, RN-571), a escada, o muro e o pilar de contenção
       * também, por cima do índice da obra: não têm tipo.
       */
      readonly type: 'SET_FOUNDATION_FACTOR';
      readonly pieceId: StructuralPieceId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
    }
  | {
      /**
       * A taxa de aço própria do bloco, do baldrame ou do radier, kg/m³ por bitola (Fase 41,
       * etapa 2 — RN-544). A lista inteira, por cima da do tipo e da disciplina; vazia apaga, e
       * a peça volta à de baixo. A laje do pavimento não leva: a taxa dela é a da disciplina.
       * Desde a Fase 42 (etapa 3 — RN-572), a escada, o muro e o pilar de contenção também,
       * por cima da da disciplina — a lista do elemento, quando ela a tem (RN-573).
       */
      readonly type: 'SET_PIECE_STEEL_RATES';
      readonly pieceId: StructuralPieceId;
      readonly rates: readonly SteelRate[];
    }
  | {
      /**
       * A viga baldrame traçada de eixo a eixo (D6): os dois pontos em metros e a seção, que
       * a obra fala como "20 × 40" — largura 0,20 e altura 0,40. O trecho dentro dos blocos
       * sai da conta, e quem faz essa conta é a medida (RN-368).
       */
      readonly type: 'ADD_GROUND_BEAM';
      readonly name: string;
      readonly from: Point;
      readonly to: Point;
      readonly width: number;
      readonly height: number;
    }
  | {
      /** Corrigir o baldrame: o nome, uma das pontas (arrastar a ponta) ou a seção. */
      readonly type: 'UPDATE_GROUND_BEAM';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly from?: Point;
      readonly to?: Point;
      readonly width?: number;
      readonly height?: number;
    }
  | { readonly type: 'REMOVE_GROUND_BEAM'; readonly pieceId: StructuralPieceId }
  | {
      /**
       * O muro de contenção traçado de eixo a eixo (D9): os dois pontos em metros, a altura
       * e a espessura. O nome não se repete entre os muros do desenho (Fase 27, etapa 4).
       */
      readonly type: 'ADD_RETAINING_WALL';
      readonly name: string;
      readonly from: Point;
      readonly to: Point;
      readonly height: number;
      readonly thickness: number;
    }
  | {
      /** Corrigir o muro: o nome, uma das pontas (arrastar a ponta), a altura ou a espessura. */
      readonly type: 'UPDATE_RETAINING_WALL';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly from?: Point;
      readonly to?: Point;
      readonly height?: number;
      readonly thickness?: number;
    }
  | { readonly type: 'REMOVE_RETAINING_WALL'; readonly pieceId: StructuralPieceId }
  | {
      /**
       * O pilar de contenção: o centro, a seção, a altura e o ângulo do muro em que foi
       * encaixado (F6, RN-372). Solto, o ângulo é 0.
       */
      readonly type: 'ADD_RETAINING_COLUMN';
      readonly name: string;
      readonly center: Point;
      readonly sectionWidth: number;
      readonly sectionDepth: number;
      readonly height: number;
      readonly angle: number;
    }
  | {
      /** Corrigir o pilar: o nome, o centro (arrastar), a seção, a altura ou o ângulo. */
      readonly type: 'UPDATE_RETAINING_COLUMN';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly center?: Point;
      readonly sectionWidth?: number;
      readonly sectionDepth?: number;
      readonly height?: number;
      readonly angle?: number;
    }
  | { readonly type: 'REMOVE_RETAINING_COLUMN'; readonly pieceId: StructuralPieceId }
  | {
      /**
       * O radier ou a laje traçados na prancha: o contorno em metros e a espessura (Fase 27,
       * etapa 3, D7). O radier é da área comum da Fundação ou da Contenção; a laje, da área
       * comum de qualquer pavimento — é a laje do andar (RN-361, F3). Nasce sem regiões.
       */
      readonly type: 'ADD_SLAB';
      readonly kind: SlabKind;
      readonly name: string;
      readonly outline: readonly Point[];
      readonly thickness: number;
    }
  | {
      /**
       * Corrigir a peça: o nome, a espessura ou o contorno. O contorno que chega é o mesmo, só
       * deslocado — arrastar a peça inteira —, e aí as regiões vão junto; contorno de outra
       * forma deixa as regiões onde estão, e o domínio recusa as que ficarem de fora.
       */
      readonly type: 'UPDATE_SLAB';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly outline?: readonly Point[];
      readonly thickness?: number;
    }
  | { readonly type: 'REMOVE_SLAB'; readonly pieceId: StructuralPieceId }
  | {
      /** Uma região de espessura diferente, dentro do contorno da peça (RN-369). */
      readonly type: 'ADD_SLAB_REGION';
      readonly pieceId: StructuralPieceId;
      readonly outline: readonly Point[];
      readonly thickness: number;
    }
  | {
      /** Corrigir a região de número `index` (de zero): o contorno ou a espessura. */
      readonly type: 'UPDATE_SLAB_REGION';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
      readonly outline?: readonly Point[];
      readonly thickness?: number;
    }
  | {
      readonly type: 'REMOVE_SLAB_REGION';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
    }
  | {
      /**
       * A escada de concreto traçada na prancha: o nome e o primeiro lance — contorno, degraus,
       * espelho, piso e espessura (Fase 27, etapa 5, D10). É da área comum de qualquer pavimento,
       * como a laje (F3). Sem o lance, o domínio recusa: escada sem lance não tem conta.
       */
      readonly type: 'ADD_STAIR';
      readonly name: string;
      readonly flight?: StairFlight;
    }
  | {
      /**
       * Corrigir a escada: o nome, ou o deslocamento — arrastar a escada inteira leva lances e
       * patamares juntos, em metros.
       */
      readonly type: 'UPDATE_STAIR';
      readonly pieceId: StructuralPieceId;
      readonly name?: string;
      readonly shift?: Point;
    }
  | { readonly type: 'REMOVE_STAIR'; readonly pieceId: StructuralPieceId }
  | {
      readonly type: 'ADD_STAIR_FLIGHT';
      readonly pieceId: StructuralPieceId;
      readonly flight: StairFlight;
    }
  | {
      /** Corrigir o lance de número `index` (de zero). */
      readonly type: 'UPDATE_STAIR_FLIGHT';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
      readonly outline?: readonly Point[];
      readonly steps?: number;
      readonly riser?: number;
      readonly tread?: number;
      readonly thickness?: number;
    }
  | {
      readonly type: 'REMOVE_STAIR_FLIGHT';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
    }
  | {
      readonly type: 'ADD_STAIR_LANDING';
      readonly pieceId: StructuralPieceId;
      readonly landing: StairLanding;
    }
  | {
      /** Corrigir o patamar de número `index` (de zero). */
      readonly type: 'UPDATE_STAIR_LANDING';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
      readonly outline?: readonly Point[];
      readonly thickness?: number;
    }
  | {
      readonly type: 'REMOVE_STAIR_LANDING';
      readonly pieceId: StructuralPieceId;
      readonly index: number;
    }
  | {
      readonly type: 'SET_ITEM_QUANTITY';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly quantity: number;
      /**
       * O calculado que a tela mostrava (Fase 24, fatia 6). Presente, digitar por cima dele
       * é discordar da conta, e o motivo passa a ser obrigatório (D9). A tipologia não tem o
       * desenho nem o índice para refazer a conta — quem a fez foi a tela, e ela diz aqui.
       */
      readonly calculated?: number;
      readonly reason?: string;
    }
  | {
      /** Devolve o item à planilha — a decisão sai, o número dela volta a valer. */
      readonly type: 'CLEAR_ITEM_QUANTITY';
      readonly serviceKey: string;
      readonly itemKey: string;
    }
  | {
      /**
       * O serviço saiu da tipologia, e o que foi digitado nele sai junto.
       *
       * Só o digitado: o que a planilha orçou é da planilha, e volta se o serviço for
       * escolhido de novo.
       */
      readonly type: 'CLEAR_SERVICE_QUANTITIES';
      readonly serviceKey: string;
    }
  | { readonly type: 'RESET' };

function newId<B extends string>(): ReturnType<typeof asId<B>> {
  return asId<B>(crypto.randomUUID());
}

export function emptyDraft(
  name = 'Tipologia A',
  kind: UnitTypeKind = 'APARTAMENTO',
  floorKind: FloorPlanKind = 'TIPO',
): TypologyDraft {
  return {
    unitTypeId: newId<'UnitType'>(),
    name,
    kind,
    floorKind,
    rooms: [],
    walls: [],
    openings: [],
    elements: [],
    elementSegments: [],
    labels: {},
  };
}

/** `"Quarto 1 · Leste ↔ Sala · Oeste"`, ou o nome que a pessoa deu. */
export function describeWall(label: WallLabel | undefined): string {
  if (label === undefined) return 'Parede';
  if (label.custom !== undefined) return label.custom;
  return label.b === undefined ? `${label.a} ↔ externa` : `${label.a} ↔ ${label.b}`;
}

/** O nome curto, para a lista de "casar com" — o livre vence o automático. */
export function wallShortName(label: WallLabel | undefined): string {
  return label?.custom ?? label?.a ?? 'Parede';
}

export function roomOf(draft: TypologyDraft, roomId: RoomId): Room | undefined {
  return draft.rooms.find((room) => room.id === roomId);
}

/**
 * As paredes de cada ambiente, separadas entre abertas e já casadas.
 *
 * Cada parede aparece **uma única vez**, no ambiente do lado A. Casada, ela é dos dois —
 * mas listá-la nas duas seções faria a mesma parede pedir decisão duas vezes, e quem monta
 * tentaria casar o que já está casado. O rótulo nomeia os dois lados, então nada sai de
 * vista.
 */
export interface WallGroup {
  readonly room: Room;
  /** Ainda dão para a rua — é aqui que o trabalho de casar acontece. */
  readonly open: readonly Wall[];
  /** Já têm vizinho. Ficam recolhidas: continuam alcançáveis, mas fora do caminho. */
  readonly paired: readonly Wall[];
}

export function groupWallsByRoom(
  draft: TypologyDraft,
  /**
   * As divisas com outras tipologias que encostam nesta.
   *
   * Entram pelo lado **que for deste ambiente**, A ou B: para quem monta a Tipologia B, a
   * parede que ela divide com a A é uma parede dela como qualquer outra, e escondê-la
   * daria a impressão de que aquele lado do apartamento não tem parede.
   */
  divisas: readonly Wall[] = [],
): readonly WallGroup[] {
  return draft.rooms.map((room) => {
    const paredes = [
      ...draft.walls.filter((wall) => wall.sideA.roomId === room.id),
      ...divisas.filter((wall) => wall.sideA.roomId === room.id || wall.sideB?.roomId === room.id),
    ];
    return {
      room,
      open: paredes.filter((wall) => wall.sideB === undefined),
      paired: paredes.filter((wall) => wall.sideB !== undefined),
    };
  });
}

/** O rótulo de uma parede, esteja ela na tipologia ou entre duas. */
export function wallLabelOf(
  workspace: WorkspaceDraft,
  draft: TypologyDraft,
  wallId: WallId,
): WallLabel | undefined {
  return draft.labels[wallId] ?? workspace.partyLabels?.[wallId];
}

/**
 * As paredes que o desenho põe neste lado do contorno.
 *
 * As que lembram o lado (RN-243) e a de antes do índice que o casamento põe ali (RN-244) —
 * a mesma resposta que a prancha desenha. Mais de uma quando a sobreposição parcial foi
 * cortada (fatia 8).
 */
export function wallsOnEdge(
  room: Room,
  walls: readonly Wall[],
  edgeIndex: number,
): readonly Wall[] {
  const aqui = new Set(
    placeWalls([room], walls)
      .filter((posta) => posta.edgeIndex === edgeIndex)
      .map((posta) => String(posta.wallId)),
  );
  return walls.filter((wall) => aqui.has(String(wall.id)));
}

/** O que o lado é hoje, do jeito que a tela pergunta — ver `EdgeChoice`. */
export function edgeChoiceOf(room: Room, walls: readonly Wall[], edgeIndex: number): EdgeChoice {
  const kind = edgeKindAt(room, edgeIndex);
  if (kind !== 'PAREDE') return kind;
  return wallsOnEdge(room, walls, edgeIndex).some((wall) => wall.railingHeight !== undefined)
    ? 'MURETA'
    : 'PAREDE';
}

/** Divisa é sempre parede (RN-269) — o recado de quem tentou tirá-la. */
const DIVISA_E_PAREDE =
  'Este lado é divisa com outro ambiente, e divisa é sempre parede. Para tirar a parede entre os dois, conjugue-a.';

/** As paredes que ainda dão para a rua — as candidatas a casar com esta. */
export function pairableWith(draft: TypologyDraft, wall: Wall): readonly Wall[] {
  return draft.walls.filter(
    (other) =>
      other.id !== wall.id && other.sideB === undefined && other.sideA.roomId !== wall.sideA.roomId,
  );
}

/**
 * O lado oposto, quando o ambiente ainda é um retângulo.
 *
 * Mexer no lado norte de um retângulo mexe no sul — são a mesma medida do mesmo cômodo.
 * Sem isso, corrigir uma parede desmancharia o par, a área deixaria de sair das paredes na
 * hora e a pessoa teria de lembrar de editar as duas. Só vale para quatro paredes que se
 * emparelham: onde a forma já não é retângulo, não há "oposto" a adivinhar.
 */
/**
 * Largura e comprimento do retângulo que as paredes desenham hoje.
 *
 * Só mexe quando as quatro paredes se emparelham — onde a forma já não é retângulo, não há
 * largura nem comprimento a atualizar, e o par antigo continua sendo o melhor palpite.
 */
/**
 * Casa as paredes do ambiente recém-traçado com as dos vizinhos.
 *
 * Dois ambientes desenhados sobre a mesma prancha compartilham a origem, e um trecho de
 * parede em comum é um par de lados colineares que se sobrepõem. O que a pessoa fazia num
 * `select`, vinte vezes por tipologia, vira consequência de ter traçado — e era o maior
 * risco desta fase.
 *
 * Casa só o que ainda está livre dos dois lados: parede já casada não se casa de novo, e
 * ambiente sem contorno (digitado à mão) fica de fora porque não há geometria a comparar.
 *
 * **Não exige lado em cima de lado** (`WALL_PAIRING`). Quem traça cada ambiente pela face
 * interna dele desenha dois lados para uma parede só, afastados da espessura do bloco —
 * quinze centímetros que faziam o casamento falhar em toda divisa da prancha, justamente
 * onde ele mais economiza trabalho. Trinta centímetros e cinco graus de folga é o que
 * cobre a parede grossa e o pulso trêmulo sem alcançar o corredor do outro lado.
 *
 * **A parede do vizinho é achada pelo lado, e não pela medida** (RN-243). Pela medida, o
 * lado de 3 m da cozinha era qualquer uma das quatro paredes de 3 m dela: o casamento
 * levava a de cima, a da esquerda ficava livre por baixo da divisa, e a sala perdia a
 * parede dela no desenho. E depois de uma medida corrigida no painel nenhuma batia — as
 * duas ficavam uma sobre a outra, as duas contadas (D22). `novas` vem **por lado**, pelo
 * mesmo motivo: `novas[i]` é a parede do lado `i`, e buraco é lado sem parede nova.
 *
 * **E casa só o trecho em comum** (D24, RN-305). A sala de 6 m com o quarto de 3 m encostado
 * no meio não casa a parede inteira: o casamento corta a mais comprida nos cantos da mais
 * curta, e a sala fica com 1,5 m livre, 3 m casada e 1,5 m livre naquele lado.
 */
function marryByGeometry(draft: TypologyDraft, room: Room, novas: readonly Wall[]): TypologyDraft {
  if (room.outline === undefined) return draft;

  let resultado = draft;

  for (const vizinho of draft.rooms) {
    if (vizinho.id === room.id || vizinho.outline === undefined) continue;

    for (const divisa of sharedEdges(room.outline, vizinho.outline, WALL_PAIRING)) {
      if (novas[divisa.aIndex] === undefined) continue;

      // Um lado cortado tem mais de uma parede (RN-305): cada pedaço livre deste lado que
      // corre junto de um pedaço livre do lado dele casa, e o casamento corta o que sobra —
      // o corredor de 6 m encostado em dois quartos casa com um, e a sobra com o outro. O
      // teto de voltas é guarda contra laço: cada volta casa um par, e par casado sai da conta.
      for (let volta = 0; volta < 20; volta += 1) {
        const par = parQueEncosta(resultado, room, divisa.aIndex, vizinho, divisa.bIndex);
        if (par === undefined) break;
        const casado = draftReducer(resultado, {
          type: 'MERGE_WALLS',
          keepId: par.minha,
          dropId: par.dele,
        });
        if (casado.lastError !== undefined) break;
        resultado = casado;
      }
    }
  }

  return resultado;
}

/**
 * O par de paredes livres que encosta entre este lado e o lado do vizinho — o mais colado.
 *
 * As deste ambiente pelo trecho que cada uma ocupa no lado; as do vizinho pelo lugar que o
 * desenho lhes dá — pelo índice que lembram, ou, na de antes do índice, pelo lugar que o
 * desenho sempre lhe deu. Encostar é o mesmo critério do traço (`WALL_PAIRING`), agora
 * pedaço a pedaço; menos que `MIN_WALL_PIECE` em comum é canto, e não divisa.
 */
function parQueEncosta(
  draft: TypologyDraft,
  room: Room,
  aIndex: number,
  vizinho: Room,
  bIndex: number,
): { readonly minha: WallId; readonly dele: WallId } | undefined {
  const livreDe = (roomId: RoomId) =>
    new Set(
      draft.walls
        .filter((wall) => wall.sideA.roomId === roomId && wall.sideB === undefined)
        .map((wall) => String(wall.id)),
    );
  const minhasLivres = livreDe(room.id);
  const delasLivres = livreDe(vizinho.id);
  const minhas = placeWalls([room], draft.walls).filter(
    (posta) => posta.edgeIndex === aIndex && minhasLivres.has(String(posta.wallId)),
  );
  const delas = placeWalls([vizinho], draft.walls).filter(
    (posta) => posta.edgeIndex === bIndex && delasLivres.has(String(posta.wallId)),
  );

  let melhor: { readonly minha: WallId; readonly dele: WallId; readonly gap: number } | undefined;
  for (const minha of minhas) {
    for (const dela of delas) {
      const trecho = sharedRun(minha.segment, dela.segment, WALL_PAIRING);
      if (trecho === undefined || trecho.length < MIN_WALL_PIECE) continue;
      if (melhor === undefined || trecho.gap < melhor.gap) {
        melhor = { minha: minha.wallId, dele: dela.wallId, gap: trecho.gap };
      }
    }
  }
  return melhor;
}

function syncRoomToWalls(room: Room, walls: readonly Wall[]): Room {
  const doAmbiente = walls.filter(
    (wall) => wall.sideA.roomId === room.id || wall.sideB?.roomId === room.id,
  );
  if (doAmbiente.length !== 4) return room;

  const iguais = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;
  const [a, b, c, d] = [...doAmbiente].map((wall) => wall.length).sort((x, y) => x - y) as [
    number,
    number,
    number,
    number,
  ];
  if (!iguais(a, b) || !iguais(c, d)) return room;

  return { ...room, width: (a + b) / 2, length: (c + d) / 2 };
}

function oppositeWallId(draft: TypologyDraft, wall: Wall): WallId | undefined {
  const doAmbiente = draft.walls.filter(
    (candidate) =>
      candidate.sideA.roomId === wall.sideA.roomId || candidate.sideB?.roomId === wall.sideA.roomId,
  );
  if (doAmbiente.length !== 4) return undefined;

  const iguais = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;
  const ordenados = [...doAmbiente].map((candidate) => candidate.length).sort((a, b) => a - b);
  if (!iguais(ordenados[0]!, ordenados[1]!) || !iguais(ordenados[2]!, ordenados[3]!)) {
    return undefined;
  }

  return doAmbiente.find(
    (candidate) => candidate.id !== wall.id && iguais(candidate.length, wall.length),
  )?.id;
}

/**
 * O que fazer com um patch cuja medida não fecha.
 *
 * `SEGUE` é o caso comum. Os outros dois são erro de jeitos diferentes, e tratá-los igual
 * atrapalharia um dos dois:
 *
 * - `DIGITANDO` é o zero, que é **estágio** de quem está escrevendo "0,5" — o campo
 *   dispara a cada tecla, e o zero some na seguinte. Recusar com alarme faria a tela
 *   piscar em vermelho a cada casa decimal;
 * - `RECUSA` é negativo e NaN, que não são caminho para número nenhum. Aí vale o recado,
 *   porque quem digitou −5 precisa saber por que o número não entrou.
 */
type Conferencia = 'SEGUE' | 'DIGITANDO' | 'RECUSA';

/**
 * As medidas do patch, conferidas antes de entrarem.
 *
 * `createRoom`, `createWall` e `createOpening` recusam zero e negativo na criação — e o
 * patch passava por cima de todos eles. Parede de −5 m entrava; pior, **vão de largura
 * negativa somava alvenaria**, porque `wallNetArea` subtrai a área dos vãos e subtrair
 * negativo é somar. Uma porta digitada com o sinal trocado inflava o quantitativo do
 * prédio inteiro sem que nada na tela parecesse estranho.
 */
function conferirMedidas(patch: object, campos: readonly string[]): Conferencia {
  const valores = patch as Record<string, unknown>;
  let resultado: Conferencia = 'SEGUE';

  for (const campo of campos) {
    const valor = valores[campo];
    if (typeof valor !== 'number') continue;
    if (Number.isFinite(valor) && valor > 0) continue;
    if (valor !== 0) return 'RECUSA';
    resultado = 'DIGITANDO';
  }

  return resultado;
}

const MEDIDAS_DO_AMBIENTE = [
  'width',
  'length',
  'ceilingHeight',
  'areaOverride',
  'perimeterOverride',
] as const;
const MEDIDAS_DA_PAREDE = ['length', 'height', 'thickness', 'railingHeight'] as const;
const MEDIDAS_DO_VAO = ['width', 'height', 'count'] as const;
const MEDIDAS_DO_ELEMENTO = ['sectionWidth', 'sectionDepth', 'length'] as const;
const MEDIDAS_DO_TRECHO = ['length'] as const;

/**
 * A estrutura que sobra quando paredes somem do desenho.
 *
 * O trecho vai junto com a parede dele — é o encontro dos dois, e sem um dos dois não há
 * encontro. E o elemento que ficou sem trecho nenhum sai também: ele foi cadastrado como
 * o pilar **daquela** parede, e mantê-lo somaria concreto de uma estrutura que ninguém
 * mais vê na tela. Exceto o que tem contorno: esse é visto na planta sem parede alguma.
 */
function semAsParedes(
  draft: Pick<TypologyDraft, 'elements' | 'elementSegments'>,
  removidas: ReadonlySet<WallId>,
): Pick<TypologyDraft, 'elements' | 'elementSegments'> {
  const elementSegments = draft.elementSegments.filter((segment) => !removidas.has(segment.wallId));
  const comTrecho = new Set(elementSegments.map((segment) => segment.elementId));
  return {
    elementSegments,
    // O elemento com contorno sabe onde está sem parede (06/10/2026): perder a última não o
    // tira do desenho, e ele segue na conta como o pilar solto que sempre pôde ser.
    elements: draft.elements.filter(
      (element) => comTrecho.has(element.id) || element.outline !== undefined,
    ),
  };
}

const SO_NA_FUNDACAO =
  'A estaca é traçada na área comum do desenho de Fundação ou de Contenção — é lá que ela mora.';
const CENTRO_INVALIDO = 'Clique no centro da estaca, na planta.';
const TIPO_DE_ESTACA_DESCONHECIDO =
  'Esse tipo de estaca não está cadastrado nesta obra: escolha um da lista ou cadastre o tipo.';
const ESTACA_NAO_ENCONTRADA = 'Essa estaca não está mais neste desenho.';
const COMODO_NAO_ENCONTRADO = 'Esse ambiente não está mais nesta tipologia.';
/** RN-514: a peça da planilha traz a quantidade pronta (RN-384), e não há medida a multiplicar. */
const ESTACA_DA_PLANILHA_SEM_FATOR = (nome: string): string =>
  `A estaca ${nome} veio da planilha por peça: a quantidade dela já vem pronta, e não leva fator próprio.`;
const SO_NA_FUNDACAO_DO_BLOCO =
  'O bloco é traçado na área comum do desenho de Fundação ou de Contenção — é lá que ele mora.';
const SO_NA_FUNDACAO_DO_BALDRAME =
  'O baldrame é traçado na área comum do desenho de Fundação ou de Contenção — é lá que ele mora.';
const CONTORNO_DO_BLOCO_INVALIDO =
  'Trace o contorno do bloco na planta: ao menos três pontos, todos na prancha.';
const PONTOS_DO_BALDRAME_INVALIDOS = 'Clique os dois pontos do eixo do baldrame, na planta.';
const BLOCO_NAO_ENCONTRADO = 'Esse bloco não está mais neste desenho.';
const BALDRAME_NAO_ENCONTRADO = 'Esse baldrame não está mais neste desenho.';
const TIPO_DE_BLOCO_DESCONHECIDO =
  'Esse tipo de bloco não está cadastrado nesta obra: escolha um da lista ou cadastre o tipo.';
const TIPO_DE_BLOCO_NAO_ENCONTRADO = 'Esse tipo de bloco não está mais nesta obra.';
/** RN-540: a peça da planilha traz a quantidade pronta, e não há medida a multiplicar (F10). */
const PECA_DA_PLANILHA_SEM_FATOR = (peca: string, nome: string): string =>
  `${peca} ${nome} veio da planilha por peça: a quantidade já vem pronta, e não leva fator próprio.`;
const BLOCO_DA_PLANILHA_SEM_TIPO = (nome: string): string =>
  `O bloco ${nome} veio da planilha por peça: a quantidade já vem pronta, e não leva tipo de bloco.`;
const LAJE_SEM_FATOR_DA_FUNDACAO =
  'O fator próprio é do radier: a laje do pavimento segue o índice da obra e a média do apartamento.';
/** RN-547: o aço da peça importada já veio pronto, por bitola. */
const PECA_DA_PLANILHA_SEM_TAXA = (peca: string, nome: string): string =>
  `${peca} ${nome} veio da planilha por peça: o aço já vem pronto, e não leva taxa própria.`;
const LAJE_SEM_TAXA_DA_FUNDACAO =
  'A taxa de aço própria é do radier: a laje do pavimento segue a taxa da disciplina.';

const SO_NA_FUNDACAO_DO_MURO =
  'O muro de contenção é traçado na área comum do desenho de Fundação ou de Contenção — é lá que ele mora.';
const SO_NA_FUNDACAO_DO_PILAR_DE_CONTENCAO =
  'O pilar de contenção é traçado na área comum do desenho de Fundação ou de Contenção — é lá que ele mora.';
const PONTOS_DO_MURO_INVALIDOS = 'Clique os dois pontos do eixo do muro, na planta.';
const CENTRO_DO_PILAR_INVALIDO = 'Clique no centro do pilar de contenção, na planta.';
const nomeDoPilarSobreposto = (outro: string): string =>
  `Esse pilar ficaria por cima do pilar ${outro}: dois pilares na mesma posição descontariam o muro duas vezes.`;
const MURO_NAO_ENCONTRADO = 'Esse muro não está mais neste desenho.';
const PILAR_DE_CONTENCAO_NAO_ENCONTRADO = 'Esse pilar de contenção não está mais neste desenho.';

const SO_NA_FUNDACAO_DO_RADIER =
  'O radier é traçado na área comum do desenho de Fundação ou de Contenção — é lá que ele mora.';
const SO_NA_AREA_COMUM_DA_LAJE =
  'A laje é traçada na área comum ou no apartamento — a laje da vaga é a da área comum.';
const CONTORNO_DA_LAJE_INVALIDO =
  'Trace o contorno na planta: ao menos três pontos, todos na prancha.';
const LAJE_NAO_ENCONTRADA = 'Essa peça não está mais neste desenho.';
const REGIAO_NAO_ENCONTRADA = 'Essa região não está mais nesta peça.';

const SO_NA_AREA_COMUM_DA_ESCADA =
  'A escada é traçada na área comum ou no apartamento — a da garagem é da área comum.';
const ESCADA_NAO_ENCONTRADA = 'Essa escada não está mais neste desenho.';
const LANCE_NAO_ENCONTRADO = 'Esse lance não está mais nesta escada.';
const PATAMAR_NAO_ENCONTRADO = 'Esse patamar não está mais nesta escada.';
const DESLOCAMENTO_DA_ESCADA_INVALIDO = 'O deslocamento da escada precisa de números.';

/** A estaca é da área comum do desenho de Fundação ou de Contenção (RN-361, F3). */
export function estacaCabeAqui(typology: Pick<TypologyDraft, 'kind' | 'floorKind'>): boolean {
  return (
    typology.kind === 'AREA_COMUM' &&
    (typology.floorKind === 'FUNDACAO' || typology.floorKind === 'CONTENCAO')
  );
}

/** O radier é da área comum da Fundação ou da Contenção, como a estaca (RN-361, F3). */
export const radierCabeAqui = estacaCabeAqui;

/**
 * A laje é da área comum ou do apartamento, em **qualquer** pavimento (F3, revisto em 06/10).
 *
 * Na área comum ela é a laje do andar e conta uma vez; no apartamento, é a do apto e se
 * multiplica pelos aptos do andar. A mesma laje nos dois lugares conta em dobro — é escolha de
 * quem desenha. A laje da vaga é a da área comum do pavimento: a vaga não tem laje própria.
 */
export function lajeCabeAqui(typology: Pick<TypologyDraft, 'kind'>): boolean {
  return typology.kind !== 'VAGA';
}

/** A escada cabe onde a laje cabe: área comum ou apartamento, em qualquer pavimento (D10). */
export const escadaCabeAqui = lajeCabeAqui;

/** O mesmo contorno, só deslocado? Devolve o deslocamento; outra forma, `undefined`. */
function deslocamentoDoContorno(
  antes: readonly Point[],
  depois: readonly Point[],
): Point | undefined {
  const primeiro = antes[0];
  const novo = depois[0];
  if (primeiro === undefined || novo === undefined || antes.length !== depois.length) {
    return undefined;
  }
  const dx = novo.x - primeiro.x;
  const dy = novo.y - primeiro.y;
  const mesmoFormato = antes.every((ponto, indice) => {
    const outro = depois[indice]!;
    return Math.abs(outro.x - ponto.x - dx) < 1e-9 && Math.abs(outro.y - ponto.y - dy) < 1e-9;
  });
  return mesmoFormato ? { x: dx, y: dy } : undefined;
}

/**
 * Quanto a parede andou, na perpendicular dela — o plano que ela ocupa. O deslizamento ao
 * longo (um canto que mudou) não leva o pilar: ele continua onde estava na parede.
 */
function deslocamentoDaParede(antes: Segment, depois: Segment): Point | undefined {
  const comprimento = segmentLength(antes);
  if (comprimento === 0) return undefined;
  const nx = -(antes.to.y - antes.from.y) / comprimento;
  const ny = (antes.to.x - antes.from.x) / comprimento;
  const meioAntes = { x: (antes.from.x + antes.to.x) / 2, y: (antes.from.y + antes.to.y) / 2 };
  const meioDepois = { x: (depois.from.x + depois.to.x) / 2, y: (depois.from.y + depois.to.y) / 2 };
  const andou = (meioDepois.x - meioAntes.x) * nx + (meioDepois.y - meioAntes.y) * ny;
  if (Math.abs(andou) < 1e-9) return undefined;
  return { x: nx * andou, y: ny * andou };
}

const copiarContorno = (outline: readonly Point[]): Point[] =>
  outline.map((ponto) => ({ x: ponto.x, y: ponto.y }));

const copiarLance = (lance: StairFlight): StairFlight => ({
  outline: copiarContorno(lance.outline),
  steps: lance.steps,
  riser: lance.riser,
  tread: lance.tread,
  thickness: lance.thickness,
});

const copiarPatamar = (patamar: StairLanding): StairLanding => ({
  outline: copiarContorno(patamar.outline),
  thickness: patamar.thickness,
});

function centroValido(centro: Point): boolean {
  return Number.isFinite(centro.x) && Number.isFinite(centro.y);
}

/** Todos os pontos com os dois números reais — o contorno do bloco e o eixo do baldrame só valem assim. */
/** O retângulo que envolve o contorno, nas três casas da geometria: a seção guardada do pilar. */
function caixaDoContorno(
  contorno: readonly Point[],
): Pick<StructuralElement, 'sectionWidth' | 'sectionDepth'> {
  const xs = contorno.map((ponto) => ponto.x);
  const ys = contorno.map((ponto) => ponto.y);
  const tres = (valor: number): number => Math.round(valor * 1000) / 1000;
  return {
    sectionWidth: tres(Math.max(...xs) - Math.min(...xs)),
    sectionDepth: tres(Math.max(...ys) - Math.min(...ys)),
  };
}

function pontosValidos(pontos: readonly Point[]): boolean {
  return pontos.every(centroValido);
}

/**
 * As listas da tipologia em que o fator e a taxa próprios se gravam por peça — a fundação (Fase
 * 41) e, desde a Fase 42 (etapa 3 — G3), a escada, o muro e o pilar de contenção. A laje mora
 * em `slabs` com o radier, e é recusada por quem chama.
 */
type ListaDePecaComFator =
  'pileCaps' | 'groundBeams' | 'slabs' | 'stairs' | 'retainingWalls' | 'retainingColumns';

/** Como a recusa fala da peça: "O bloco", "A escada", "O pilar de contenção". */
const ROTULO_DA_PECA_COM_FATOR: Readonly<Record<ListaDePecaComFator, string>> = {
  pileCaps: 'O bloco',
  groundBeams: 'O baldrame',
  slabs: 'O radier',
  stairs: 'A escada',
  retainingWalls: 'O muro',
  retainingColumns: 'O pilar de contenção',
};

/**
 * A peça achada pelo id nas seis listas, e em qual delas mora. `undefined` quando não está em
 * nenhuma — sumiu do desenho.
 */
function pecaComFator(
  clean: TypologyDraft,
  pieceId: StructuralPieceId,
):
  | {
      readonly lista: ListaDePecaComFator;
      readonly peca: {
        readonly id: StructuralPieceId;
        readonly name: string;
        readonly kind: string;
        readonly factors?: readonly ItemFactor[];
        readonly steelRates?: readonly SteelRate[];
      };
    }
  | undefined {
  const listas: readonly ListaDePecaComFator[] = [
    'pileCaps',
    'groundBeams',
    'slabs',
    'stairs',
    'retainingWalls',
    'retainingColumns',
  ];
  for (const lista of listas) {
    const peca = (clean[lista] ?? []).find((candidata) => candidata.id === pieceId);
    if (peca !== undefined) return { lista, peca };
  }
  return undefined;
}

/** A lista com a peça trocada — só ela muda, e a tipologia recebe a lista no mesmo campo. */
function comAPecaTrocada(
  clean: TypologyDraft,
  lista: ListaDePecaComFator,
  pieceId: StructuralPieceId,
  mudanca:
    { readonly factors: readonly ItemFactor[] } | { readonly steelRates: readonly SteelRate[] },
): TypologyDraft {
  const trocar = <P extends { readonly id: StructuralPieceId }>(pecas: readonly P[]) =>
    pecas.map((candidata) =>
      candidata.id === pieceId
        ? { ...candidata, ...mudanca, updatedAt: new Date().toISOString() }
        : candidata,
    );
  switch (lista) {
    case 'pileCaps':
      return { ...clean, pileCaps: trocar(clean.pileCaps ?? []) };
    case 'groundBeams':
      return { ...clean, groundBeams: trocar(clean.groundBeams ?? []) };
    case 'slabs':
      return { ...clean, slabs: trocar(clean.slabs ?? []) };
    case 'stairs':
      return { ...clean, stairs: trocar(clean.stairs ?? []) };
    case 'retainingWalls':
      return { ...clean, retainingWalls: trocar(clean.retainingWalls ?? []) };
    case 'retainingColumns':
      return { ...clean, retainingColumns: trocar(clean.retainingColumns ?? []) };
  }
}

/**
 * O fator próprio de um item no bloco, no baldrame ou no radier (Fase 41, RN-538, RN-540) — e,
 * desde a Fase 42 (etapa 3 — RN-571, RN-574), na escada, no muro e no pilar de contenção.
 *
 * A mesma conta do fator da estaca (`SET_PIECE_FACTOR`): `withOwnFactor` e a conferência do
 * domínio, e a lista vazia fica — é ela que diz ao servidor "nenhum". A peça é achada pelo id
 * nas seis listas; a laje do pavimento recusa, porque o fator dela é o do apartamento (Fase 40).
 */
function comFatorDaFundacao(
  clean: TypologyDraft,
  action: Extract<DraftAction, { readonly type: 'SET_FOUNDATION_FACTOR' }>,
): TypologyDraft {
  const achada = pecaComFator(clean, action.pieceId);
  if (achada === undefined) return { ...clean, lastError: LAJE_NAO_ENCONTRADA };
  const { lista, peca } = achada;
  if (lista === 'slabs' && peca.kind !== 'RADIER') {
    return { ...clean, lastError: LAJE_SEM_FATOR_DA_FUNDACAO };
  }
  // A peça de nome importado não entra na conta do desenho (RN-384): o fator não multiplicaria
  // nada. Tirar o que já havia continua valendo (RN-540, RN-574).
  if (
    action.factor !== undefined &&
    pecasImportadas(clean.pieceQuantities).has(nomeDaPeca(peca.name))
  ) {
    return {
      ...clean,
      lastError: PECA_DA_PLANILHA_SEM_FATOR(ROTULO_DA_PECA_COM_FATOR[lista], peca.name),
    };
  }
  const conferidos = checkItemFactors(
    withOwnFactor(peca.factors, action.serviceKey, action.itemKey, action.factor),
  );
  if (!conferidos.ok) return { ...clean, lastError: conferidos.error.message };
  return comAPecaTrocada(clean, lista, peca.id, { factors: conferidos.value });
}

/**
 * A taxa de aço própria do bloco, do baldrame ou do radier (Fase 41, etapa 2 — RN-544, RN-547) —
 * e, desde a Fase 42 (etapa 3 — RN-572, RN-574), da escada, do muro e do pilar de contenção.
 *
 * A lista vale inteira e passa pela conferência da taxa da disciplina (`normalizeSteelRates`):
 * positiva, uma por bitola. A vazia fica — é ela que diz ao servidor "nenhuma", e a peça volta à
 * do tipo ou à da disciplina. A peça da planilha por peça não leva taxa: o aço dela já veio pronto
 * (RN-547); tirar a que havia continua valendo.
 */
function comTaxaDaFundacao(
  clean: TypologyDraft,
  action: Extract<DraftAction, { readonly type: 'SET_PIECE_STEEL_RATES' }>,
): TypologyDraft {
  const achada = pecaComFator(clean, action.pieceId);
  if (achada === undefined) return { ...clean, lastError: LAJE_NAO_ENCONTRADA };
  const { lista, peca } = achada;
  if (lista === 'slabs' && peca.kind !== 'RADIER') {
    return { ...clean, lastError: LAJE_SEM_TAXA_DA_FUNDACAO };
  }
  if (
    action.rates.length > 0 &&
    pecasImportadas(clean.pieceQuantities).has(nomeDaPeca(peca.name))
  ) {
    return {
      ...clean,
      lastError: PECA_DA_PLANILHA_SEM_TAXA(ROTULO_DA_PECA_COM_FATOR[lista], peca.name),
    };
  }
  const conferidas = normalizeSteelRates(action.rates);
  if (!conferidas.ok) return { ...clean, lastError: conferidas.error.message };
  return comAPecaTrocada(clean, lista, peca.id, { steelRates: conferidas.value });
}

export function draftReducer(
  draft: TypologyDraft,
  action: DraftAction,
  /**
   * Os tipos de estaca da obra, que moram na bancada e não na tipologia (Fase 27, D1). A
   * estaca confere o tipo e o comprimento corrigido contra eles; ausentes, quem chama já
   * conferiu — é o caso do reducer chamado direto, sem bancada.
   */
  pileTypes?: readonly PileType[],
  /** Os tipos de bloco da obra (Fase 41, RN-536), pela mesma razão dos de estaca. */
  pileCapTypes?: readonly PileCapType[],
): TypologyDraft {
  // Toda ação começa apagando a recusa anterior: erro que sobrevive à correção vira
  // mensagem mentindo na tela. A chave **sai** do objeto em vez de virar `undefined` —
  // é o que `exactOptionalPropertyTypes` cobra, e com razão.
  const { lastError: _anterior, ...clean } = draft;

  switch (action.type) {
    case 'SET_NAME':
      return { ...clean, name: action.name };

    case 'ADD_ROOM': {
      const now = new Date().toISOString();
      const created = createRoom({
        id: newId<'Room'>(),
        unitTypeId: draft.unitTypeId,
        name: action.name,
        kind: action.kind,
        width: action.width,
        length: action.length,
        ceilingHeight: action.ceilingHeight,
        now,
      });
      if (!created.ok) return { ...clean, lastError: created.error.message };

      const room = created.value;
      // Quatro paredes externas, prontas para casar (ADR-109): duas do comprimento da
      // largura, duas do comprimento. Quem monta aponta depois quais são a mesma.
      const walls: Wall[] = [];
      const labels: Record<string, WallLabel> = { ...draft.labels };

      WALL_FACES.forEach((face, index) => {
        const length = index % 2 === 0 ? room.width : room.length;
        const wall = createWall({
          id: newId<'Wall'>(),
          owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
          sideA: { roomId: room.id },
          length,
          height: action.wallHeight ?? DEFAULT_WALL_HEIGHT,
          thickness: DEFAULT_WALL_THICKNESS,
          now,
        });
        if (!wall.ok) return;
        walls.push(wall.value);
        labels[wall.value.id] = { a: `${room.name} · ${face}` };
      });

      return { ...clean, rooms: [...draft.rooms, room], walls: [...draft.walls, ...walls], labels };
    }

    case 'UPDATE_ROOM': {
      const conferido = conferirMedidas(action.patch, MEDIDAS_DO_AMBIENTE);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'A medida do ambiente precisa ser maior que zero.' };
      }

      const rooms = draft.rooms.map((room) =>
        room.id === action.roomId ? { ...room, ...action.patch, id: room.id } : room,
      );
      return { ...clean, rooms };
    }

    case 'RESET_ROOM_GEOMETRY': {
      // As correções **saem** do objeto em vez de virarem `undefined`: ausente é o que o
      // domínio lê como "vale largura × comprimento" (RN-157).
      const rooms = draft.rooms.map((room) => {
        if (room.id !== action.roomId) return room;
        const { areaOverride: _area, perimeterOverride: _perimetro, ...resto } = room;
        return resto;
      });
      return { ...clean, rooms };
    }

    case 'SET_ROOM_FACTOR': {
      const comodo = clean.rooms.find((room) => room.id === action.roomId);
      if (comodo === undefined) return { ...clean, lastError: COMODO_NAO_ENCONTRADO };
      const anterior = comodo.factors?.find(
        (linha) => linha.serviceKey === action.serviceKey && linha.itemKey === action.itemKey,
      );
      const foto = action.average ?? anterior?.average;
      const fatores = withOwnFactor(
        comodo.factors,
        action.serviceKey,
        action.itemKey,
        action.factor,
        {
          origin: action.origin ?? 'DIGITADO',
          ...(foto === undefined ? {} : { average: foto }),
        },
      );
      const conferidos = checkItemFactors(fatores);
      if (!conferidos.ok) return { ...clean, lastError: conferidos.error.message };
      // A lista vazia fica, como na estaca: é ela que diz ao servidor "nenhum".
      return {
        ...clean,
        rooms: clean.rooms.map((room) =>
          room.id === comodo.id
            ? { ...room, factors: conferidos.value, updatedAt: new Date().toISOString() }
            : room,
        ),
      };
    }

    case 'REMOVE_ROOM': {
      // A parede que sobra sem um dos lados vira externa em vez de sumir: o ambiente
      // vizinho continua tendo aquela parede, e apagá-la levaria junto a alvenaria dele.
      const walls: Wall[] = [];
      const removedWallIds = new Set<WallId>();
      const liberadas: WallId[] = [];
      for (const wall of draft.walls) {
        const aIsGone = wall.sideA.roomId === action.roomId;
        const bIsGone = wall.sideB?.roomId === action.roomId;
        if (aIsGone && (wall.sideB === undefined || bIsGone)) {
          removedWallIds.add(wall.id);
          continue;
        }
        if (aIsGone && wall.sideB !== undefined) {
          const { sideB: _dropped, ...rest } = wall;
          walls.push({ ...rest, sideA: wall.sideB });
          liberadas.push(wall.id);
          continue;
        }
        if (bIsGone) {
          const { sideB: _dropped, ...rest } = wall;
          walls.push(rest);
          liberadas.push(wall.id);
          continue;
        }
        walls.push(wall);
      }

      // A parede que ficou sem o vizinho é o pedaço do meio de um lado cortado: sem o quarto,
      // a sala volta a ter uma parede só naquele lado (RN-305).
      const semOAmbiente: TypologyDraft = {
        ...clean,
        rooms: draft.rooms.filter((room) => room.id !== action.roomId),
        walls,
        openings: draft.openings.filter((opening) => !removedWallIds.has(opening.wallId)),
        ...semAsParedes(draft, removedWallIds),
        // O shaft é do chão do ambiente: sem o ambiente, não há de quem descontar (RN-272).
        ...(draft.shafts === undefined
          ? {}
          : { shafts: draft.shafts.filter((shaft) => shaft.roomId !== action.roomId) }),
      };
      return liberadas.reduce(
        (atual, wallId) => juntarPedacos(atual, wallId),
        mantendoOLugar(draft, liberadas, semOAmbiente),
      );
    }

    case 'ADD_TRACED_ROOM': {
      const now = new Date().toISOString();
      const lados = polygonEdges(action.outline);
      const xs = action.outline.map((ponto) => ponto.x);
      const ys = action.outline.map((ponto) => ponto.y);

      const criado = createRoom({
        id: newId<'Room'>(),
        unitTypeId: draft.unitTypeId,
        name: action.name,
        kind: action.kind,
        // O retângulo que envolve o contorno: só resumo de lista, porque a área e o
        // perímetro passam a sair do polígono (RN-180).
        width: Math.max(...xs) - Math.min(...xs),
        length: Math.max(...ys) - Math.min(...ys),
        ceilingHeight: action.ceilingHeight,
        outline: action.outline,
        now,
      });
      if (!criado.ok) return { ...clean, lastError: criado.error.message };

      const room = criado.value;
      const novas: Wall[] = [];
      const labels: Record<string, WallLabel> = { ...draft.labels };

      lados.forEach((lado, indice) => {
        const parede = createWall({
          id: newId<'Wall'>(),
          owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
          // A parede nasce sabendo de que lado é (RN-243): é isso, e não a medida, que a põe
          // no lugar do desenho e a acha no casamento.
          sideA: { roomId: room.id, edgeIndex: indice },
          length: comprimentoDoLado(lado),
          height: action.wallHeight ?? DEFAULT_WALL_HEIGHT,
          thickness: DEFAULT_WALL_THICKNESS,
          now,
        });
        if (!parede.ok) return;
        novas.push(parede.value);
        labels[parede.value.id] = { a: `${room.name} · Lado ${String(indice + 1)}` };
      });

      const comOAmbiente: TypologyDraft = {
        ...clean,
        rooms: [...draft.rooms, room],
        walls: [...draft.walls, ...novas],
        labels,
      };

      // E o casamento sai de graça: quem já foi traçado sobre a mesma planta e divide um
      // trecho colinear com este ambiente é vizinho — não precisa que ninguém aponte.
      return marryByGeometry(comOAmbiente, room, novas);
    }

    case 'SET_ROOM_OUTLINE': {
      const room = draft.rooms.find((candidate) => candidate.id === action.roomId);
      if (room === undefined) return clean;
      if (action.outline.length < 3) {
        return { ...clean, lastError: 'O contorno precisa de pelo menos três pontos.' };
      }

      const now = new Date().toISOString();
      const xs = action.outline.map((ponto) => ponto.x);
      const ys = action.outline.map((ponto) => ponto.y);
      const atualizado: Room = {
        ...room,
        outline: action.outline,
        width: Math.max(...xs) - Math.min(...xs),
        length: Math.max(...ys) - Math.min(...ys),
        updatedAt: now,
      };

      // As paredes livres deste ambiente nasceram do retângulo do cadastro, ou de um
      // contorno anterior: o traço novo as substitui. As **casadas** ficam — elas são do
      // vizinho também, e apagá-las levaria junto a alvenaria dele.
      const daCasa = (wall: Wall): boolean =>
        wall.sideA.roomId === room.id || wall.sideB?.roomId === room.id;

      // E o lado que a casada lembra **deste** ambiente mudou com o contorno: o índice
      // antigo apontaria outro lado, ou nenhum. Quem responde é o vizinho — o lado dele que
      // a parede lembra, e o lado do traço novo que encosta nele (RN-243). Sem vizinho
      // traçado, ou sem lado do traço que encoste nele, o índice sai: quem redesenhou longe
      // da divisa desfaz o casamento e traça de novo, e até lá o painel diz que ela ficou
      // sem lugar no desenho.
      const ladoNovo = (wall: Wall): number | undefined => {
        const outro = wall.sideA.roomId === room.id ? wall.sideB : wall.sideA;
        const vizinho = draft.rooms.find((candidate) => candidate.id === outro?.roomId);
        const ladoDele =
          vizinho?.outline === undefined || outro?.edgeIndex === undefined
            ? undefined
            : polygonEdges(vizinho.outline)[outro.edgeIndex];
        // Só o trecho dele que a casada ocupa (RN-304): o lado inteiro do corredor encosta
        // em dois quartos, e o redesenho de um não pode achar o lado do outro.
        return ladoDele === undefined
          ? undefined
          : sideAlong(subSegment(ladoDele, spanOf(outro)), action.outline);
      };
      // O trecho deste lado sai junto: era fração do lado antigo, e quem o mede de novo no
      // lado novo é `reajustarCasada`, pelo que o vizinho cobre dele.
      const mantidas = draft.walls
        .filter((wall) => !daCasa(wall) || wall.sideB !== undefined)
        .map((wall) =>
          daCasa(wall)
            ? withEdgeSpan(withEdgeIndex(wall, room.id, ladoNovo(wall)), room.id, undefined)
            : wall,
        );
      const removidas = new Set(
        draft.walls.filter((wall) => daCasa(wall) && wall.sideB === undefined).map((w) => w.id),
      );
      const daDivisa = new Set(
        mantidas.filter(daCasa).map((wall) => wallSideOf(wall, room.id)?.edgeIndex),
      );

      // O lado sem parede e a mureta seguem no traço novo quando ele tem um lado no mesmo
      // lugar — o lado antigo que anda junto com um do novo (D25). O que o redesenho levou
      // embora volta a ser parede, e a divisa nunca deixa de ser (RN-269).
      const antigos = room.outline === undefined ? [] : polygonEdges(room.outline);
      const ladoDoNovo = (edgeIndex: number | undefined): number | undefined => {
        const trecho = edgeIndex === undefined ? undefined : antigos[edgeIndex];
        const novo = trecho === undefined ? undefined : sideAlong(trecho, action.outline);
        return novo === undefined || daDivisa.has(novo) ? undefined : novo;
      };
      const semParede = (room.edgesWithoutWall ?? []).flatMap((lado): EdgeWithoutWall[] => {
        const novo = ladoDoNovo(lado.edgeIndex);
        return novo === undefined ? [] : [{ ...lado, edgeIndex: novo }];
      });
      const ladosSemParede = new Set(semParede.map((lado) => lado.edgeIndex));
      const muretaNoLado = new Map<number, Wall>();
      for (const wall of draft.walls) {
        if (!daCasa(wall) || wall.sideB !== undefined || wall.railingHeight === undefined) continue;
        const novo = ladoDoNovo(wallSideOf(wall, room.id)?.edgeIndex);
        if (novo !== undefined) muretaNoLado.set(novo, wall);
      }
      const { edgesWithoutWall: _ladosAntigos, ...semLados } = atualizado;
      const redesenhado: Room =
        semParede.length === 0
          ? semLados
          : { ...semLados, edgesWithoutWall: normalizeEdgesWithoutWall(semParede)! };

      const labels: Record<string, WallLabel> = { ...draft.labels };
      for (const id of removidas) delete labels[id];

      // A casada mantida passa a ocupar, do lado novo, o trecho que o vizinho cobre dele —
      // e o vizinho, o trecho do lado dele que o lado novo cobre (RN-305).
      const semAsLivres: TypologyDraft = {
        ...clean,
        rooms: draft.rooms.map((candidate) => (candidate.id === room.id ? redesenhado : candidate)),
        walls: mantidas,
        openings: draft.openings.filter((opening) => !removidas.has(opening.wallId)),
        ...semAsParedes(draft, removidas),
        labels,
      };
      const comAsCasadas = mantidas
        .filter(daCasa)
        .reduce((atual, wall) => reajustarCasada(atual, wall.id, room.id), semAsLivres);
      const trechosCasados = (indice: number): EdgeSpan[] =>
        comAsCasadas.walls
          .filter((wall) => daCasa(wall) && wallSideOf(wall, room.id)?.edgeIndex === indice)
          .map((wall) => spanOf(wallSideOf(wall, room.id)));

      const altura = alturaDasParedesDe(draft.walls, room.id);
      const novas: Wall[] = [];
      const porLado: Wall[] = [];
      const novosNomes: Record<string, WallLabel> = {};
      polygonEdges(action.outline).forEach((lado, indice) => {
        // O lado sem parede não ganha parede: é justamente o que ele é.
        if (ladosSemParede.has(indice)) return;
        // O lado que a divisa mantida ocupa só ganha parede no que sobra dela: no trecho da
        // divisa seriam duas, uma sobre a outra, as duas contadas — e a nova ainda casaria
        // com outra parede do vizinho.
        const casados = trechosCasados(indice);
        const livres =
          casados.length === 0 ? [undefined] : trechosLivres(casados, segmentLength(lado));
        const mureta = muretaNoLado.get(indice);
        for (const trecho of livres) {
          const parede = createWall({
            id: newId<'Wall'>(),
            owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
            sideA: {
              roomId: room.id,
              edgeIndex: indice,
              ...(trecho === undefined ? {} : { span: trecho }),
            },
            length: trecho === undefined ? comprimentoDoLado(lado) : spanLength(lado, trecho),
            height: mureta?.height ?? altura,
            thickness: DEFAULT_WALL_THICKNESS,
            ...(mureta?.railingHeight === undefined ? {} : { railingHeight: mureta.railingHeight }),
            now,
          });
          if (!parede.ok) continue;
          novas.push(parede.value);
          porLado[indice] = parede.value;
          novosNomes[parede.value.id] = { a: `${room.name} · Lado ${String(indice + 1)}` };
        }
      });

      const comOContorno: TypologyDraft = {
        ...comAsCasadas,
        walls: [...comAsCasadas.walls, ...novas],
        labels: { ...comAsCasadas.labels, ...novosNomes },
      };

      return marryByGeometry(comOContorno, redesenhado, porLado);
    }

    /**
     * O lado empurrado, com as paredes de sempre.
     *
     * O que muda é o contorno e o **comprimento** das paredes que os lados movidos
     * carregam — a identidade de cada uma fica, e com ela a porta pendurada nela, o pilar
     * que a cruza e o nome que alguém digitou. Casar não é assunto daqui: quem empurrou a
     * parede até o vizinho encontra o casamento na volta, por geometria, como no traço.
     */
    case 'RESHAPE_ROOM': {
      const room = draft.rooms.find((candidate) => candidate.id === action.roomId);
      if (room?.outline === undefined || room.outline.length !== action.outline.length) {
        return clean;
      }
      if (!isValidOutline(action.outline)) {
        return { ...clean, lastError: 'O contorno precisa fechar um ambiente.' };
      }

      const now = new Date().toISOString();
      const xs = action.outline.map((ponto) => ponto.x);
      const ys = action.outline.map((ponto) => ponto.y);
      const atualizado: Room = {
        ...room,
        outline: action.outline,
        width: Math.max(...xs) - Math.min(...xs),
        length: Math.max(...ys) - Math.min(...ys),
        updatedAt: now,
      };

      // O lugar de antes é quem diz qual parede é qual lado; o contorno novo diz quanto
      // cada uma passa a medir. Os dois contornos têm os mesmos lados, na mesma ordem.
      const colocadas = placeWalls([room], draft.walls);
      const ladosNovos = polygonEdges(action.outline);
      const porLado: Wall[] = [];

      const ladosAntigos = polygonEdges(room.outline);
      const sairam = new Set<WallId>();
      const walls = draft.walls.map((wall) => {
        const posta = colocadas.find((candidata) => candidata.wallId === wall.id);
        const lado = posta === undefined ? undefined : ladosNovos[posta.edgeIndex];
        if (posta === undefined || lado === undefined) return wall;

        // O pedaço de um lado cortado fica onde estava no plano, e mede o trecho dele no
        // lado novo; o que chegava ao canto continua chegando (RN-305).
        const cortado = wallSideOf(wall, room.id)?.span;
        const antigo = ladosAntigos[posta.edgeIndex];
        const levado =
          cortado === undefined || antigo === undefined
            ? undefined
            : trechoEmpurrado(antigo, lado, cortado);
        // O pedaço livre que saiu do lado sai do desenho logo abaixo, e a casada fica no
        // trecho de antes até o reajuste: não é o empurrão que decide descasar.
        if (cortado !== undefined && levado === undefined && wall.sideB === undefined) {
          sairam.add(wall.id);
        }
        const trecho = levado ?? cortado;

        // O lado fica gravado também na parede de antes do índice: o empurrão acabou de
        // confirmar qual lado ela é, e dali em diante a medida nova não a tira do lugar.
        const movida: Wall = {
          ...withEdgeSpan(withEdgeIndex(wall, room.id, posta.edgeIndex), room.id, trecho),
          length: trecho === undefined ? comprimentoDoLado(lado) : spanLength(lado, trecho),
          updatedAt: now,
        };
        porLado[posta.edgeIndex] = movida;
        return movida;
      });

      const empurrado: TypologyDraft = semOsPedacosQueSairam(
        draft,
        {
          ...clean,
          rooms: draft.rooms.map((candidate) =>
            candidate.id === room.id ? atualizado : candidate,
          ),
          walls,
        },
        room.id,
        sairam,
      );
      // A casada acompanha o quanto os dois lados passaram a correr juntos, e os pedaços
      // livres vizinhos acompanham a casada (RN-305): o quarto alargado come o pedaço livre
      // da sala ao lado dele, em vez de ficar por cima.
      const comOContorno = walls
        .filter(
          (wall) =>
            wall.sideB !== undefined &&
            (wall.sideA.roomId === room.id || wall.sideB.roomId === room.id),
        )
        .reduce((atual, wall) => reajustarCasada(atual, wall.id), empurrado);

      // O pilar e a viga em polígono guardam o contorno em metros, e não o lugar na parede:
      // quando a parede anda, o contorno anda com ela — como o pilar de retângulo e a viga
      // de antes, que são postos pelo trecho. Quem leva é o ambiente do lado A: a divisa
      // empurrada reforma os dois, e levar nos dois deslocaria o pilar duas vezes.
      const deslocamentos = new Map<WallId, { readonly x: number; readonly y: number }>();
      for (const posta of colocadas) {
        const parede = walls.find((wall) => wall.id === posta.wallId);
        if (parede === undefined || parede.sideA.roomId !== room.id) continue;
        const antigo = ladosAntigos[posta.edgeIndex];
        const novo = ladosNovos[posta.edgeIndex];
        if (antigo === undefined || novo === undefined) continue;
        const deslocamento = deslocamentoDaParede(antigo, novo);
        if (deslocamento !== undefined) deslocamentos.set(parede.id, deslocamento);
      }
      const comPecasLevadas: TypologyDraft =
        deslocamentos.size === 0
          ? comOContorno
          : {
              ...comOContorno,
              elements: comOContorno.elements.map((element) => {
                if (element.outline === undefined) return element;
                let dx = 0;
                let dy = 0;
                for (const trecho of comOContorno.elementSegments) {
                  if (trecho.elementId !== element.id) continue;
                  const deslocamento = deslocamentos.get(trecho.wallId);
                  if (deslocamento === undefined) continue;
                  dx += deslocamento.x;
                  dy += deslocamento.y;
                }
                if (dx === 0 && dy === 0) return element;
                return {
                  ...element,
                  outline: element.outline.map((ponto) => ({ x: ponto.x + dx, y: ponto.y + dy })),
                  updatedAt: now,
                };
              }),
            };

      return marryByGeometry(comPecasLevadas, atualizado, porLado);
    }

    case 'CLEAR_ROOM_OUTLINE': {
      // O contorno **sai** do objeto em vez de virar `undefined`: ausente é o que a
      // geometria lê como "a área vem das paredes" (RN-157). Os lados sem parede saem
      // junto: o índice deles apontava lados deste contorno, e sem contorno não há lado.
      const rooms = draft.rooms.map((room) => {
        if (room.id !== action.roomId) return room;
        const { outline: _fora, edgesWithoutWall: _lados, ...semContorno } = room;
        return semContorno;
      });
      return { ...clean, rooms };
    }

    case 'SET_EDGE_KIND': {
      const room = draft.rooms.find((candidate) => candidate.id === action.roomId);
      const lado =
        room?.outline === undefined ? undefined : polygonEdges(room.outline)[action.edgeIndex];
      if (room === undefined || lado === undefined) return clean;

      const doLado = wallsOnEdge(room, draft.walls, action.edgeIndex);
      const semParede = action.kind === 'GUARDA_CORPO' || action.kind === 'ABERTO';

      // Divisa é sempre parede (RN-269): o lado casado com outro ambiente é dos dois, e tirá-lo
      // daqui tiraria a parede do vizinho junto. Onde não há parede entre os dois, conjuga-se.
      const divisa = draft.walls.some(
        (wall) =>
          wall.sideB !== undefined &&
          (doLado.includes(wall) || wallSideOf(wall, room.id)?.edgeIndex === action.edgeIndex),
      );
      if (divisa && semParede) return { ...clean, lastError: DIVISA_E_PAREDE };

      const now = new Date().toISOString();

      if (semParede) {
        // A parede do lado sai, com o que estava pendurado nela: o vão e a peça não têm
        // onde ficar num guarda-corpo (RN-266). A tela pediu a confirmação antes.
        const removidas = new Set(doLado.map((wall) => wall.id));
        const labels: Record<string, WallLabel> = { ...draft.labels };
        for (const id of removidas) delete labels[id];
        const atualizado = { ...withEdgeKind(room, action.edgeIndex, action.kind), updatedAt: now };
        return {
          ...clean,
          rooms: draft.rooms.map((candidate) =>
            candidate.id === room.id ? atualizado : candidate,
          ),
          walls: draft.walls.filter((wall) => !removidas.has(wall.id)),
          openings: draft.openings.filter((opening) => !removidas.has(opening.wallId)),
          ...semAsParedes(draft, removidas),
          labels,
        };
      }

      const mureta = action.kind === 'MURETA';
      const atualizado =
        edgeWithoutWallAt(room, action.edgeIndex) === undefined
          ? room
          : { ...withEdgeKind(room, action.edgeIndex, 'PAREDE'), updatedAt: now };
      const rooms = draft.rooms.map((candidate) =>
        candidate.id === room.id ? atualizado : candidate,
      );

      if (doLado.length === 0) {
        // O lado volta a ter parede — nova, como a do traço, e casando com o vizinho se ele
        // encosta ali. A mureta nasce baixa e com o guarda-corpo em cima; a parede, com a
        // altura das irmãs.
        const parede = createWall({
          id: newId<'Wall'>(),
          owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
          sideA: { roomId: room.id, edgeIndex: action.edgeIndex },
          length: comprimentoDoLado(lado),
          height: mureta ? DEFAULT_MURETA_HEIGHT : alturaDasParedesDe(draft.walls, room.id),
          thickness: DEFAULT_WALL_THICKNESS,
          ...(mureta ? { railingHeight: DEFAULT_RAILING_HEIGHT } : {}),
          now,
        });
        if (!parede.ok) return { ...clean, rooms, lastError: parede.error.message };
        const porLado: Wall[] = [];
        porLado[action.edgeIndex] = parede.value;
        const comAParede: TypologyDraft = {
          ...clean,
          rooms,
          walls: [...draft.walls, parede.value],
          labels: {
            ...draft.labels,
            [parede.value.id]: { a: `${room.name} · Lado ${String(action.edgeIndex + 1)}` },
          },
        };
        return marryByGeometry(comAParede, atualizado, porLado);
      }

      // A parede que já está no lado vira mureta, ou deixa de ser. A altura acompanha: a
      // mureta nasce baixa, e a parede que deixa de ser mureta volta à altura das irmãs —
      // os dois números estão ali na tela, para quem quiser outro.
      const doLadoIds = new Set(doLado.map((wall) => wall.id));
      const alturaCheia = alturaDasParedesDe(
        draft.walls.filter((wall) => !doLadoIds.has(wall.id)),
        room.id,
      );
      const walls = draft.walls.map((wall): Wall => {
        if (!doLadoIds.has(wall.id)) return wall;
        if (mureta) {
          return wall.railingHeight !== undefined
            ? wall
            : {
                ...wall,
                height: DEFAULT_MURETA_HEIGHT,
                railingHeight: DEFAULT_RAILING_HEIGHT,
                updatedAt: now,
              };
        }
        if (wall.railingHeight === undefined) return wall;
        const { railingHeight: _fora, ...semGuardaCorpo } = wall;
        return { ...semGuardaCorpo, height: alturaCheia, updatedAt: now };
      });
      return { ...clean, rooms, walls };
    }

    case 'SET_EDGE_RAILING_HEIGHT': {
      const conferido = conferirMedidas({ height: action.height }, ['height']);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'A altura do guarda-corpo precisa ser maior que zero.' };
      }
      const room = draft.rooms.find((candidate) => candidate.id === action.roomId);
      if (room === undefined || edgeKindAt(room, action.edgeIndex) !== 'GUARDA_CORPO') return clean;
      const atualizado = {
        ...withEdgeKind(room, action.edgeIndex, 'GUARDA_CORPO', action.height),
        updatedAt: new Date().toISOString(),
      };
      return {
        ...clean,
        rooms: draft.rooms.map((candidate) => (candidate.id === room.id ? atualizado : candidate)),
      };
    }

    case 'ADD_WALL': {
      // Quatro paredes é o que um retângulo tem; um ambiente em L tem mais. A parede extra
      // nasce externa e com um metro, para ser medida ou casada em seguida.
      const room = draft.rooms.find((candidate) => candidate.id === action.roomId);
      if (room === undefined) return clean;

      const quantas = draft.walls.filter((wall) => wall.sideA.roomId === room.id).length;
      const criada = createWall({
        id: newId<'Wall'>(),
        owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
        sideA: { roomId: room.id },
        length: 1,
        height: alturaDasParedesDe(draft.walls, room.id),
        thickness: DEFAULT_WALL_THICKNESS,
        now: new Date().toISOString(),
      });
      if (!criada.ok) return { ...clean, lastError: criada.error.message };

      return {
        ...clean,
        walls: [...draft.walls, criada.value],
        labels: {
          ...draft.labels,
          [criada.value.id]: { a: `${room.name} · Parede ${String(quantas + 1)}` },
        },
      };
    }

    case 'UPDATE_WALL': {
      const conferido = conferirMedidas(action.patch, MEDIDAS_DA_PAREDE);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'A medida da parede precisa ser maior que zero.' };
      }

      const alvo = draft.walls.find((wall) => wall.id === action.wallId);
      const espelho =
        alvo === undefined || action.patch.length === undefined
          ? undefined
          : oppositeWallId(draft, alvo);

      const walls = draft.walls.map((wall) => {
        if (wall.id === action.wallId) return { ...wall, ...action.patch, id: wall.id };
        if (wall.id === espelho) return { ...wall, length: action.patch.length!, id: wall.id };
        return wall;
      });

      // Enquanto o ambiente é retângulo, largura e comprimento seguem as paredes. É o que
      // impede a medida de envelhecer: sem isso, acrescentar uma quinta parede faria a área
      // cair para o retângulo do cadastro — um número que as paredes já desmentiram.
      const rooms =
        alvo === undefined
          ? draft.rooms
          : draft.rooms.map((room) =>
              room.id === alvo.sideA.roomId ? syncRoomToWalls(room, walls) : room,
            );

      return { ...clean, walls, rooms };
    }

    case 'RENAME_WALL': {
      const label = draft.labels[action.wallId];
      if (label === undefined) return clean;

      // Nome em branco não é "parede sem nome": é voltar ao automático. Por isso a chave
      // **sai** do rótulo, em vez de guardar string vazia.
      const nome = action.name.trim();
      const { custom: _anterior, ...resto } = label;
      return {
        ...clean,
        labels: {
          ...draft.labels,
          [action.wallId]: nome === '' ? resto : { ...resto, custom: nome },
        },
      };
    }

    case 'MERGE_WALLS': {
      const keep = draft.walls.find((wall) => wall.id === action.keepId);
      const drop = draft.walls.find((wall) => wall.id === action.dropId);
      if (keep === undefined || drop === undefined) return clean;
      if (keep.sideB !== undefined || drop.sideB !== undefined) {
        return { ...clean, lastError: 'Uma das paredes já está casada.' };
      }
      if (keep.sideA.roomId === drop.sideA.roomId) {
        return { ...clean, lastError: 'Uma parede não pode ter o mesmo ambiente dos dois lados.' };
      }
      // A sobreposição parcial é cortada antes (D24): o trecho comum casa, e o resto de cada
      // uma continua parede livre do ambiente dela, no mesmo lado.
      if (action.cortadas !== true) {
        const casado = casarComCorte(clean, keep, drop, (cortado) =>
          draftReducer(cortado, { ...action, cortadas: true }),
        );
        return casado === 'SEM_TRECHO' ? { ...clean, lastError: SEM_TRECHO_COMUM } : casado;
      }

      const labels: Record<string, WallLabel> = { ...draft.labels };
      const daQueFica = labels[keep.id];
      labels[keep.id] = {
        a: daQueFica?.a ?? 'Parede',
        b: labels[drop.id]?.a ?? 'Parede',
        // O nome que alguém escreveu sobrevive ao casamento.
        ...(daQueFica?.custom === undefined ? {} : { custom: daQueFica.custom }),
      };
      delete labels[drop.id];

      return {
        ...clean,
        walls: draft.walls
          .filter((wall) => wall.id !== drop.id)
          // O lado da que some vira o lado B da que fica **inteiro**, com o lado do contorno
          // que ela lembrava (RN-243): o 2 da sala continua sendo o 2, e o 4 da cozinha, o 4.
          .map((wall) => (wall.id === keep.id ? { ...wall, sideB: drop.sideA } : wall)),
        // Os vãos da parede que sumiu passam para a que ficou: a porta continua existindo.
        openings: draft.openings.map((opening) =>
          opening.wallId === drop.id ? { ...opening, wallId: keep.id } : opening,
        ),
        // A estrutura segue o mesmo caminho — o pilar não deixa de estar ali porque as
        // duas paredes viraram uma.
        elementSegments: draft.elementSegments.map((segment) =>
          segment.wallId === drop.id ? { ...segment, wallId: keep.id } : segment,
        ),
        labels,
      };
    }

    case 'UNMERGE_WALL': {
      const wall = draft.walls.find((candidate) => candidate.id === action.wallId);
      if (wall?.sideB === undefined) return clean;
      // Descasada, cada uma volta a se juntar aos pedaços livres do lado dela (RN-305).
      if (action.semJuntar !== true) {
        return descasarEJuntar(clean, wall.id, (antes) =>
          draftReducer(antes, { ...action, semJuntar: true }),
        );
      }

      const now = new Date().toISOString();
      const reborn = createWall({
        id: newId<'Wall'>(),
        owner: wall.owner,
        // A que renasce leva o lado B inteiro, e com ele o lado do contorno de onde veio: o
        // desenho a devolve ao lugar dela, e não ao primeiro lado do mesmo tamanho. Com o
        // trecho, se o lado dela foi cortado — e então mede o trecho dela (RN-305).
        sideA: wall.sideB,
        length: medidaDoTrecho(draft.rooms, wall.sideB) ?? wall.length,
        height: wall.height,
        now,
        ...(wall.thickness === undefined ? {} : { thickness: wall.thickness }),
      });
      if (!reborn.ok) return { ...clean, lastError: reborn.error.message };

      const labels: Record<string, WallLabel> = { ...draft.labels };
      const label = labels[wall.id];
      labels[wall.id] = { a: label?.a ?? 'Parede' };
      labels[reborn.value.id] = { a: label?.b ?? 'Parede' };

      const { sideB: _dropped, ...rest } = wall;
      return {
        ...clean,
        walls: [
          ...draft.walls.map((candidate) =>
            candidate.id === wall.id ? { ...rest, joined: false } : candidate,
          ),
          reborn.value,
        ],
        labels,
      };
    }

    case 'TOGGLE_JOIN': {
      const wall = draft.walls.find((candidate) => candidate.id === action.wallId);
      if (wall === undefined) return clean;
      if (wall.joined) {
        return {
          ...clean,
          walls: draft.walls.map((candidate) =>
            candidate.id === wall.id ? splitWall(candidate, new Date().toISOString()) : candidate,
          ),
        };
      }

      const roomA = wall.sideB === undefined ? undefined : roomOf(draft, wall.sideA.roomId);
      const roomB = wall.sideB === undefined ? undefined : roomOf(draft, wall.sideB.roomId);
      if (roomA === undefined || roomB === undefined) {
        return {
          ...clean,
          lastError: 'Parede externa não pode ser conjugada: do outro lado é a rua.',
        };
      }

      const joined = joinWall(wall, roomA, roomB, new Date().toISOString());
      if (!joined.ok) return { ...clean, lastError: joined.error.message };

      return {
        ...clean,
        walls: draft.walls.map((candidate) =>
          candidate.id === wall.id ? joined.value : candidate,
        ),
      };
    }

    case 'ADD_OPENING': {
      const created = createOpening({
        id: newId<'Opening'>(),
        wallId: action.wallId,
        kind: action.kind,
        ...medidasDoVao(action),
      });
      if (!created.ok) return { ...clean, lastError: created.error.message };
      return { ...clean, openings: [...draft.openings, created.value] };
    }

    case 'UPDATE_OPENING': {
      const conferido = conferirMedidas(action.patch, MEDIDAS_DO_VAO);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'A medida do vão precisa ser maior que zero.' };
      }

      const openings = draft.openings.map((opening) =>
        opening.id === action.openingId ? { ...opening, ...action.patch, id: opening.id } : opening,
      );
      return { ...clean, openings };
    }

    case 'REMOVE_OPENING':
      return {
        ...clean,
        openings: draft.openings.filter((opening) => opening.id !== action.openingId),
      };

    case 'ADD_ELEMENT': {
      const parede = draft.walls.find((wall) => wall.id === action.wallId);
      if (parede === undefined) return { ...clean, lastError: 'Parede não encontrada.' };

      const now = new Date().toISOString();
      const elementId = newId<'StructuralElement'>();
      // A seção que a obra mais usa, e a corrida da parede: o pilar sobe até a laje, a
      // viga corre com ela. Quem tiver outra medida corrige os dois campos que já estão
      // na tela.
      const preset =
        action.kind === 'PILAR'
          ? { sectionWidth: 0.2, sectionDepth: 0.4, length: parede.height }
          : { sectionWidth: 0.15, sectionDepth: 0.5, length: parede.length };
      const created = createStructuralElement({
        id: elementId,
        owner: parede.owner,
        kind: action.kind,
        ...preset,
        now,
      });
      if (!created.ok) return { ...clean, lastError: created.error.message };

      // O pilar nasce mostrando a face maior, que é como ele costuma ser assentado
      // (RN-182); a viga nasce correndo por cima da parede, que é o caso comum.
      const trecho = createElementSegment({
        id: newId<'ElementSegment'>(),
        elementId,
        wallId: action.wallId,
        orientation: action.kind === 'PILAR' ? 'PERPENDICULAR' : 'PARALELA',
      });
      if (!trecho.ok) return { ...clean, lastError: trecho.error.message };

      return {
        ...clean,
        elements: [...draft.elements, created.value],
        elementSegments: [...draft.elementSegments, trecho.value],
      };
    }

    case 'ADD_TRACED_ELEMENT': {
      // Traço sem contorno e sem parede é peça solta: ela teria volume e não saberia onde
      // está, e ninguém veria por quê. Com o contorno ela existe sem parede (06/10/2026) —
      // o desenho a mostra, e a conta sai dele. Sem ele, melhor recusar e dizer.
      if (action.segments.length === 0 && action.outline === undefined) {
        return {
          ...clean,
          lastError: 'O traço não encostou em parede nenhuma — desenhe a peça sobre uma delas.',
        };
      }

      const now = new Date().toISOString();
      const elementId = newId<'StructuralElement'>();
      const created = createStructuralElement({
        id: elementId,
        owner: { kind: 'UNIT_TYPE', unitTypeId: draft.unitTypeId },
        kind: action.kind,
        sectionWidth: action.sectionWidth,
        sectionDepth: action.sectionDepth,
        length: action.length,
        // O contrato confere o contorno (três pontos, área, ponta da viga) e recusa com a
        // mensagem dele: o rascunho não repete a regra.
        ...(action.outline === undefined ? {} : { outline: action.outline }),
        now,
      });
      if (!created.ok) return { ...clean, lastError: created.error.message };

      const trechos: ElementSegment[] = [];
      for (const lido of action.segments) {
        const trecho = createElementSegment({
          id: newId<'ElementSegment'>(),
          elementId,
          wallId: lido.wallId,
          orientation: lido.orientation,
          ...(lido.length === undefined ? {} : { length: lido.length }),
          // A posição vem do traço e é gravada: é ela que faz a peça ficar onde foi
          // desenhada, em vez de o desenho recolocá-la no meio da parede.
          ...(lido.offset === undefined ? {} : { offset: lido.offset }),
          ...(lido.lateral === undefined ? {} : { lateral: lido.lateral }),
        });
        if (!trecho.ok) return { ...clean, lastError: trecho.error.message };
        trechos.push(trecho.value);
      }

      // O avanço fica **em branco de propósito**: ausente é derivado da seção menos a
      // espessura da parede, e é o traço que acabou de medir a seção. Gravar um número
      // aqui congelaria a sobra no dia do traço.
      return {
        ...clean,
        elements: [...draft.elements, created.value],
        elementSegments: [...draft.elementSegments, ...trechos],
      };
    }

    case 'UPDATE_ELEMENT': {
      const conferido = conferirMedidas(action.patch, MEDIDAS_DO_ELEMENTO);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'A medida do pilar ou da viga precisa ser maior que zero.' };
      }
      // O vão livre fica fora de `conferirMedidas` pelo motivo do avanço: **zero é
      // resposta**, e é a da viga que não tem vazio nenhum.
      const { freeSpan } = action.patch;
      if (freeSpan !== undefined && !(Number.isFinite(freeSpan) && freeSpan >= 0)) {
        return { ...clean, lastError: 'O vão livre não pode ser negativo.' };
      }

      // O contorno manda na forma: o novo (andar é trocar o contorno por ele mesmo
      // transladado) e a largura da viga digitada por cima do contorno que ela já tem passam
      // pelo mesmo contrato do cadastro.
      const alvo = draft.elements.find((element) => element.id === action.elementId);
      let patch = action.patch;
      if (alvo !== undefined && patch.outline !== undefined) {
        const largura = patch.sectionWidth ?? alvo.sectionWidth;
        const conferido = checkElementOutline(alvo.kind, patch.outline, largura);
        if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
        patch = { ...patch, outline: conferido.value };
        // A seção guardada do pilar é o retângulo que envolve o contorno: acompanha o novo.
        if (alvo.kind === 'PILAR') patch = { ...caixaDoContorno(conferido.value), ...patch };
      } else if (alvo?.outline !== undefined && patch.sectionWidth !== undefined) {
        const conferido = checkElementOutline(alvo.kind, alvo.outline, patch.sectionWidth);
        if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      }

      return {
        ...clean,
        elements: draft.elements.map((element) =>
          element.id === action.elementId
            ? { ...element, ...patch, id: element.id, updatedAt: new Date().toISOString() }
            : element,
        ),
      };
    }

    case 'UPDATE_ELEMENT_SEGMENT': {
      const conferido = conferirMedidas(action.patch, MEDIDAS_DO_TRECHO);
      if (conferido === 'DIGITANDO') return clean;
      if (conferido === 'RECUSA') {
        return { ...clean, lastError: 'O trecho na parede precisa ser maior que zero.' };
      }
      // O avanço não passa por `conferirMedidas`: **zero é resposta**, e a resposta mais
      // comum — o elemento rente à parede. O que ele não pode é ser negativo.
      const { advance } = action.patch;
      if (advance !== undefined && !(Number.isFinite(advance) && advance >= 0)) {
        return { ...clean, lastError: 'O avanço não pode ser negativo.' };
      }

      return {
        ...clean,
        elementSegments: draft.elementSegments.map((segment) =>
          segment.id === action.segmentId
            ? { ...segment, ...action.patch, id: segment.id }
            : segment,
        ),
      };
    }

    case 'ADD_ELEMENT_SEGMENT': {
      const parede = draft.walls.find((wall) => wall.id === action.wallId);
      if (parede === undefined) return { ...clean, lastError: 'Parede não encontrada.' };
      if (!draft.elements.some((element) => element.id === action.elementId)) {
        return { ...clean, lastError: 'Pilar ou viga não encontrado.' };
      }
      // Já cruza esta parede: acrescentar de novo descontaria a sombra duas vezes do
      // mesmo plano, e o total cairia sem que a tela mostrasse dois trechos iguais.
      if (
        draft.elementSegments.some(
          (segment) => segment.elementId === action.elementId && segment.wallId === action.wallId,
        )
      ) {
        return clean;
      }

      const criado = createElementSegment({
        id: newId<'ElementSegment'>(),
        elementId: action.elementId,
        wallId: action.wallId,
        // Quem acrescenta uma parede a uma viga que já corria está dizendo que ela
        // atravessa aquela parede — paralela seria a viga correndo por cima de duas.
        orientation: 'PERPENDICULAR',
      });
      if (!criado.ok) return { ...clean, lastError: criado.error.message };

      return { ...clean, elementSegments: [...draft.elementSegments, criado.value] };
    }

    case 'REMOVE_ELEMENT_SEGMENT': {
      const alvo = draft.elementSegments.find((segment) => segment.id === action.segmentId);
      if (alvo === undefined) return clean;

      const elementSegments = draft.elementSegments.filter(
        (segment) => segment.id !== action.segmentId,
      );
      const aindaCruza = elementSegments.some((segment) => segment.elementId === alvo.elementId);

      return {
        ...clean,
        elementSegments,
        // Sem trecho nenhum, o elemento não está em lugar nenhum do desenho — e um
        // concreto que não encosta em parede alguma somaria volume que ninguém vê. O que
        // tem contorno está na planta sem parede, e fica.
        elements: draft.elements.filter(
          (element) => element.id !== alvo.elementId || aindaCruza || element.outline !== undefined,
        ),
      };
    }

    case 'REMOVE_ELEMENT':
      return {
        ...clean,
        elements: draft.elements.filter((element) => element.id !== action.elementId),
        elementSegments: draft.elementSegments.filter(
          (segment) => segment.elementId !== action.elementId,
        ),
      };

    case 'ADD_SHAFT': {
      // Só no ambiente desta tipologia: o shaft desconta do piso de quem o aponta, e o
      // ambiente de outra tipologia é outra conta (RN-272).
      if (!draft.rooms.some((room) => room.id === action.roomId)) {
        return {
          ...clean,
          lastError:
            'Trace o shaft dentro de um ambiente desta tipologia — é do piso dele que o buraco desconta.',
        };
      }
      const criado = createShaft({
        id: newId<'Shaft'>(),
        roomId: action.roomId,
        from: action.from,
        to: action.to,
        now: new Date().toISOString(),
        ...(action.count === undefined ? {} : { count: action.count }),
      });
      if (!criado.ok) return { ...clean, lastError: criado.error.message };
      return { ...clean, shafts: [...(draft.shafts ?? []), criado.value] };
    }

    case 'SET_SHAFT_COUNT': {
      // Campo vazio é alguém digitando, como na medida do ambiente: não grava e não acusa.
      if (action.count === 0) return clean;
      if (!Number.isInteger(action.count) || action.count < 0) {
        return {
          ...clean,
          lastError: 'A quantidade de shafts precisa ser um número inteiro de 1 para cima.',
        };
      }
      return {
        ...clean,
        shafts: (draft.shafts ?? []).map((shaft) =>
          shaft.id === action.shaftId ? { ...shaft, count: action.count } : shaft,
        ),
      };
    }

    case 'REMOVE_SHAFT':
      return {
        ...clean,
        shafts: (draft.shafts ?? []).filter((shaft) => shaft.id !== action.shaftId),
      };

    case 'ADD_PILE': {
      if (!estacaCabeAqui(clean)) return { ...clean, lastError: SO_NA_FUNDACAO };
      if (!centroValido(action.center)) return { ...clean, lastError: CENTRO_INVALIDO };
      if (pileTypes !== undefined && !pileTypes.some((tipo) => tipo.id === action.pileTypeId)) {
        return { ...clean, lastError: TIPO_DE_ESTACA_DESCONHECIDO };
      }
      const pecas = clean.pieces ?? [];
      const conferida = checkPileFields(
        { name: action.name, pileTypeId: action.pileTypeId },
        pecas,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      const agora = new Date().toISOString();
      const estaca: StructuralPiece = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'ESTACA',
        name: conferida.value.name,
        pileTypeId: conferida.value.pileTypeId,
        center: { x: action.center.x, y: action.center.y },
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, pieces: [...pecas, estaca] };
    }

    case 'PLACE_PILE':
    case 'MOVE_PILE': {
      const estaca = (clean.pieces ?? []).find((peca) => peca.id === action.pieceId);
      if (estaca === undefined) return { ...clean, lastError: ESTACA_NAO_ENCONTRADA };
      if (!centroValido(action.center)) return { ...clean, lastError: CENTRO_INVALIDO };
      return {
        ...clean,
        pieces: (clean.pieces ?? []).map((peca) =>
          peca.id === action.pieceId
            ? {
                ...peca,
                center: { x: action.center.x, y: action.center.y },
                updatedAt: new Date().toISOString(),
              }
            : peca,
        ),
      };
    }

    case 'UPDATE_PILE': {
      const estaca = (clean.pieces ?? []).find((peca) => peca.id === action.pieceId);
      if (estaca === undefined) return { ...clean, lastError: ESTACA_NAO_ENCONTRADA };

      const conferida = checkPileFields(
        {
          name: action.name ?? estaca.name,
          pileTypeId: action.pileTypeId ?? estaca.pileTypeId,
        },
        clean.pieces ?? [],
        estaca.id,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      if (
        pileTypes !== undefined &&
        !pileTypes.some((tipo) => tipo.id === conferida.value.pileTypeId)
      ) {
        return { ...clean, lastError: TIPO_DE_ESTACA_DESCONHECIDO };
      }

      // O comprimento é conferido contra o tipo **novo**: trocar de R40 para R60 pode fazer
      // a correção da estaca virar o comprimento do tipo, e aí ela deixa de ser correção.
      // Sem a lista de tipos, o comprimento do tipo é desconhecido e a conferência segue
      // só pelo que a estaca precisa ter: positivo e com motivo.
      const tipo = pileTypes?.find((candidato) => candidato.id === conferida.value.pileTypeId);
      const queCorrige = action.lengthOverride ?? estaca.lengthOverride;
      const motivo =
        action.lengthOverride === undefined ? estaca.lengthReason : action.lengthReason;
      const comprimento = checkLengthOverride(queCorrige, motivo, tipo ?? { length: Number.NaN });
      if (!comprimento.ok) return { ...clean, lastError: comprimento.error.message };

      const { lengthOverride: _antigo, lengthReason: _motivoAntigo, ...semCorrecao } = estaca;
      return {
        ...clean,
        pieces: (clean.pieces ?? []).map((peca) =>
          peca.id === estaca.id
            ? {
                ...semCorrecao,
                name: conferida.value.name,
                pileTypeId: conferida.value.pileTypeId,
                ...comprimento.value,
                updatedAt: new Date().toISOString(),
              }
            : peca,
        ),
      };
    }

    case 'SET_PIECE_FACTOR': {
      const estaca = (clean.pieces ?? []).find((peca) => peca.id === action.pieceId);
      if (estaca === undefined) return { ...clean, lastError: ESTACA_NAO_ENCONTRADA };
      // A estaca de nome importado não entra na conta do desenho (RN-384): o fator dela não
      // multiplicaria nada. Tirar o que já havia continua valendo (RN-514).
      if (
        action.factor !== undefined &&
        pecasImportadas(clean.pieceQuantities).has(nomeDaPeca(estaca.name))
      ) {
        return { ...clean, lastError: ESTACA_DA_PLANILHA_SEM_FATOR(estaca.name) };
      }
      const fatores = withOwnFactor(
        estaca.factors,
        action.serviceKey,
        action.itemKey,
        action.factor,
      );
      const conferidos = checkItemFactors(fatores);
      if (!conferidos.ok) return { ...clean, lastError: conferidos.error.message };
      // A lista vazia fica, e não some: é ela que diz ao servidor "nenhum" — ausente seria "não
      // mexe", e a última volta ao do tipo não chegaria ao banco.
      return {
        ...clean,
        pieces: (clean.pieces ?? []).map((peca) =>
          peca.id === estaca.id
            ? { ...peca, factors: conferidos.value, updatedAt: new Date().toISOString() }
            : peca,
        ),
      };
    }

    case 'REMOVE_PILE':
      return {
        ...clean,
        pieces: (clean.pieces ?? []).filter((peca) => peca.id !== action.pieceId),
        ...(clean.pieceMarks === undefined
          ? {}
          : { pieceMarks: clean.pieceMarks.filter((marca) => marca.pieceId !== action.pieceId) }),
      };

    case 'TOGGLE_PIECE_MARK': {
      const estaca = (clean.pieces ?? []).find((peca) => peca.id === action.pieceId);
      if (estaca === undefined) return { ...clean, lastError: ESTACA_NAO_ENCONTRADA };
      // A estaca da fila não conta (RN-364), e marcar o que não conta seria uma marca que
      // ninguém vê: ela só passaria a valer no dia em que a estaca fosse posicionada.
      if (estaca.center === undefined) {
        return {
          ...clean,
          lastError: `Posicione a estaca ${estaca.name} antes de marcá-la: a que está na fila não conta.`,
        };
      }
      const marcas = clean.pieceMarks ?? [];
      const jaMarcada = marcas.some(
        (marca) => marca.pieceId === action.pieceId && marca.serviceKey === action.serviceKey,
      );
      return {
        ...clean,
        pieceMarks: jaMarcada
          ? marcas.filter(
              (marca) =>
                !(marca.pieceId === action.pieceId && marca.serviceKey === action.serviceKey),
            )
          : [...marcas, { pieceId: action.pieceId, serviceKey: action.serviceKey }],
      };
    }

    case 'MARK_ALL_PIECES': {
      const marcas = clean.pieceMarks ?? [];
      const jaMarcadas = new Set(
        marcas.filter((marca) => marca.serviceKey === action.serviceKey).map((m) => m.pieceId),
      );
      const novas = (clean.pieces ?? [])
        .filter(
          (peca) =>
            peca.deletedAt === undefined &&
            peca.center !== undefined &&
            (action.pileTypeId === undefined || peca.pileTypeId === action.pileTypeId) &&
            !jaMarcadas.has(peca.id),
        )
        .map((peca): PieceMark => ({ pieceId: peca.id, serviceKey: action.serviceKey }));
      return { ...clean, pieceMarks: [...marcas, ...novas] };
    }

    case 'UNMARK_ALL_PIECES':
      return {
        ...clean,
        pieceMarks: (clean.pieceMarks ?? []).filter(
          (marca) => marca.serviceKey !== action.serviceKey,
        ),
      };

    case 'ADD_PILE_CAP': {
      if (!estacaCabeAqui(clean)) return { ...clean, lastError: SO_NA_FUNDACAO_DO_BLOCO };
      if (!pontosValidos(action.outline)) {
        return { ...clean, lastError: CONTORNO_DO_BLOCO_INVALIDO };
      }
      const blocos = clean.pileCaps ?? [];
      const conferido = checkPileCapFields(
        { name: action.name, outline: action.outline, height: action.height },
        blocos,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const agora = new Date().toISOString();
      const bloco: PileCap = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'BLOCO',
        name: conferido.value.name,
        outline: conferido.value.outline.map((ponto) => ({ x: ponto.x, y: ponto.y })),
        height: conferido.value.height,
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, pileCaps: [...blocos, bloco] };
    }

    case 'UPDATE_PILE_CAP': {
      const blocos = clean.pileCaps ?? [];
      const bloco = blocos.find((candidato) => candidato.id === action.pieceId);
      if (bloco === undefined) return { ...clean, lastError: BLOCO_NAO_ENCONTRADO };
      if (action.outline !== undefined && !pontosValidos(action.outline)) {
        return { ...clean, lastError: CONTORNO_DO_BLOCO_INVALIDO };
      }
      const conferido = checkPileCapFields(
        {
          name: action.name ?? bloco.name,
          outline: action.outline ?? bloco.outline,
          height: action.height ?? bloco.height,
        },
        blocos,
        bloco.id,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      return {
        ...clean,
        pileCaps: blocos.map((candidato) =>
          candidato.id === bloco.id
            ? {
                ...candidato,
                name: conferido.value.name,
                outline: conferido.value.outline.map((ponto) => ({ x: ponto.x, y: ponto.y })),
                height: conferido.value.height,
                updatedAt: new Date().toISOString(),
              }
            : candidato,
        ),
      };
    }

    case 'REMOVE_PILE_CAP':
      return {
        ...clean,
        pileCaps: (clean.pileCaps ?? []).filter((bloco) => bloco.id !== action.pieceId),
      };

    case 'SET_PILE_CAP_TYPE_OF': {
      const blocos = clean.pileCaps ?? [];
      const alvo = new Set<string>(action.pieceIds);
      const escolhidos = blocos.filter((bloco) => alvo.has(bloco.id));
      if (escolhidos.length !== alvo.size) return { ...clean, lastError: BLOCO_NAO_ENCONTRADO };
      if (
        action.typeId !== undefined &&
        pileCapTypes !== undefined &&
        !pileCapTypes.some((tipo) => tipo.id === action.typeId && tipo.deletedAt === undefined)
      ) {
        return { ...clean, lastError: TIPO_DE_BLOCO_DESCONHECIDO };
      }
      // O bloco importado não recebe tipo (RN-540): a quantidade dele já veio pronta. A recusa é
      // do lote inteiro — dar o tipo a metade dos marcados seria uma meia mudança calada.
      if (action.typeId !== undefined) {
        const importadas = pecasImportadas(clean.pieceQuantities);
        const daPlanilha = escolhidos.find((bloco) => importadas.has(nomeDaPeca(bloco.name)));
        if (daPlanilha !== undefined) {
          return { ...clean, lastError: BLOCO_DA_PLANILHA_SEM_TIPO(daPlanilha.name) };
        }
      }
      const agora = new Date().toISOString();
      return {
        ...clean,
        pileCaps: blocos.map((bloco) => {
          if (!alvo.has(bloco.id)) return bloco;
          // Sem tipo, a chave sai do objeto: ausente é "segue a obra" para o servidor (RN-537).
          const { pileCapTypeId: _antes, ...semTipo } = bloco;
          return action.typeId === undefined
            ? { ...semTipo, updatedAt: agora }
            : { ...semTipo, pileCapTypeId: action.typeId, updatedAt: agora };
        }),
      };
    }

    case 'SET_FOUNDATION_FACTOR':
      return comFatorDaFundacao(clean, action);

    case 'SET_PIECE_STEEL_RATES':
      return comTaxaDaFundacao(clean, action);

    case 'ADD_GROUND_BEAM': {
      if (!estacaCabeAqui(clean)) return { ...clean, lastError: SO_NA_FUNDACAO_DO_BALDRAME };
      if (!pontosValidos([action.from, action.to])) {
        return { ...clean, lastError: PONTOS_DO_BALDRAME_INVALIDOS };
      }
      const baldrames = clean.groundBeams ?? [];
      const conferido = checkGroundBeamFields(
        {
          name: action.name,
          from: action.from,
          to: action.to,
          width: action.width,
          height: action.height,
        },
        baldrames,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const agora = new Date().toISOString();
      const baldrame: GroundBeam = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'BALDRAME',
        name: conferido.value.name,
        from: { x: conferido.value.from.x, y: conferido.value.from.y },
        to: { x: conferido.value.to.x, y: conferido.value.to.y },
        width: conferido.value.width,
        height: conferido.value.height,
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, groundBeams: [...baldrames, baldrame] };
    }

    case 'UPDATE_GROUND_BEAM': {
      const baldrames = clean.groundBeams ?? [];
      const baldrame = baldrames.find((candidato) => candidato.id === action.pieceId);
      if (baldrame === undefined) return { ...clean, lastError: BALDRAME_NAO_ENCONTRADO };
      const de = action.from ?? baldrame.from;
      const ate = action.to ?? baldrame.to;
      if (!pontosValidos([de, ate])) {
        return { ...clean, lastError: PONTOS_DO_BALDRAME_INVALIDOS };
      }
      const conferido = checkGroundBeamFields(
        {
          name: action.name ?? baldrame.name,
          from: de,
          to: ate,
          width: action.width ?? baldrame.width,
          height: action.height ?? baldrame.height,
        },
        baldrames,
        baldrame.id,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      return {
        ...clean,
        groundBeams: baldrames.map((candidato) =>
          candidato.id === baldrame.id
            ? {
                ...candidato,
                name: conferido.value.name,
                from: { x: conferido.value.from.x, y: conferido.value.from.y },
                to: { x: conferido.value.to.x, y: conferido.value.to.y },
                width: conferido.value.width,
                height: conferido.value.height,
                updatedAt: new Date().toISOString(),
              }
            : candidato,
        ),
      };
    }

    case 'REMOVE_GROUND_BEAM':
      return {
        ...clean,
        groundBeams: (clean.groundBeams ?? []).filter((baldrame) => baldrame.id !== action.pieceId),
      };

    case 'ADD_RETAINING_WALL': {
      if (!estacaCabeAqui(clean)) return { ...clean, lastError: SO_NA_FUNDACAO_DO_MURO };
      if (!pontosValidos([action.from, action.to])) {
        return { ...clean, lastError: PONTOS_DO_MURO_INVALIDOS };
      }
      const muros = clean.retainingWalls ?? [];
      const conferido = checkRetainingWallFields(
        {
          name: action.name,
          from: action.from,
          to: action.to,
          height: action.height,
          thickness: action.thickness,
        },
        muros,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const agora = new Date().toISOString();
      const muro: RetainingWall = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'MURO_CONTENCAO',
        name: conferido.value.name,
        from: { x: conferido.value.from.x, y: conferido.value.from.y },
        to: { x: conferido.value.to.x, y: conferido.value.to.y },
        height: conferido.value.height,
        thickness: conferido.value.thickness,
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, retainingWalls: [...muros, muro] };
    }

    case 'UPDATE_RETAINING_WALL': {
      const muros = clean.retainingWalls ?? [];
      const muro = muros.find((candidato) => candidato.id === action.pieceId);
      if (muro === undefined) return { ...clean, lastError: MURO_NAO_ENCONTRADO };
      const de = action.from ?? muro.from;
      const ate = action.to ?? muro.to;
      if (!pontosValidos([de, ate])) return { ...clean, lastError: PONTOS_DO_MURO_INVALIDOS };
      const conferido = checkRetainingWallFields(
        {
          name: action.name ?? muro.name,
          from: de,
          to: ate,
          height: action.height ?? muro.height,
          thickness: action.thickness ?? muro.thickness,
        },
        muros,
        muro.id,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const depois: RetainingWall = {
        ...muro,
        name: conferido.value.name,
        from: { x: conferido.value.from.x, y: conferido.value.from.y },
        to: { x: conferido.value.to.x, y: conferido.value.to.y },
        height: conferido.value.height,
        thickness: conferido.value.thickness,
        updatedAt: new Date().toISOString(),
      };
      // O pilar que continua encaixado no muro dobrado copia o ângulo novo dele (RN-372): o
      // que perdeu o muro fica solto como estava, e o que já tinha outro ângulo não é tocado.
      const anguloAntes = wallAngle(muro);
      const anguloDepois = wallAngle(depois);
      const acompanha = (pilar: RetainingColumn): boolean =>
        anguloAntes !== anguloDepois &&
        pilar.deletedAt === undefined &&
        Math.abs(pilar.angle - anguloAntes) < 0.01 &&
        isColumnInWall(pilar, muro) &&
        isColumnInWall(pilar, depois);
      const pilaresDoMuro = clean.retainingColumns ?? [];
      return {
        ...clean,
        retainingWalls: muros.map((candidato) => (candidato.id === muro.id ? depois : candidato)),
        ...(pilaresDoMuro.some(acompanha)
          ? {
              retainingColumns: pilaresDoMuro.map((pilar) =>
                acompanha(pilar)
                  ? { ...pilar, angle: anguloDepois, updatedAt: depois.updatedAt }
                  : pilar,
              ),
            }
          : {}),
      };
    }

    // Mover ou excluir o muro não leva os pilares: o que deixa de estar encaixado fica solto,
    // e a prancha o avisa (RN-372).
    case 'REMOVE_RETAINING_WALL':
      return {
        ...clean,
        retainingWalls: (clean.retainingWalls ?? []).filter((muro) => muro.id !== action.pieceId),
      };

    case 'ADD_RETAINING_COLUMN': {
      if (!estacaCabeAqui(clean)) {
        return { ...clean, lastError: SO_NA_FUNDACAO_DO_PILAR_DE_CONTENCAO };
      }
      if (!centroValido(action.center)) return { ...clean, lastError: CENTRO_DO_PILAR_INVALIDO };
      const pilares = clean.retainingColumns ?? [];
      const conferido = checkRetainingColumnFields(
        {
          name: action.name,
          center: action.center,
          sectionWidth: action.sectionWidth,
          sectionDepth: action.sectionDepth,
          height: action.height,
          angle: action.angle,
        },
        pilares,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const porCima = pilares.find(
        (outro) => outro.deletedAt === undefined && retainingColumnsOverlap(conferido.value, outro),
      );
      if (porCima !== undefined) {
        return { ...clean, lastError: nomeDoPilarSobreposto(porCima.name) };
      }
      const agora = new Date().toISOString();
      const pilar: RetainingColumn = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'PILAR_CONTENCAO',
        name: conferido.value.name,
        center: { x: conferido.value.center.x, y: conferido.value.center.y },
        sectionWidth: conferido.value.sectionWidth,
        sectionDepth: conferido.value.sectionDepth,
        height: conferido.value.height,
        angle: conferido.value.angle,
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, retainingColumns: [...pilares, pilar] };
    }

    case 'UPDATE_RETAINING_COLUMN': {
      const pilares = clean.retainingColumns ?? [];
      const pilar = pilares.find((candidato) => candidato.id === action.pieceId);
      if (pilar === undefined) return { ...clean, lastError: PILAR_DE_CONTENCAO_NAO_ENCONTRADO };
      const centro = action.center ?? pilar.center;
      if (!centroValido(centro)) return { ...clean, lastError: CENTRO_DO_PILAR_INVALIDO };
      const conferido = checkRetainingColumnFields(
        {
          name: action.name ?? pilar.name,
          center: centro,
          sectionWidth: action.sectionWidth ?? pilar.sectionWidth,
          sectionDepth: action.sectionDepth ?? pilar.sectionDepth,
          height: action.height ?? pilar.height,
          angle: action.angle ?? pilar.angle,
        },
        pilares,
        pilar.id,
      );
      if (!conferido.ok) return { ...clean, lastError: conferido.error.message };
      const porCima = pilares.find(
        (outro) =>
          outro.id !== pilar.id &&
          outro.deletedAt === undefined &&
          retainingColumnsOverlap(conferido.value, outro),
      );
      if (porCima !== undefined) {
        return { ...clean, lastError: nomeDoPilarSobreposto(porCima.name) };
      }
      return {
        ...clean,
        retainingColumns: pilares.map((candidato) =>
          candidato.id === pilar.id
            ? {
                ...candidato,
                name: conferido.value.name,
                center: { x: conferido.value.center.x, y: conferido.value.center.y },
                sectionWidth: conferido.value.sectionWidth,
                sectionDepth: conferido.value.sectionDepth,
                height: conferido.value.height,
                angle: conferido.value.angle,
                updatedAt: new Date().toISOString(),
              }
            : candidato,
        ),
      };
    }

    case 'REMOVE_RETAINING_COLUMN':
      return {
        ...clean,
        retainingColumns: (clean.retainingColumns ?? []).filter(
          (pilar) => pilar.id !== action.pieceId,
        ),
      };

    case 'ADD_SLAB': {
      if (action.kind === 'RADIER' ? !radierCabeAqui(clean) : !lajeCabeAqui(clean)) {
        return {
          ...clean,
          lastError: action.kind === 'RADIER' ? SO_NA_FUNDACAO_DO_RADIER : SO_NA_AREA_COMUM_DA_LAJE,
        };
      }
      if (!pontosValidos(action.outline)) return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
      const pecas = clean.slabs ?? [];
      const conferida = checkSlabFields(
        action.kind,
        { name: action.name, outline: action.outline, thickness: action.thickness, regions: [] },
        pecas,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      const agora = new Date().toISOString();
      const peca: Slab = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: action.kind,
        name: conferida.value.name,
        outline: copiarContorno(conferida.value.outline),
        thickness: conferida.value.thickness,
        regions: [],
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, slabs: [...pecas, peca] };
    }

    case 'UPDATE_SLAB': {
      const pecas = clean.slabs ?? [];
      const peca = pecas.find((candidata) => candidata.id === action.pieceId);
      if (peca === undefined) return { ...clean, lastError: LAJE_NAO_ENCONTRADA };
      if (action.outline !== undefined && !pontosValidos(action.outline)) {
        return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
      }
      // Arrastar a peça é o mesmo contorno em outro lugar: as regiões não podem ficar para trás.
      const deslocamento =
        action.outline === undefined
          ? undefined
          : deslocamentoDoContorno(peca.outline, action.outline);
      const regioes: readonly SlabRegion[] =
        deslocamento === undefined
          ? peca.regions
          : peca.regions.map((regiao) => ({
              ...regiao,
              outline: regiao.outline.map((ponto) => ({
                x: ponto.x + deslocamento.x,
                y: ponto.y + deslocamento.y,
              })),
            }));
      const conferida = checkSlabFields(
        peca.kind,
        {
          name: action.name ?? peca.name,
          outline: action.outline ?? peca.outline,
          thickness: action.thickness ?? peca.thickness,
          regions: regioes,
        },
        pecas,
        peca.id,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      return {
        ...clean,
        slabs: pecas.map((candidata) =>
          candidata.id === peca.id
            ? {
                ...candidata,
                name: conferida.value.name,
                outline: copiarContorno(conferida.value.outline),
                thickness: conferida.value.thickness,
                regions: conferida.value.regions.map((regiao) => ({
                  outline: copiarContorno(regiao.outline),
                  thickness: regiao.thickness,
                })),
                updatedAt: new Date().toISOString(),
              }
            : candidata,
        ),
      };
    }

    case 'REMOVE_SLAB':
      return {
        ...clean,
        slabs: (clean.slabs ?? []).filter((peca) => peca.id !== action.pieceId),
      };

    case 'ADD_SLAB_REGION':
    case 'UPDATE_SLAB_REGION':
    case 'REMOVE_SLAB_REGION': {
      const pecas = clean.slabs ?? [];
      const peca = pecas.find((candidata) => candidata.id === action.pieceId);
      if (peca === undefined) return { ...clean, lastError: LAJE_NAO_ENCONTRADA };

      let regioes: readonly SlabRegion[];
      if (action.type === 'ADD_SLAB_REGION') {
        if (!pontosValidos(action.outline)) {
          return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
        }
        regioes = [...peca.regions, { outline: action.outline, thickness: action.thickness }];
      } else {
        if (peca.regions[action.index] === undefined) {
          return { ...clean, lastError: REGIAO_NAO_ENCONTRADA };
        }
        if (action.type === 'REMOVE_SLAB_REGION') {
          regioes = peca.regions.filter((_regiao, indice) => indice !== action.index);
        } else {
          if (action.outline !== undefined && !pontosValidos(action.outline)) {
            return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
          }
          regioes = peca.regions.map((regiao, indice) =>
            indice === action.index
              ? {
                  outline: action.outline ?? regiao.outline,
                  thickness: action.thickness ?? regiao.thickness,
                }
              : regiao,
          );
        }
      }

      // Tirar uma região nunca deixa a peça pior, mas a conferência é a mesma — a regra mora
      // num lugar só, e o domínio diz por que recusou (fora do contorno, sobrepondo outra).
      const conferida = checkSlabFields(
        peca.kind,
        { name: peca.name, outline: peca.outline, thickness: peca.thickness, regions: regioes },
        pecas,
        peca.id,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      return {
        ...clean,
        slabs: pecas.map((candidata) =>
          candidata.id === peca.id
            ? {
                ...candidata,
                regions: conferida.value.regions.map((regiao) => ({
                  outline: copiarContorno(regiao.outline),
                  thickness: regiao.thickness,
                })),
                updatedAt: new Date().toISOString(),
              }
            : candidata,
        ),
      };
    }

    case 'ADD_STAIR': {
      if (!escadaCabeAqui(clean)) return { ...clean, lastError: SO_NA_AREA_COMUM_DA_ESCADA };
      if (action.flight !== undefined && !pontosValidos(action.flight.outline)) {
        return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
      }
      const escadas = clean.stairs ?? [];
      const conferida = checkStairFields(
        {
          name: action.name,
          flights: action.flight === undefined ? [] : [action.flight],
          landings: [],
        },
        escadas,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      const agora = new Date().toISOString();
      const escada: Stair = {
        id: newId<'StructuralPiece'>(),
        unitTypeId: clean.unitTypeId,
        kind: 'ESCADA',
        name: conferida.value.name,
        flights: conferida.value.flights.map(copiarLance),
        landings: [],
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...clean, stairs: [...escadas, escada] };
    }

    case 'REMOVE_STAIR':
      return {
        ...clean,
        stairs: (clean.stairs ?? []).filter((escada) => escada.id !== action.pieceId),
      };

    case 'UPDATE_STAIR':
    case 'ADD_STAIR_FLIGHT':
    case 'UPDATE_STAIR_FLIGHT':
    case 'REMOVE_STAIR_FLIGHT':
    case 'ADD_STAIR_LANDING':
    case 'UPDATE_STAIR_LANDING':
    case 'REMOVE_STAIR_LANDING': {
      const escadas = clean.stairs ?? [];
      const escada = escadas.find((candidata) => candidata.id === action.pieceId);
      if (escada === undefined) return { ...clean, lastError: ESCADA_NAO_ENCONTRADA };

      let nome = escada.name;
      let lances: readonly StairFlight[] = escada.flights;
      let patamares: readonly StairLanding[] = escada.landings;

      switch (action.type) {
        case 'UPDATE_STAIR': {
          nome = action.name ?? escada.name;
          const { shift } = action;
          if (shift !== undefined) {
            if (!centroValido(shift)) {
              return { ...clean, lastError: DESLOCAMENTO_DA_ESCADA_INVALIDO };
            }
            const deslocar = (contorno: readonly Point[]): readonly Point[] =>
              contorno.map((ponto) => ({ x: ponto.x + shift.x, y: ponto.y + shift.y }));
            lances = lances.map((lance) => ({ ...lance, outline: deslocar(lance.outline) }));
            patamares = patamares.map((patamar) => ({
              ...patamar,
              outline: deslocar(patamar.outline),
            }));
          }
          break;
        }
        case 'ADD_STAIR_FLIGHT':
          if (!pontosValidos(action.flight.outline)) {
            return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
          }
          lances = [...lances, action.flight];
          break;
        case 'UPDATE_STAIR_FLIGHT': {
          const lance = lances[action.index];
          if (lance === undefined) return { ...clean, lastError: LANCE_NAO_ENCONTRADO };
          if (action.outline !== undefined && !pontosValidos(action.outline)) {
            return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
          }
          lances = lances.map((candidato, indice) =>
            indice === action.index
              ? {
                  outline: action.outline ?? lance.outline,
                  steps: action.steps ?? lance.steps,
                  riser: action.riser ?? lance.riser,
                  tread: action.tread ?? lance.tread,
                  thickness: action.thickness ?? lance.thickness,
                }
              : candidato,
          );
          break;
        }
        case 'REMOVE_STAIR_FLIGHT':
          if (lances[action.index] === undefined) {
            return { ...clean, lastError: LANCE_NAO_ENCONTRADO };
          }
          lances = lances.filter((_lance, indice) => indice !== action.index);
          break;
        case 'ADD_STAIR_LANDING':
          if (!pontosValidos(action.landing.outline)) {
            return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
          }
          patamares = [...patamares, action.landing];
          break;
        case 'UPDATE_STAIR_LANDING': {
          const patamar = patamares[action.index];
          if (patamar === undefined) return { ...clean, lastError: PATAMAR_NAO_ENCONTRADO };
          if (action.outline !== undefined && !pontosValidos(action.outline)) {
            return { ...clean, lastError: CONTORNO_DA_LAJE_INVALIDO };
          }
          patamares = patamares.map((candidato, indice) =>
            indice === action.index
              ? {
                  outline: action.outline ?? patamar.outline,
                  thickness: action.thickness ?? patamar.thickness,
                }
              : candidato,
          );
          break;
        }
        case 'REMOVE_STAIR_LANDING':
          if (patamares[action.index] === undefined) {
            return { ...clean, lastError: PATAMAR_NAO_ENCONTRADO };
          }
          patamares = patamares.filter((_patamar, indice) => indice !== action.index);
          break;
      }

      // A regra mora num lugar só: o domínio diz por que recusou (nome repetido, sem lance,
      // contorno sem área, degrau sem medida).
      const conferida = checkStairFields(
        { name: nome, flights: lances, landings: patamares },
        escadas,
        escada.id,
      );
      if (!conferida.ok) return { ...clean, lastError: conferida.error.message };
      return {
        ...clean,
        stairs: escadas.map((candidata) =>
          candidata.id === escada.id
            ? {
                ...candidata,
                name: conferida.value.name,
                flights: conferida.value.flights.map(copiarLance),
                landings: conferida.value.landings.map(copiarPatamar),
                updatedAt: new Date().toISOString(),
              }
            : candidata,
        ),
      };
    }

    case 'SET_ITEM_QUANTITY': {
      const chave = itemQuantityKey(action.serviceKey, action.itemKey);
      // Discordar do calculado sem motivo não entra (D9); sem calculado, o motivo não é
      // pedido nem guardado — o número é a quantidade, e não uma discordância.
      const decidido = decideItemOverride({
        description: action.itemKey,
        calculated: action.calculated,
        reason: action.reason,
      });
      if (!decidido.ok) return { ...clean, lastError: decidido.error.message };
      const { [chave]: _motivoAnterior, ...outrosMotivos } = clean.itemReasons ?? {};
      return {
        ...clean,
        itemQuantities: { ...clean.itemQuantities, [chave]: action.quantity },
        ...(decidido.value.reason === undefined
          ? clean.itemReasons === undefined
            ? {}
            : { itemReasons: outrosMotivos }
          : { itemReasons: { ...outrosMotivos, [chave]: decidido.value.reason } }),
      };
    }

    case 'CLEAR_ITEM_QUANTITY': {
      const chave = itemQuantityKey(action.serviceKey, action.itemKey);
      const { [chave]: _fora, ...restantes } = clean.itemQuantities ?? {};
      // O motivo sai com o número: sem o digitado, não há de que ele fosse a razão.
      const { [chave]: _motivo, ...outrosMotivos } = clean.itemReasons ?? {};
      return {
        ...clean,
        itemQuantities: restantes,
        ...(clean.itemReasons === undefined ? {} : { itemReasons: outrosMotivos }),
      };
    }

    case 'CLEAR_SERVICE_QUANTITIES': {
      const prefixo = itemQuantityKey(action.serviceKey, '');
      // O número do próprio serviço sai junto (fatia 5): é decisão desta tipologia sobre
      // um serviço que ela deixou de usar, e ressuscitaria no dia em que voltasse a usá-lo.
      const { [action.serviceKey]: _doServico, ...outrosServicos } = clean.serviceQuantities ?? {};
      return {
        ...clean,
        itemQuantities: Object.fromEntries(
          Object.entries(clean.itemQuantities ?? {}).filter(
            ([chave]) => !chave.startsWith(prefixo),
          ),
        ),
        ...(clean.serviceQuantities === undefined ? {} : { serviceQuantities: outrosServicos }),
        ...(clean.itemReasons === undefined
          ? {}
          : {
              itemReasons: Object.fromEntries(
                Object.entries(clean.itemReasons).filter(([chave]) => !chave.startsWith(prefixo)),
              ),
            }),
      };
    }

    case 'RESET':
      // O id **sobrevive**: recomeçar é esvaziar esta tipologia, não trocá-la por outra.
      // Com um id novo, a bancada continuaria apontando para o antigo, `activeTypology`
      // cairia no desempate e a mesa saltaria em silêncio para a primeira da lista — quem
      // pediu para recomeçar a Tipologia B ficaria olhando a A.
      return {
        ...emptyDraft(draft.name, draft.kind),
        unitTypeId: draft.unitTypeId,
        // As peças importadas também esvaziam (Fase 28) — vazias, e não ausentes, para a
        // gravação seguinte apagá-las do servidor como apaga o resto (RN-390).
        ...(draft.pieceQuantities === undefined
          ? {}
          : { pieceQuantities: semQuantidadesPorPeca() }),
      };
  }
}

// ── A bancada ───────────────────────────────────────────────────────────────────

/**
 * As tipologias da obra e a que está sendo montada.
 *
 * Uma tipologia sozinha nunca foi o caso real: um pavimento tipo tem seis aptos e uma área
 * comum. A bancada existe para que montar a segunda não signifique apagar a primeira — e
 * para que a tela consiga dizer, o tempo todo, **qual** delas está na mesa.
 */
/**
 * Um pavimento real da torre.
 *
 * `hasUnits` é o que liga a estrutura ao quantitativo: só pavimento com apartamento
 * recebe tipologia. Tipo e garden nascem com ele ligado e não abrem mão — o tipo se
 * repete porque os aptos dele se repetem, e o garden só existe por causa dos especiais
 * (RN-179).
 */
export interface FloorDraft {
  readonly id: string;
  readonly name: string;
  readonly level: number;
  readonly kind: FloorPlanKind;
  /** O desenho que o pavimento usa — o que liga o andar às tipologias daquele desenho. */
  readonly floorPlanId?: string;
  readonly hasUnits: boolean;
}

export interface TowerDraft {
  readonly id: string;
  readonly name: string;
  readonly floors: readonly FloorDraft[];
}

/**
 * A planta carregada, e a escala que a torna medível.
 *
 * Fica na bancada, e não na tipologia: a prancha é do **pavimento**, e é traçando todas as
 * tipologias sobre ela que os contornos dividem a mesma origem — que é o que faz a divisa
 * entre apartamentos vizinhos aparecer por geometria.
 */
export interface PlanDraft {
  /**
   * A página renderizada, como `data:`.
   *
   * Vive só na memória da tela (Fase 24): o arquivo e a escala são da obra, guardados no
   * servidor por desenho de pavimento, e a imagem é refeita a partir deles a cada abertura.
   */
  readonly image: string;
  readonly width: number;
  readonly height: number;
  /** Metros por pixel. Ausente = ainda não calibrada, e nada pode ser traçado. */
  readonly metresPerPixel?: number;
}

export interface WorkspaceDraft {
  /**
   * A estrutura vem **antes**.
   *
   * Sem torre e pavimento não há onde pendurar tipologia nenhuma: o quantitativo mora na
   * tipologia, mas o total é ela vezes os andares que a repetem — e esse número sai daqui.
   */
  readonly towers: readonly TowerDraft[];
  readonly plan?: PlanDraft;
  /**
   * O catálogo importado da planilha de serviços — disciplina → serviço → item.
   *
   * Fica na bancada, e não na tipologia, porque é da **obra**: as mesmas quarenta linhas
   * de serviço valem para as seis tipologias, e guardar uma cópia em cada uma criaria
   * seis catálogos que envelheceriam à parte.
   */
  readonly catalog?: ServiceCatalog;
  readonly typologies: readonly TypologyDraft[];
  readonly activeId: UnitTypeId;
  /**
   * As divisas entre tipologias — **paredes do pavimento**, e não de nenhuma delas.
   *
   * A parede entre o Apartamento 1 e o 2 é uma só, e é isso que o lugar dela precisa
   * dizer. Guardada dentro de uma das tipologias, a outra não a enxergaria e perderia a
   * face daquele lado; guardada nas duas, o prédio ganharia uma parede que não existe —
   * vinte andares vezes duas vezes. Morando aqui, cada tipologia lê a mesma parede e leva
   * a sua parte (RN-163).
   */
  readonly partyWalls?: readonly Wall[];
  /** Os vãos das divisas. Moram junto com elas, pelo mesmo motivo. */
  readonly partyOpenings?: readonly Opening[];
  /**
   * O pilar e a viga das divisas — e o motivo é o mesmo das paredes.
   *
   * A viga que corre em cima da divisa é **uma**, e o concreto dela se reparte entre os
   * dois aptos como a alvenaria se reparte (RN-163). Cadastrada dentro de uma tipologia,
   * o vizinho não a veria e continuaria cobrando bloco onde há concreto; cadastrada nas
   * duas, o prédio ganharia uma viga que não existe.
   */
  readonly partyElements?: readonly StructuralElement[];
  readonly partyElementSegments?: readonly ElementSegment[];
  /** O nome de cada divisa — os dois lados dizem de que tipologia cada um é. */
  readonly partyLabels?: Readonly<Record<string, WallLabel>>;
  /**
   * O que cada apto faz de diferente da tipologia dele (RN-167).
   *
   * Por `unitKey(floorId, unitTypeId)`, e **só o que diverge**. A ausência é o caso comum
   * e quer dizer "este apto segue a tipologia" — que é a razão de o quantitativo morar na
   * tipologia: cento e vinte aptos não guardam cento e vinte cópias do mesmo número.
   *
   * A leitura é "as linhas da tipologia, **menos** as que este apto sombreia". Guardar a
   * cópia inteira em cada apto transformaria "ninguém mexeu nisto" em "alguém confirmou
   * isto", e as duas se resolvem de formas diferentes quando a tipologia mudar.
   */
  readonly unitShadows?: Readonly<Record<string, UnitShadow>>;
  /** Os tipos de estaca da obra (Fase 27, D1). São da obra, e não da tipologia. */
  readonly pileTypes?: readonly PileType[];
  /**
   * Os tipos de bloco da obra (Fase 41, RN-536), no molde dos de estaca: da obra, e não da
   * tipologia. Ausente é "ainda não sei" — a bancada antes de o servidor responder.
   */
  readonly pileCapTypes?: readonly PileCapType[];
  /**
   * Os tipos de pilar e de viga da obra (Fase 42, RN-561), no molde dos de bloco: da obra, cada
   * um de um elemento. Ausente é "ainda não sei" — a bancada antes de o servidor responder.
   */
  readonly elementTypes?: readonly StructuralElementType[];
  /**
   * A última recusa da bancada, para a tela dizer o que não passou.
   *
   * O `draftReducer` já tinha o dele e a página já o mostra; as ações de bancada não
   * tinham canal nenhum, e recusar em silêncio é pior do que recusar: o número digitado
   * some no `onBlur` e a pessoa fica sem saber se errou ou se a tela quebrou
   * (PLANO_GERAL §4.3).
   *
   * Sai a cada ação, como o da tipologia: erro que sobrevive à ação seguinte vira aviso
   * de coisa nenhuma.
   */
  readonly lastError?: string;
}

/**
 * A linha-sombra de um apto (RN-167, RN-168).
 *
 * `REPLACE` troca a quantidade; `REMOVE` diz que ali aquilo não acontece — e não é o
 * mesmo que zero, porque zero seria uma quantidade lançada.
 *
 * Guarda **autor, data e motivo**, os três que a RN-168 exige. O motivo explica o número
 * diferente seis meses depois; o autor diz a quem perguntar quando a explicação não
 * bastar. É o mesmo trio de `UnitLineOverride` no domínio, que é quem manda quando a fase
 * ganhar banco — aqui o registro é de rascunho, sem os identificadores que ainda não
 * existem (não há `UnitId` nem `TakeoffLineId` numa bancada de navegador).
 */
export interface UnitShadowLine {
  readonly effect: OverrideEffect;
  /** Presente exatamente quando `effect` é `REPLACE`. */
  readonly quantity?: number;
  readonly reason: string;
  readonly by: UserId;
  /** O nome de quem decidiu, para a tela não depender de buscar o perfil. */
  readonly byName: string;
  readonly at: string;
}

export interface UnitShadow {
  /** Por `itemQuantityKey(serviceKey, itemKey)`. */
  readonly lines: Readonly<Record<string, UnitShadowLine>>;
}

/** Três casas, como o domínio arredonda item: é assim que se compra argamassa e tinta. */
const QUANTITY_DECIMALS = 3;

/**
 * O apto: um pavimento real × uma tipologia daquele pavimento.
 *
 * Não existe registro para ele enquanto ele não diverge — é o que RN-177 quer dizer com
 * "o apto materializado herda da tipologia e nunca copia o quantitativo". O que existe é
 * a **chave**, e ela é derivada: o andar que a estrutura tem, e o desenho que ele usa.
 */
export function unitKey(floorId: string, unitTypeId: UnitTypeId): string {
  return `${floorId}@${unitTypeId}`;
}

export interface UnitDraft {
  readonly key: string;
  readonly tower: TowerDraft;
  readonly floor: FloorDraft;
  readonly typology: TypologyDraft;
}

/** Os aptos que a estrutura e as tipologias produzem juntas — nenhum deles é gravado. */
export function unitsOf(workspace: WorkspaceDraft): readonly UnitDraft[] {
  return workspace.towers.flatMap((tower) =>
    tower.floors.flatMap((floor) =>
      typologiesOfDrawing(workspace, {
        floorKind: floor.kind,
        floorPlanId: floor.floorPlanId,
      }).map((typology): UnitDraft => ({
        key: unitKey(floor.id, typology.unitTypeId),
        tower,
        floor,
        typology,
      })),
    ),
  );
}

/** O que este apto diverge — ausente quando ele segue a tipologia. */
export function shadowOf(workspace: WorkspaceDraft, key: string): UnitShadow | undefined {
  return workspace.unitShadows?.[key];
}

/**
 * As quantidades que valem **neste apto**: as da tipologia, menos as que ele sombreia.
 *
 * O `REMOVE` sai como zero de propósito: é assim que a consolidação já entende "não
 * entra" (ela pula quantidade zero), e assim a sombra não precisa de um caminho próprio
 * lá dentro.
 */
export function quantitiesOfUnit(
  workspace: WorkspaceDraft,
  unit: UnitDraft,
): Readonly<Record<string, number>> {
  const daTipologia = unit.typology.itemQuantities ?? {};
  const sombra = shadowOf(workspace, unit.key);
  if (sombra === undefined) return daTipologia;

  const resultado: Record<string, number> = { ...daTipologia };
  for (const [chave, linha] of Object.entries(sombra.lines)) {
    resultado[chave] = linha.effect === 'REMOVE' ? 0 : (linha.quantity ?? 0);
  }
  return resultado;
}

/**
 * A posição do pavimento que esta tipologia ocupa.
 *
 * No rascunho, **uma tipologia é uma posição**: a bancada monta o andar tipo com uma
 * instância de cada, e é isso que permite a divisa saber de quem é cada metade sem que
 * exista ainda a tabela de posições. Quando o apto 01 e o 03 passarem a compartilhar a
 * Tipologia A de verdade, esta função é o ponto onde a diferença aparece — e ela é a
 * única passagem do id de tipologia para o de posição no sistema inteiro.
 */
export function positionOfTypology(unitTypeId: UnitTypeId): UnitPositionId {
  return asId<'UnitPosition'>(unitTypeId);
}

const partyWallsOf = (workspace: WorkspaceDraft): readonly Wall[] => workspace.partyWalls ?? [];
const partyElementsOf = (workspace: WorkspaceDraft): readonly StructuralElement[] =>
  workspace.partyElements ?? [];
const partySegmentsOf = (workspace: WorkspaceDraft): readonly ElementSegment[] =>
  workspace.partyElementSegments ?? [];

/**
 * A altura das paredes deste ambiente.
 *
 * As quatro nascem juntas e com a mesma altura; a parede acrescentada depois, ou
 * redesenhada sobre a planta, herda a das irmãs em vez de voltar ao padrão. Sem isso, um
 * ambiente de pé-direito duplo ganharia uma parede de 2,90 no meio das de 5,40 — e a
 * alvenaria erraria sem nada na tela denunciando.
 */
function alturaDasParedesDe(walls: readonly Wall[], roomId: RoomId): number {
  // A mureta não é irmã de altura: a parede nova da varanda não nasce com 1,10 porque a
  // primeira da lista é a mureta com guarda-corpo (D25).
  const irma = walls.find(
    (wall) =>
      (wall.sideA.roomId === roomId || wall.sideB?.roomId === roomId) &&
      wall.railingHeight === undefined,
  );
  return irma?.height ?? DEFAULT_WALL_HEIGHT;
}

/** A tipologia dona de um ambiente — quem responde pela metade dele na divisa. */
export function typologyOfRoom(
  workspace: WorkspaceDraft,
  roomId: RoomId,
): TypologyDraft | undefined {
  return workspace.typologies.find((typology) => typology.rooms.some((room) => room.id === roomId));
}

/** As divisas que encostam nesta tipologia — as que ela precisa contar e desenhar. */
/**
 * A estrutura que esta tipologia vê: a dela, mais a das divisas que a tocam.
 *
 * A viga da divisa é da bancada, e mesmo assim entra no desenho das duas tipologias — o
 * concreto dela se reparte, mas a peça aparece inteira nas duas telas. Contar só
 * `typology.elements` diria "1 peça" para quem está olhando duas.
 */
export function structureOf(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): {
  readonly elements: readonly StructuralElement[];
  readonly segments: readonly ElementSegment[];
} {
  const divisas = new Set(partyWallsTouching(workspace, typology).map((wall) => wall.id));
  const daBancada = partySegmentsOf(workspace).filter((segment) => divisas.has(segment.wallId));
  const ids = new Set(daBancada.map((segment) => segment.elementId));

  return {
    elements: [
      ...typology.elements,
      ...partyElementsOf(workspace).filter((element) => ids.has(element.id)),
    ],
    segments: [...typology.elementSegments, ...daBancada],
  };
}

export function partyWallsTouching(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): readonly Wall[] {
  const meus = new Set(typology.rooms.map((room) => room.id));
  return partyWallsOf(workspace).filter(
    (wall) =>
      meus.has(wall.sideA.roomId) || (wall.sideB !== undefined && meus.has(wall.sideB.roomId)),
  );
}

/**
 * Os ambientes do outro lado das divisas desta tipologia.
 *
 * Vão para `neighbourRooms`: dão lado à parede sem entrar no piso nem no teto de quem
 * está sendo calculado.
 */
export function neighbourRoomsFor(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): readonly Room[] {
  const meus = new Set(typology.rooms.map((room) => room.id));
  const procurados = new Set<RoomId>();
  for (const wall of partyWallsTouching(workspace, typology)) {
    for (const lado of wall.sideB === undefined ? [wall.sideA] : [wall.sideA, wall.sideB]) {
      if (!meus.has(lado.roomId)) procurados.add(lado.roomId);
    }
  }

  return workspace.typologies
    .flatMap((outra) => outra.rooms)
    .filter((room) => procurados.has(room.id));
}

/** As posições que são área comum — as que abrem mão da divisa para o apto (RN-178). */
export function commonAreaPositions(workspace: WorkspaceDraft): readonly UnitPositionId[] {
  return workspace.typologies
    .filter((typology) => typology.kind === 'AREA_COMUM')
    .map((typology) => positionOfTypology(typology.unitTypeId));
}

export interface CandidataDeFora {
  readonly wall: Wall;
  readonly typology: TypologyDraft;
}

/**
 * As paredes livres das **outras** tipologias — as candidatas a virar divisa.
 *
 * É o que faltava para o andar tipo fechar: a parede entre dois apartamentos vizinhos
 * nunca esteve dentro de uma tipologia só, e sem esta lista não havia como apontá-la.
 */
export function pairableAcrossTypologies(
  workspace: WorkspaceDraft,
  wall: Wall,
): readonly CandidataDeFora[] {
  const daAtiva = activeTypology(workspace).unitTypeId;

  // A parede que já virou divisa saiu de `typology.walls` para `partyWalls`: o filtro de
  // livres basta, e não há como oferecer duas vezes a mesma.
  return workspace.typologies
    .filter((typology) => typology.unitTypeId !== daAtiva)
    .flatMap((typology) =>
      typology.walls
        .filter((outra) => outra.id !== wall.id && outra.sideB === undefined)
        .map((outra): CandidataDeFora => ({ wall: outra, typology })),
    );
}

/** O que é da bancada — o resto é ação de ambiente e parede, e vale para a que está na mesa. */
export type BenchAction =
  | {
      /**
       * A recusa é da ação que falhou: trocar de ferramenta, ou esvaziar o campo que a causou,
       * a tira da tela. Não mexe em mais nada.
       */
      readonly type: 'DISMISS_ERROR';
    }
  | {
      /**
       * Os tipos de estaca da obra, inteiros (Fase 27, D1, D16). É a ação do cadastro de tipos
       * e do import da aba Tipos: quem chama já conferiu os campos (`checkPileTypeFields`).
       */
      readonly type: 'SET_PILE_TYPES';
      readonly pileTypes: readonly PileType[];
    }
  | {
      /**
       * O tipo de bloco novo da obra (Fase 41, RN-536): só o nome, com a composição vazia — o
       * bloco dele segue o índice da obra até alguém dar os fatores. Nasce no card do bloco,
       * digitado no campo "Tipo", e o card já o dá ao bloco (`SET_PILE_CAP_TYPE_OF`).
       */
      readonly type: 'ADD_PILE_CAP_TYPE';
      readonly name: string;
      /** O id escolhido por quem cria, para dar o tipo ao bloco logo em seguida. */
      readonly id?: PileCapTypeId;
      /** A obra do tipo. Ausente, a dos tipos que já existem. */
      readonly projectId?: ProjectId;
    }
  | {
      readonly type: 'RENAME_PILE_CAP_TYPE';
      readonly typeId: PileCapTypeId;
      readonly name: string;
    }
  | {
      /** Sai o tipo, e os blocos que o apontavam voltam a seguir a obra (RN-537). */
      readonly type: 'REMOVE_PILE_CAP_TYPE';
      readonly typeId: PileCapTypeId;
    }
  | {
      /**
       * O fator de um item na composição do tipo de bloco (RN-536), sem motivo (D9). `factor`
       * ausente tira o item: o bloco do tipo volta ao índice da obra nele.
       */
      readonly type: 'SET_PILE_CAP_TYPE_RATE';
      readonly typeId: PileCapTypeId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
    }
  | {
      /**
       * A taxa de aço do tipo de bloco, kg/m³ por bitola (Fase 41, etapa 2 — RN-543): a lista
       * inteira. Vazia apaga, e o bloco do tipo volta à da disciplina.
       */
      readonly type: 'SET_PILE_CAP_TYPE_STEEL_RATES';
      readonly typeId: PileCapTypeId;
      readonly rates: readonly SteelRate[];
    }
  | {
      /**
       * O tipo de pilar ou de viga novo da obra (Fase 42, RN-561): o elemento e o nome, com a
       * composição vazia — a peça dele segue o índice da obra até alguém dar os fatores. Nasce no
       * card do pilar ou da viga, digitado no campo "Tipo", e o card já o dá à peça
       * (`SET_ELEMENT_TYPE_OF`).
       */
      readonly type: 'ADD_ELEMENT_TYPE';
      readonly kind: StructuralElementKind;
      readonly name: string;
      /** O id escolhido por quem cria, para dar o tipo à peça logo em seguida. */
      readonly id?: StructuralElementTypeId;
      /** A obra do tipo. Ausente, a dos tipos que já existem. */
      readonly projectId?: ProjectId;
    }
  | {
      readonly type: 'RENAME_ELEMENT_TYPE';
      readonly typeId: StructuralElementTypeId;
      readonly name: string;
    }
  | {
      /** Sai o tipo, e o pilar ou a viga que o apontava volta a seguir a obra (RN-562). */
      readonly type: 'REMOVE_ELEMENT_TYPE';
      readonly typeId: StructuralElementTypeId;
    }
  | {
      /**
       * O fator de um item na composição do tipo de pilar ou de viga (RN-561), sem motivo (D9).
       * `factor` ausente tira o item: a peça do tipo volta ao índice da obra nele.
       */
      readonly type: 'SET_ELEMENT_TYPE_RATE';
      readonly typeId: StructuralElementTypeId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
    }
  | {
      /**
       * Dar o tipo a um pilar ou a uma viga, ou a vários de uma vez (Fase 42, RN-562) — o card dá
       * a um, a lista "Aplicar o P20 a outros pilares" marca vários. `typeId` ausente tira o tipo:
       * a peça volta a seguir a obra.
       *
       * É da bancada, e não da tipologia na mesa: a viga da divisa mora na bancada, e o lote pode
       * misturar as duas. O tipo é do mesmo elemento de cada peça (RN-257); o lote que mistura
       * pilar e viga é recusado inteiro.
       */
      readonly type: 'SET_ELEMENT_TYPE_OF';
      readonly elementIds: readonly StructuralElementId[];
      readonly typeId?: StructuralElementTypeId;
    }
  | {
      /**
       * O fator próprio de um item no pilar ou na viga, por cima do tipo ou do índice da obra
       * (Fase 42, RN-563) — sem motivo (D9). `factor` ausente é a volta ao de baixo. Da bancada
       * pela mesma razão do tipo: a peça pode ser da divisa.
       */
      readonly type: 'SET_ELEMENT_FACTOR';
      readonly elementId: StructuralElementId;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly factor?: number;
    }
  | {
      /**
       * A taxa de aço própria do pilar ou da viga, kg/m³ por bitola (Fase 42, etapa 2 — RN-568):
       * a lista inteira, por cima da do tipo e da disciplina. Vazia apaga, e a peça volta à do
       * tipo ou à da disciplina. Da bancada pela mesma razão do fator: a peça pode ser da divisa.
       */
      readonly type: 'SET_ELEMENT_STEEL_RATES';
      readonly elementId: StructuralElementId;
      readonly rates: readonly SteelRate[];
    }
  | {
      /**
       * A taxa de aço do tipo de pilar ou de viga (Fase 42, etapa 2 — RN-567): a lista inteira.
       * Vazia apaga, e a peça do tipo volta à da disciplina.
       */
      readonly type: 'SET_ELEMENT_TYPE_STEEL_RATES';
      readonly typeId: StructuralElementTypeId;
      readonly rates: readonly SteelRate[];
    }
  | {
      /**
       * A média chega à composição e à taxa do tipo de bloco (Fase 42, etapa 4 — G11, RN-582,
       * RN-583; ADR-191): o tipo que nasce no card com a média da fundação, e o "Atualizar" do
       * que segue a média. Cada linha com `follow` passa a ser a média, marcada `MEDIA` e com a
       * foto; sem `follow`, só a foto muda — o digitado fica, com a média nova ao lado (D8). A
       * lista que vem vazia não mexe na do tipo.
       */
      readonly type: 'APPLY_PILE_CAP_TYPE_AVERAGE';
      readonly typeId: PileCapTypeId;
      readonly rates: readonly LinhaDaMediaNoTipo[];
      readonly steelRates: readonly BitolaDaMediaNoTipo[];
    }
  | {
      /**
       * A média chega à composição e à taxa do tipo de pilar ou de viga (Fase 42, etapa 4 — G10,
       * RN-582, RN-583; ADR-191), com a média da estrutura. A mesma regra do tipo de bloco.
       */
      readonly type: 'APPLY_ELEMENT_TYPE_AVERAGE';
      readonly typeId: StructuralElementTypeId;
      readonly rates: readonly LinhaDaMediaNoTipo[];
      readonly steelRates: readonly BitolaDaMediaNoTipo[];
    }
  | {
      /**
       * Estacas que chegam sem lugar, para a fila do "faltam N" (Fase 27, D5, F10). O nome que
       * já existe na tipologia não entra de novo (RN-376): quem importa avisa.
       */
      readonly type: 'IMPORT_PILES';
      readonly unitTypeId: UnitTypeId;
      readonly piles: readonly { readonly name: string; readonly pileTypeId: PileType['id'] }[];
    }
  | {
      /**
       * As quantidades prontas que a planilha por peça trouxe para uma tipologia (Fase 28,
       * RN-384, RN-392) — só as da planilha, e não a soma com as de antes.
       *
       * Quem junta é o reducer, por `juntarQuantidadesPorPeca`: a peça que veio troca todas as
       * quantidades dela, e a que não veio fica (RN-386). O catálogo — os itens novos que a
       * planilha criou — chega antes, por `IMPORT_CATALOG`.
       */
      readonly type: 'IMPORT_PIECE_QUANTITIES';
      readonly unitTypeId: UnitTypeId;
      readonly quantidades: QuantidadesPorPeca;
    }
  | {
      /**
       * A peça da planilha com nome novo (20/10/2026, RN-457) — o que o servidor acabou de
       * gravar, refeito na mesa: as quantidades dela em todos os serviços e itens, a peça
       * desenhada de mesmo nome e a lembrança do nome da planilha. Sem isto, a próxima gravação
       * da bancada levaria o nome antigo de volta.
       */
      readonly type: 'RENAME_IMPORTED_PIECE';
      readonly unitTypeId: UnitTypeId;
      readonly from: string;
      readonly to: string;
      readonly aliases: Readonly<Record<string, string>>;
    }
  | { readonly type: 'SET_PLAN'; readonly plan: PlanDraft }
  | { readonly type: 'CLEAR_PLAN' }
  | {
      readonly type: 'SET_PLAN_SCALE';
      readonly metresPerPixel: number;
      /** As tipologias traçadas nesta prancha — só elas reescalam. Ausente: todas. */
      readonly unitTypeIds?: readonly UnitTypeId[];
    }
  | { readonly type: 'ADD_TOWER'; readonly name?: string }
  | { readonly type: 'RENAME_TOWER'; readonly towerId: string; readonly name: string }
  | { readonly type: 'REMOVE_TOWER'; readonly towerId: string }
  | { readonly type: 'ADD_FLOORS'; readonly towerId: string; readonly spec: FloorPlanSpec }
  | { readonly type: 'TOGGLE_FLOOR_UNITS'; readonly towerId: string; readonly floorId: string }
  | { readonly type: 'REMOVE_FLOOR'; readonly towerId: string; readonly floorId: string }
  | {
      readonly type: 'ADD_TYPOLOGY';
      readonly name?: string;
      readonly kind?: UnitTypeKind;
      /** Em que pavimento ela nasce. Ausente = pavimento tipo, o caso comum. */
      readonly floorKind?: FloorPlanKind;
    }
  | {
      /**
       * O rascunho ganha um lugar para cada tipologia da obra.
       *
       * Existe porque a bancada tem **duas** listas de tipologia, e elas precisavam se
       * encontrar: a do reducer, que é onde a geometria mora e é o que o `localStorage`
       * guarda, e a montada em `montarWorkspace`, que é o que a tela mostra — a
       * identidade vem do servidor e a geometria é procurada por `unitTypeId`.
       *
       * Sem esta ação a segunda nunca achava a primeira. A bancada abre com uma tipologia
       * de rascunho de id sorteado, que não é o de tipologia nenhuma da obra; todo
       * ambiente e toda parede iam parar nela, e a busca por `unitTypeId` não os
       * encontrava. O ambiente era criado e não aparecia — sem erro, porque erro não
       * houve: ele foi guardado num lugar que a tela não olha.
       */
      readonly type: 'SYNC_TYPOLOGIES';
      readonly unitTypes: readonly {
        readonly unitTypeId: UnitTypeId;
        readonly name: string;
        readonly kind: UnitTypeKind;
        readonly floorKind: FloorPlanKind;
      }[];
    }
  | {
      /**
       * O desenho que veio do servidor entra na bancada (Fase 14, fatia 3).
       *
       * Substitui a geometria inteira — ambientes, paredes, vãos, pilares, vigas e os
       * rótulos —, e **só** ela: a prancha na mesa tem caminho próprio (Fase 24), e o
       * catálogo importado e a divergência do apto continuam no navegador porque ainda não
       * têm tabela — sobrescrevê-los aqui apagaria trabalho que o servidor nem sabe que
       * existe.
       *
       * Quem monta o envelope é `geometriaParaRascunho`; esta ação só o assenta.
       */
      readonly type: 'HYDRATE_GEOMETRY';
      readonly workspace: WorkspaceDraft;
    }
  | {
      /**
       * O cadastro de serviços que veio do servidor entra na bancada (Fase 14, fatia 4).
       *
       * Substitui o catálogo inteiro, e quem o monta é `catalogoDoServidor` — que faz um
       * **merge**, e não uma troca: o servidor manda na medida e na espessura, e os itens
       * ficam os do rascunho. Desde a Fase 24 isso só acontece quando os itens ainda não
       * subiram — a migração única — ou a leitura deles falhou; o caminho comum é
       * `HYDRATE_ITEMS`.
       */
      readonly type: 'HYDRATE_CATALOG';
      readonly catalog: ServiceCatalog;
    }
  | {
      /**
       * Os itens do serviço que vieram do servidor entram na bancada (Fase 24, fatia 3).
       *
       * Troca o catálogo — já com os itens de lá — e, em cada tipologia, a aba de
       * referência e o que foi digitado. **O servidor manda**: a tipologia que ele não
       * lista não decidiu nada, e fica sem decisão aqui também.
       *
       * Traz a lista inteira das tipologias da obra, com nome e tipo, e não só as que
       * decidiram: a que ainda não tem lugar no rascunho ganha um aqui. Sem isso, a
       * decisão de uma tipologia que o rascunho não conhecia se perderia na hidratação —
       * e a gravação seguinte a apagaria do servidor, por ausência.
       */
      readonly type: 'HYDRATE_ITEMS';
      readonly catalog: ServiceCatalog;
      readonly typologies: readonly {
        readonly unitTypeId: UnitTypeId;
        readonly name: string;
        readonly kind: UnitTypeKind;
        readonly sheetName?: string;
        readonly itemQuantities: Readonly<Record<string, number>>;
        /** O motivo de cada digitado que discorda do calculado (fatia 6). Ausente = nenhum. */
        readonly itemReasons?: Readonly<Record<string, string>>;
        /** O número de cada serviço, pela chave dele (fatia 5). Ausente = nenhum. */
        readonly serviceQuantities?: Readonly<Record<string, ServiceQuantityDecision>>;
        /**
         * As quantidades prontas das peças importadas (Fase 28, RN-390). Ausente = o servidor
         * não tem peça desta tipologia, e ela fica sem o campo — fora da gravação das peças.
         */
        readonly pieceQuantities?: QuantidadesPorPeca;
        /** A lembrança das peças renomeadas (RN-458). Ausente = nenhuma mudou de nome. */
        readonly pieceNameAliases?: Readonly<Record<string, string>>;
      }[];
    }
  | { readonly type: 'SELECT_TYPOLOGY'; readonly unitTypeId: UnitTypeId }
  | { readonly type: 'RENAME_TYPOLOGY'; readonly unitTypeId: UnitTypeId; readonly name: string }
  | {
      readonly type: 'SET_TYPOLOGY_KIND';
      readonly unitTypeId: UnitTypeId;
      readonly kind: UnitTypeKind;
    }
  | {
      /** Muda a tipologia de pavimento — do tipo para o garden, por exemplo. */
      readonly type: 'SET_TYPOLOGY_FLOOR';
      readonly unitTypeId: UnitTypeId;
      readonly floorKind: FloorPlanKind;
    }
  | { readonly type: 'REMOVE_TYPOLOGY'; readonly unitTypeId: UnitTypeId }
  | {
      readonly type: 'SET_TYPOLOGY_SHEET';
      readonly unitTypeId: UnitTypeId;
      /** Vazio desfaz o vínculo — a tipologia volta a não ter orçamento de referência. */
      readonly sheetName: string;
    }
  | { readonly type: 'IMPORT_CATALOG'; readonly catalog: ServiceCatalog }
  | { readonly type: 'CLEAR_CATALOG' }
  | {
      readonly type: 'SET_SERVICE_MEASURE';
      readonly serviceKey: string;
      readonly measureBy: MeasureBy;
      /**
       * A peça, quando a tela a escolheu junto (Fase 24). Ausente mantém a do serviço
       * enquanto ela casar com a medida — a mesma regra do cadastro (`resolveAppliesTo`).
       */
      readonly appliesTo?: AppliesTo;
    }
  | {
      readonly type: 'SET_SERVICE_THICKNESS';
      readonly serviceKey: string;
      /** `undefined` é "não tem espessura", que não é o mesmo que zero. */
      readonly layerThickness?: number;
    }
  | {
      /**
       * A margem da soleira e do peitoril, em metros para cada lado (RN-308). Do cadastro, como
       * a espessura: vale para todas as tipologias. Ausente ou zero desfaz; negativa é recusada.
       */
      readonly type: 'SET_SERVICE_SIDE_MARGIN';
      readonly serviceKey: string;
      readonly sideMargin?: number;
    }
  | {
      /**
       * O índice de consumo do item (Fase 24, fatia 6): quanto dele por unidade do medido
       * do serviço. É do cadastro — vale para todas as tipologias (D7). Ausente desfaz.
       */
      readonly type: 'SET_ITEM_INDEX';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly consumptionIndex?: number;
    }
  | {
      /**
       * A média do apartamento chega ao índice do item (Fase 40, RN-520 a RN-522). `follow`: o
       * índice passa a ser a média, com a origem MEDIA — o "Usar a média", o item que a média
       * trouxe e o "Atualizar" do que segue a média. Sem `follow`, só a foto muda: o digitado
       * fica, com a média nova ao lado (D8).
       */
      readonly type: 'APPLY_INDEX_AVERAGE';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly average: IndexAverage;
      readonly follow: boolean;
    }
  | {
      /** A perda do item, em por cento (D8). Ausente desfaz; a mão de obra não tem. */
      readonly type: 'SET_ITEM_LOSS';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly lossPercent?: number;
    }
  | {
      /**
       * As taxas de aço da disciplina, em kg/m³ por bitola (Fase 27, RN-367). Do cadastro da
       * obra, como o índice: valem para todas as tipologias, e os três serviços de ferragem
       * leem o mesmo número (ADR-176). A lista inteira, e não uma bitola: é a da disciplina
       * que se troca. Vazia é "sem taxa" — e é ela que apaga as do servidor.
       */
      readonly type: 'SET_STEEL_RATES';
      readonly disciplineKey: string;
      readonly rates: readonly SteelRate[];
      /** A lista do elemento, na disciplina que mistura (Fase 42, RN-573). Ausente: a inteira. */
      readonly element?: AppliesTo;
    }
  | {
      /**
       * A média do aço da laje chega à taxa de uma bitola (Fase 40, etapa 3 — RN-533, RN-534).
       * `follow`: a taxa passa a ser a média, com a origem MEDIA — a bitola que a disciplina não
       * tinha e o "Atualizar" da que segue a média. Sem `follow`, só a foto muda: a digitada
       * fica, com a média nova ao lado (D8).
       */
      readonly type: 'APPLY_STEEL_AVERAGE';
      readonly disciplineKey: string;
      readonly gauge: SteelRate['gauge'];
      readonly average: SteelRateAverage;
      readonly follow: boolean;
      /** A lista do elemento, na disciplina que mistura (Fase 42, RN-573). Ausente: a inteira. */
      readonly element?: AppliesTo;
    }
  | {
      /**
       * O medido do serviço nesta tipologia (Fase 24, fatia 5).
       *
       * No serviço que o desenho mede é **correção**, e sem motivo não entra (D4); no que
       * ele não mede é a quantidade, e o motivo não é pedido (D1). Quem decide qual dos dois
       * é o serviço do catálogo, e não quem despacha — por isso a ação é da bancada.
       */
      readonly type: 'SET_SERVICE_MEASURED';
      readonly unitTypeId: UnitTypeId;
      readonly serviceKey: string;
      readonly measured: number;
      readonly reason?: string;
    }
  | {
      /** Volta ao traçado: a correção sai, e o número do desenho volta a valer. */
      readonly type: 'CLEAR_SERVICE_MEASURED';
      readonly unitTypeId: UnitTypeId;
      readonly serviceKey: string;
    }
  | {
      /**
       * A quantidade na unidade do serviço, quando ela diverge da medida (D5). Ausente
       * desfaz a decisão.
       */
      readonly type: 'SET_SERVICE_QUANTITY';
      readonly unitTypeId: UnitTypeId;
      readonly serviceKey: string;
      readonly quantity?: number;
    }
  | {
      /** Acrescenta um material ao serviço — o que a planilha não trouxe e a obra usa. */
      readonly type: 'ADD_SERVICE_ITEM';
      readonly serviceKey: string;
      readonly description: string;
      readonly code?: string;
      readonly unit?: string;
      /**
       * A classe, quando quem acrescenta já a escolheu.
       *
       * Escolhida na hora é **decisão**, e não palpite: quem digita um material novo sabe
       * se aquilo é ferramenta ou hora de gente. Ausente cai no palpite pelo lugar e pelo
       * nome, que é o caminho de quem só quer o item na lista.
       */
      readonly kind?: ItemKind;
      /**
       * A função que executa — só na mão de obra, escolhida da lista das que executam a
       * disciplina do serviço (RN-252). É ela, e não o texto, que diz quem é.
       */
      readonly jobRoleId?: JobRoleId;
      /**
       * A bitola, quando quem acrescenta já a sabe — o vergalhão que vem da obra de referência
       * (Fase 39, RN-505). Só vale no serviço que compra aço (RN-366); ausente, ela é lida da
       * descrição, como sempre.
       */
      readonly steelGauge?: SteelGauge;
      /**
       * Quanto desta tipologia usa, dito já no cadastro.
       *
       * Vem junto porque acrescentar um item e dizer quanto dele é **um** ato: quem
       * digita "argamassa" sabe quantos metros quadrados dela vão ali, e obrigar a
       * cadastrar primeiro e procurar a linha na tabela depois separava a decisão da
       * digitação.
       *
       * **Ausente continua significando "segue a planilha"**, que não é zero: o item sem
       * quantidade lançada herda o que a aba importada disser, enquanto o item zerado foi
       * zerado por alguém (RN-171). Por isso o campo é opcional, e não um zero de partida.
       *
       * Mora na **tipologia**, e não no catálogo, como toda quantidade: o catálogo diz
       * quais itens o serviço usa; a tipologia diz quanto (RN-167).
       */
      readonly quantity?: number;
    }
  | { readonly type: 'REMOVE_SERVICE_ITEM'; readonly serviceKey: string; readonly itemKey: string }
  | {
      /**
       * Tira a disciplina inteira do cadastro — com os serviços dela.
       *
       * O que foi lançado nos itens desses serviços sai junto: número pendurado em
       * serviço que não existe mais ressuscitaria com cara de decisão no dia em que
       * alguém recadastrasse o mesmo nome.
       */
      readonly type: 'REMOVE_DISCIPLINE';
      readonly disciplineKey: string;
    }
  | { readonly type: 'REMOVE_SERVICE'; readonly serviceKey: string }
  | {
      /**
       * O apto diverge da tipologia num item (RN-167).
       *
       * O **motivo é obrigatório** (RN-168): sem ele, seis meses depois ninguém sabe por
       * que aquele apto pede quarenta tijolos a mais, e a divergência vira ruído que
       * alguém acaba apagando por não entender.
       */
      readonly type: 'SET_UNIT_ITEM';
      readonly unitKey: string;
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly effect: OverrideEffect;
      /** Presente quando `REPLACE`. */
      readonly quantity?: number;
      readonly reason: string;
      /** Quem decidiu — o terceiro do trio que a RN-168 exige, ao lado da data e do motivo. */
      readonly by: UserId;
      readonly byName: string;
    }
  | {
      /** Devolve o item ao que a tipologia diz — a divergência some. */
      readonly type: 'CLEAR_UNIT_ITEM';
      readonly unitKey: string;
      readonly serviceKey: string;
      readonly itemKey: string;
    }
  | {
      /**
       * Cadastra uma disciplina na mão.
       *
       * O catálogo deixou de ser "o que a planilha trouxe" e passou a ser o **cadastro de
       * serviços da obra**. Quem não tem arquivo começa por aqui, e o quantitativo sai
       * igual — a planilha vira atalho para preencher, não condição para existir.
       */
      readonly type: 'ADD_DISCIPLINE';
      readonly name: string;
    }
  | {
      /** Cadastra um serviço dentro da disciplina (RN-164: o par é único). */
      readonly type: 'ADD_SERVICE';
      readonly disciplineKey: string;
      readonly name: string;
      readonly unit?: string;
    }
  | {
      /**
       * A classe do item — material, insumo, ferramenta, equipamento ou mão de obra.
       *
       * Escolher aqui é **decidir**: daí em diante reimportar a planilha não mexe mais
       * nesta linha, e o palpite do classificador para de valer para ela.
       */
      readonly type: 'SET_ITEM_KIND';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly kind: ItemKind;
    }
  | {
      /**
       * A função do item de mão de obra (RN-252). Ausente desfaz a escolha.
       *
       * É o caminho da mão de obra que veio da planilha como texto — "PEDREIRO", "M.O.
       * ALVENARIA" —: o item continua sendo o mesmo, e passa a dizer **qual** função.
       */
      readonly type: 'SET_ITEM_JOB_ROLE';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly jobRoleId?: JobRoleId;
    }
  | {
      /**
       * O código de compras de um item que já existe (Fase 29, RN-395). Ausente, ou vazio,
       * tira o código.
       *
       * O item continua o mesmo: a chave dele é a descrição (RN-251), e a quantidade de cada
       * tipologia, o motivo, a peça importada e a divergência do apto seguem penduradas nela.
       * O que muda é a identidade do **material** na consolidação, que une pelo código
       * (RN-011). Mão de obra não tem código (RN-252), e é recusada.
       */
      readonly type: 'SET_ITEM_CODE';
      readonly serviceKey: string;
      readonly itemKey: string;
      readonly code?: string;
      /**
       * Os itens de mesma descrição, sem código, que recebem o mesmo (RN-396) — os que a tela
       * listou e a pessoa aceitou com "Aplicar a todos".
       *
       * Uma ação só, e não uma por item: é **um** ato de quem decidiu, e chega à gravação
       * inteiro. E o redutor confere cada um de novo contra o catálogo de agora
       * (`itensParaPropagarCodigo`): quem já tem código não é tocado, nem que venha na lista.
       */
      readonly alsoTo?: readonly { readonly serviceKey: string; readonly itemKey: string }[];
    };

export type WorkspaceAction = BenchAction | DraftAction;

/** Quantos aptos desta tipologia divergem dela. */
export function divergentUnits(
  workspace: WorkspaceDraft,
  typology: TypologyDraft,
): readonly UnitDraft[] {
  return unitsOf(workspace).filter(
    (unit) =>
      unit.typology.unitTypeId === typology.unitTypeId &&
      shadowOf(workspace, unit.key) !== undefined,
  );
}

/**
 * Cada ação da bancada, uma vez.
 *
 * É um `Record` sobre a união e não uma lista de texto, e a diferença é o compilador:
 * esquecer um nome numa lista solta **não** dá erro de tipo — a ação desce para o
 * `draftReducer`, que não a conhece, e o que volta no lugar da tipologia ativa é
 * `undefined`. A bancada fica com um buraco na lista e o estouro acontece três telas
 * depois, em quem for ler `typology.unitTypeId`.
 *
 * Aconteceu com `HYDRATE_GEOMETRY`, que ficou de fora e só apareceu quando um servidor com
 * desenho de verdade respondeu pela primeira vez. Com o `Record`, a chave que falta é erro
 * de compilação — e a de sobra também.
 */
const ACOES_DA_BANCADA: Readonly<Record<BenchAction['type'], true>> = {
  DISMISS_ERROR: true,
  HYDRATE_GEOMETRY: true,
  HYDRATE_CATALOG: true,
  HYDRATE_ITEMS: true,
  SET_PLAN: true,
  CLEAR_PLAN: true,
  SET_PLAN_SCALE: true,
  ADD_TOWER: true,
  RENAME_TOWER: true,
  REMOVE_TOWER: true,
  ADD_FLOORS: true,
  TOGGLE_FLOOR_UNITS: true,
  REMOVE_FLOOR: true,
  ADD_TYPOLOGY: true,
  SYNC_TYPOLOGIES: true,
  SELECT_TYPOLOGY: true,
  RENAME_TYPOLOGY: true,
  SET_TYPOLOGY_KIND: true,
  SET_TYPOLOGY_FLOOR: true,
  REMOVE_TYPOLOGY: true,
  SET_TYPOLOGY_SHEET: true,
  IMPORT_CATALOG: true,
  SET_PILE_TYPES: true,
  ADD_PILE_CAP_TYPE: true,
  RENAME_PILE_CAP_TYPE: true,
  REMOVE_PILE_CAP_TYPE: true,
  SET_PILE_CAP_TYPE_RATE: true,
  SET_PILE_CAP_TYPE_STEEL_RATES: true,
  ADD_ELEMENT_TYPE: true,
  RENAME_ELEMENT_TYPE: true,
  REMOVE_ELEMENT_TYPE: true,
  SET_ELEMENT_TYPE_RATE: true,
  SET_ELEMENT_TYPE_OF: true,
  SET_ELEMENT_FACTOR: true,
  SET_ELEMENT_STEEL_RATES: true,
  SET_ELEMENT_TYPE_STEEL_RATES: true,
  APPLY_PILE_CAP_TYPE_AVERAGE: true,
  APPLY_ELEMENT_TYPE_AVERAGE: true,
  IMPORT_PILES: true,
  IMPORT_PIECE_QUANTITIES: true,
  RENAME_IMPORTED_PIECE: true,
  CLEAR_CATALOG: true,
  SET_SERVICE_MEASURE: true,
  SET_SERVICE_THICKNESS: true,
  SET_SERVICE_SIDE_MARGIN: true,
  SET_SERVICE_MEASURED: true,
  CLEAR_SERVICE_MEASURED: true,
  SET_ITEM_INDEX: true,
  APPLY_INDEX_AVERAGE: true,
  SET_ITEM_LOSS: true,
  SET_STEEL_RATES: true,
  APPLY_STEEL_AVERAGE: true,
  SET_SERVICE_QUANTITY: true,
  ADD_SERVICE_ITEM: true,
  REMOVE_SERVICE_ITEM: true,
  SET_ITEM_KIND: true,
  SET_ITEM_JOB_ROLE: true,
  SET_ITEM_CODE: true,
  ADD_DISCIPLINE: true,
  ADD_SERVICE: true,
  REMOVE_DISCIPLINE: true,
  REMOVE_SERVICE: true,
  SET_UNIT_ITEM: true,
  CLEAR_UNIT_ITEM: true,
};

const BENCH_ACTIONS: ReadonlySet<string> = new Set(Object.keys(ACOES_DA_BANCADA));

function isBenchAction(action: WorkspaceAction): action is BenchAction {
  return BENCH_ACTIONS.has(action.type);
}

export function emptyWorkspace(): WorkspaceDraft {
  const primeira = emptyDraft();
  return { towers: [], typologies: [primeira], activeId: primeira.unitTypeId };
}

/** `"Torre B"` — o próximo nome livre. */
function nextTowerName(towers: readonly TowerDraft[]): string {
  const usados = new Set(towers.map((tower) => tower.name));
  for (let indice = 0; indice < 26; indice += 1) {
    const nome = `Torre ${String.fromCharCode(65 + indice)}`;
    if (!usados.has(nome)) return nome;
  }
  return `Torre ${String(towers.length + 1)}`;
}

/**
 * Reescala o que veio da prancha, quando a escala dela muda.
 *
 * Só o que é **derivado de pixel**: contorno, o retângulo que o envolve e as paredes dos
 * ambientes traçados, que nasceram do comprimento dos lados do polígono. Ambiente digitado
 * à mão fica intacto — aquelas medidas foram lidas na cota, não contadas em pixel, e
 * mexer nelas seria corrigir o que estava certo.
 *
 * As correções à mão (`areaOverride`, `perimeterOverride`) também ficam: são decisão de
 * gente sobre a obra, e não leitura da prancha.
 *
 * **O que pendura na parede vai junto**, senão a parede cresce e a peça fica: o vão e o
 * trecho de pilar e viga guardam a posição em metros ao longo dela (`offset`) e, o trecho,
 * para o lado (`lateral`). Só tem posição o que foi posto pela prancha — o cartão não a
 * grava —, e é por ela que o traçado se separa do digitado:
 *
 * - **o vão** leva só a posição. Largura e altura foram digitadas no painel antes do
 *   clique: são medida de projeto, e a porta de 0,80 continua de 0,80;
 * - **o trecho** leva posição, lado e a corrida da viga deitada sobre um pedaço da parede;
 * - **a peça** com um trecho desses leva o que o retângulo traçado mediu — as duas faces
 *   do pilar; a largura e a corrida da viga. Não leva o que foi digitado junto do traço: a
 *   altura do pilar e a altura da seção da viga, que a planta não mostra (`ADD_TRACED_ELEMENT`).
 *
 * A divisa entra pelo mesmo critério. Ela nasceu de uma parede traçada e continua medida
 * em pixel — e o vão e a peça que foram com ela voltam para a tipologia se ela for desfeita,
 * onde o desenho os recoloca pela posição.
 *
 * **Só o que foi traçado nesta folha** (Fase 24): cada desenho de pavimento tem a prancha
 * dele, e recalibrar a da garagem não pode esticar o pavimento tipo, que foi traçado em
 * outra. `escopo` são as tipologias do desenho; ausente, vale a bancada inteira.
 */
function reescalarTracado(
  workspace: WorkspaceDraft,
  fator: number,
  escopo?: ReadonlySet<UnitTypeId>,
): WorkspaceDraft {
  const naFolha = (typology: TypologyDraft): boolean =>
    escopo === undefined || escopo.has(typology.unitTypeId);
  const tracados = new Set(
    workspace.typologies
      .filter(naFolha)
      .flatMap((typology) =>
        typology.rooms.filter((room) => room.outline !== undefined).map((room) => room.id),
      ),
  );
  // O pilar e a viga com contorno também foram traçados na prancha, mesmo sem ambiente ao lado.
  const comContorno = (typology: TypologyDraft): boolean =>
    naFolha(typology) && typology.elements.some((element) => element.outline !== undefined);
  if (tracados.size === 0 && !workspace.typologies.some(comContorno)) return workspace;

  const daPrancha = (wall: Wall): boolean =>
    tracados.has(wall.sideA.roomId) ||
    (wall.sideB !== undefined && tracados.has(wall.sideB.roomId));
  const reescaladas = new Set<WallId>(
    [...workspace.typologies.flatMap((typology) => typology.walls), ...partyWallsOf(workspace)]
      .filter(daPrancha)
      .map((wall) => wall.id),
  );

  const parede = (wall: Wall): Wall =>
    reescaladas.has(wall.id) ? { ...wall, length: wall.length * fator } : wall;
  const vao = (opening: Opening): Opening =>
    opening.offset !== undefined && reescaladas.has(opening.wallId)
      ? { ...opening, offset: opening.offset * fator }
      : opening;

  // O trecho de uma peça pode estar na tipologia e a peça na bancada: a viga que passou a
  // cruzar uma divisa deixa para trás os trechos das outras paredes.
  const posto = (segment: ElementSegment): boolean =>
    segment.offset !== undefined && reescaladas.has(segment.wallId);
  const trechos = [
    ...workspace.typologies.flatMap((typology) => typology.elementSegments),
    ...partySegmentsOf(workspace),
  ];
  const pecasTracadas = new Set(trechos.filter(posto).map((segment) => segment.elementId));

  const trecho = (segment: ElementSegment): ElementSegment =>
    posto(segment)
      ? {
          ...segment,
          offset: segment.offset! * fator,
          ...(segment.lateral === undefined ? {} : { lateral: segment.lateral * fator }),
          ...(segment.length === undefined ? {} : { length: segment.length * fator }),
        }
      : segment;
  const peca = (element: StructuralElement, folha = false): StructuralElement => {
    // O contorno é a posição da peça na planta, em metros: anda com a escala como o do
    // ambiente, e a seção que o acompanha com ele.
    const desenhada = element.outline !== undefined && folha;
    if (!pecasTracadas.has(element.id) && !desenhada) return element;
    const escalada: StructuralElement =
      element.kind === 'PILAR'
        ? {
            ...element,
            sectionWidth: element.sectionWidth * fator,
            sectionDepth: element.sectionDepth * fator,
          }
        : {
            ...element,
            sectionWidth: element.sectionWidth * fator,
            length: element.length * fator,
          };
    return element.outline === undefined
      ? escalada
      : {
          ...escalada,
          outline: element.outline.map((ponto) => ({ x: ponto.x * fator, y: ponto.y * fator })),
        };
  };

  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) => ({
      ...typology,
      rooms: typology.rooms.map((room) =>
        room.outline === undefined || !naFolha(typology)
          ? room
          : {
              ...room,
              outline: room.outline.map((ponto) => ({ x: ponto.x * fator, y: ponto.y * fator })),
              width: room.width * fator,
              length: room.length * fator,
            },
      ),
      walls: typology.walls.map(parede),
      openings: typology.openings.map(vao),
      elements: typology.elements.map((element) => peca(element, naFolha(typology))),
      elementSegments: typology.elementSegments.map(trecho),
      // O shaft é traço da prancha como o contorno: os cantos andam com a escala, senão o
      // buraco muda de tamanho sozinho e o piso do ambiente junto.
      ...(typology.shafts === undefined || !naFolha(typology)
        ? {}
        : {
            shafts: typology.shafts.map((shaft) => ({
              ...shaft,
              from: { x: shaft.from.x * fator, y: shaft.from.y * fator },
              to: { x: shaft.to.x * fator, y: shaft.to.y * fator },
            })),
          }),
    })),
    ...(workspace.partyWalls === undefined ? {} : { partyWalls: workspace.partyWalls.map(parede) }),
    ...(workspace.partyOpenings === undefined
      ? {}
      : { partyOpenings: workspace.partyOpenings.map(vao) }),
    ...(workspace.partyElements === undefined
      ? {}
      : { partyElements: workspace.partyElements.map((element) => peca(element)) }),
    ...(workspace.partyElementSegments === undefined
      ? {}
      : { partyElementSegments: workspace.partyElementSegments.map(trecho) }),
  };
}

/**
 * A bancada que chega com parede de antes do índice, cada uma passando a lembrar o lado
 * (RN-244).
 *
 * Roda na chegada do desenho — do servidor e do rascunho do navegador —, e a gravação
 * seguinte leva os lados ao banco: é "uma vez" porque dali em diante não há mais legado a
 * lembrar, e a medida deixa de decidir onde a parede está.
 *
 * O andar inteiro de uma vez, e não tipologia por tipologia: a divisa mora na bancada e tem
 * um lado em cada tipologia, e é ela que, posta primeiro pelo contorno do vizinho, impede a
 * parede de 3 m da esquerda da sala de tomar o lado de 3 m da direita.
 *
 * Devolve a **mesma** bancada quando não há nada a lembrar.
 */
export function lembrarOsLados(workspace: WorkspaceDraft): WorkspaceDraft {
  const todas = [
    ...workspace.typologies.flatMap((typology) => typology.walls),
    ...partyWallsOf(workspace),
  ];
  const lembradas = rememberWallEdges(
    workspace.typologies.flatMap((typology) => typology.rooms),
    todas,
  );
  if (lembradas === todas) return workspace;

  const porId = new Map(lembradas.map((wall) => [String(wall.id), wall]));
  const lembrada = (wall: Wall): Wall => porId.get(String(wall.id)) ?? wall;
  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) => ({
      ...typology,
      walls: typology.walls.map(lembrada),
    })),
    ...(workspace.partyWalls === undefined
      ? {}
      : { partyWalls: workspace.partyWalls.map(lembrada) }),
  };
}

/**
 * Quantos **pavimentos tipo** a obra tem — o multiplicador da tipologia que se repete.
 *
 * É por `kind`, e não por "tem apartamento": o térreo pode ter apartamento e não é
 * pavimento tipo, e é o pavimento tipo que a tipologia repete. Contar por apartamento
 * inflaria o quantitativo com andares que têm outro desenho.
 */
/**
 * Os dois são do mesmo desenho? Pelo id do desenho quando os dois o têm; senão, pelo tipo —
 * o rascunho sem servidor e a tipologia que ainda não tem posição continuam como antes.
 */
export function sameDrawing(
  a: { readonly floorKind: FloorPlanKind; readonly floorPlanId?: string | undefined },
  b: { readonly floorKind: FloorPlanKind; readonly floorPlanId?: string | undefined },
): boolean {
  if (a.floorPlanId !== undefined && b.floorPlanId !== undefined) {
    return a.floorPlanId === b.floorPlanId;
  }
  return a.floorKind === b.floorKind;
}

/** As tipologias de um desenho — o que compõe os andares que o usam. */
export function typologiesOfDrawing(
  workspace: WorkspaceDraft,
  ref: { readonly floorKind: FloorPlanKind; readonly floorPlanId?: string | undefined },
): readonly TypologyDraft[] {
  return workspace.typologies.filter((typology) => sameDrawing(typology, ref));
}

/** Quantos pavimentos usam este desenho — pelo id, quando há; senão, pelo tipo. */
export function floorsOfDrawing(
  workspace: WorkspaceDraft,
  ref: { readonly floorKind: FloorPlanKind; readonly floorPlanId?: string | undefined },
): number {
  return workspace.towers.reduce(
    (total, tower) =>
      total +
      tower.floors.filter((floor) =>
        sameDrawing({ floorKind: floor.kind, floorPlanId: floor.floorPlanId }, ref),
      ).length,
    0,
  );
}

export function floorsOfKind(workspace: WorkspaceDraft, kind: FloorPlanKind): number {
  return workspace.towers.reduce(
    (total, tower) => total + tower.floors.filter((floor) => floor.kind === kind).length,
    0,
  );
}

/**
 * Quantas vezes esta tipologia acontece — **dois** fatores, não um.
 *
 * Quantos pavimentos a repetem: o número dela quando ela tem um — a cobertura, que
 * acontece uma vez — e os pavimentos daquele tipo na estrutura quando não tem, que é o
 * caso comum. Nunca zero: obra sem o pavimento cadastrado ainda mostra o desenho uma vez,
 * e zero zeraria o total em silêncio.
 *
 * Vezes quantas cabem **em cada** pavimento, que era um fator invisível porque sempre
 * valia um. Duas garagens de quarenta vagas são oitenta, e o número continua saindo da
 * estrutura e do cadastro em vez de ser digitado numa terceira mão.
 */
export function floorsOf(workspace: WorkspaceDraft, typology: TypologyDraft): number {
  const pavimentos = typology.floors ?? Math.max(1, floorsOfDrawing(workspace, typology));
  return pavimentos * Math.max(1, typology.unitsPerFloor ?? 1);
}

/** As tipologias de um pavimento — o que compõe aquele andar. */
export function typologiesOfFloor(
  workspace: WorkspaceDraft,
  kind: FloorPlanKind,
): readonly TypologyDraft[] {
  return workspace.typologies.filter((typology) => typology.floorKind === kind);
}

/** Os tipos de pavimento que a estrutura tem, na ordem do prédio. */
export function floorKindsInUse(workspace: WorkspaceDraft): readonly FloorPlanKind[] {
  const presentes = new Set(
    workspace.towers.flatMap((tower) => tower.floors.map((floor) => floor.kind)),
  );
  for (const typology of workspace.typologies) presentes.add(typology.floorKind);
  return FLOOR_PLAN_KIND_ORDER.filter((kind) => presentes.has(kind));
}

/** Quantos pavimentos com apartamento a obra tem — o multiplicador real do quantitativo. */
export function floorsWithUnits(workspace: WorkspaceDraft): number {
  return workspace.towers.reduce(
    (total, tower) => total + tower.floors.filter((floor) => floor.hasUnits).length,
    0,
  );
}

/** A estrutura já existe? É o que libera o cadastro de ambiente (RN-179). */
export function hasStructure(workspace: WorkspaceDraft): boolean {
  return workspace.towers.some((tower) => tower.floors.length > 0);
}

function mapTower(
  workspace: WorkspaceDraft,
  towerId: string,
  muda: (tower: TowerDraft) => TowerDraft,
): WorkspaceDraft {
  return {
    ...workspace,
    towers: workspace.towers.map((tower) => (tower.id === towerId ? muda(tower) : tower)),
  };
}

/**
 * Tira do rascunho o que foi lançado nos itens destes serviços.
 *
 * A quantidade mora na **tipologia**, por `serviceKey»itemKey`, e o serviço mora no
 * catálogo: apagar um sem o outro deixa número pendurado em nada. Ele não aparece em
 * lugar nenhum enquanto isso — e volta com aparência de decisão no dia em que alguém
 * recadastrar o mesmo serviço.
 *
 * A linha-sombra do apto sai pela mesma razão e pela mesma chave: a chave do serviço é
 * normalizada e determinística (RN-164), então recadastrar "Alvenaria › Assentamento"
 * produz a **mesma** chave — e a divergência do 1204 ressuscitaria com o motivo de seis
 * meses atrás, com cara de decisão de hoje.
 */
function semOsLancamentos(
  workspace: WorkspaceDraft,
  serviceKeys: readonly string[],
): WorkspaceDraft {
  const prefixos = serviceKeys.map((chave) => `${chave}»`);
  if (prefixos.length === 0) return workspace;

  const doServicoApagado = (chave: string): boolean =>
    prefixos.some((prefixo) => chave.startsWith(prefixo));

  return {
    ...semAsSombras(workspace, doServicoApagado),
    typologies: workspace.typologies.map((typology) => {
      // A quantidade pronta das peças importadas sai junto (Fase 28), pela mesma razão.
      const semServico = semAsPecasDe(
        semNumeroDosServicos(typology, serviceKeys),
        doServicoApagado,
        (chave) => serviceKeys.includes(chave),
      );
      if (semServico.itemQuantities === undefined) return semServico;
      const restantes = Object.fromEntries(
        Object.entries(semServico.itemQuantities).filter(([chave]) => !doServicoApagado(chave)),
      );
      return { ...semServico, itemQuantities: restantes };
    }),
  };
}

/**
 * Tira das peças importadas da tipologia os itens e os serviços que saíram do catálogo
 * (Fase 28).
 *
 * O campo fica, mesmo vazio: a tipologia continua descrevendo as peças dela, e a gravação
 * seguinte apaga do servidor o que saiu daqui (RN-390). Sem nada a tirar, devolve a mesma
 * tipologia.
 */
function semAsPecasDe(
  typology: TypologyDraft,
  itemSaiu: (itemQuantityKey: string) => boolean,
  servicoSaiu: (serviceKey: string) => boolean,
): TypologyDraft {
  const atual = typology.pieceQuantities;
  if (atual === undefined) return typology;
  const itens = Object.keys(atual.itens).filter(itemSaiu);
  const servicos = Object.keys(atual.servicos).filter(servicoSaiu);
  if (itens.length === 0 && servicos.length === 0) return typology;
  return {
    ...typology,
    pieceQuantities: {
      itens: Object.fromEntries(Object.entries(atual.itens).filter(([chave]) => !itemSaiu(chave))),
      servicos: Object.fromEntries(
        Object.entries(atual.servicos).filter(([chave]) => !servicoSaiu(chave)),
      ),
    },
  };
}

/**
 * Tira da tipologia o número que ela decidiu para estes serviços (Fase 24, fatia 5).
 *
 * Pelo mesmo motivo do item: número pendurado em serviço que saiu ressuscitaria com cara de
 * decisão no dia em que alguém recadastrasse o mesmo nome.
 */
function semNumeroDosServicos(
  typology: TypologyDraft,
  serviceKeys: readonly string[],
): TypologyDraft {
  const fora = new Set(serviceKeys);
  const prefixos = serviceKeys.map((chave) => itemQuantityKey(chave, ''));
  return {
    ...typology,
    ...(typology.serviceQuantities === undefined
      ? {}
      : {
          serviceQuantities: Object.fromEntries(
            Object.entries(typology.serviceQuantities).filter(([chave]) => !fora.has(chave)),
          ),
        }),
    // O motivo de discordar do calculado sai com o item do serviço (fatia 6).
    ...(typology.itemReasons === undefined
      ? {}
      : {
          itemReasons: Object.fromEntries(
            Object.entries(typology.itemReasons).filter(
              ([chave]) => !prefixos.some((prefixo) => chave.startsWith(prefixo)),
            ),
          ),
        }),
  };
}

/**
 * A decisão de um serviço, trocada numa tipologia — e apagada quando fica sem número.
 *
 * Decisão sem medido nem quantidade é a ausência escrita por extenso, e o banco a recusa
 * (`0040`): "segue o traçado" é não ter registro.
 */
function comDecisaoDoServico(
  typology: TypologyDraft,
  serviceKey: string,
  muda: (atual: ServiceQuantityDecision) => ServiceQuantityDecision,
): TypologyDraft {
  const { [serviceKey]: atual, ...outras } = typology.serviceQuantities ?? {};
  const nova = muda(atual ?? {});
  const vazia = nova.measured === undefined && nova.quantity === undefined;
  return { ...typology, serviceQuantities: vazia ? outras : { ...outras, [serviceKey]: nova } };
}

/**
 * Tira das sombras as linhas que `apagada` reconhece, e as sombras que sobram vazias.
 *
 * Sombra sem linha nenhuma é ausência escrita por extenso: a leitura entende ausência
 * como "este apto segue a tipologia", e um objeto vazio diria a mesma coisa ocupando
 * espaço e enganando quem contar `Object.keys`.
 */
function semAsSombras(
  workspace: WorkspaceDraft,
  apagada: (chave: string) => boolean,
): WorkspaceDraft {
  if (workspace.unitShadows === undefined) return workspace;

  const restantes: Record<string, UnitShadow> = {};
  for (const [unitKey, sombra] of Object.entries(workspace.unitShadows)) {
    const linhas = Object.fromEntries(
      Object.entries(sombra.lines).filter(([chave]) => !apagada(chave)),
    );
    if (Object.keys(linhas).length > 0) restantes[unitKey] = { lines: linhas };
  }

  return { ...workspace, unitShadows: restantes };
}

/**
 * Tira as sombras de aptos que a estrutura não produz mais.
 *
 * O apto é derivado — pavimento × tipologia daquele pavimento (RN-177) —, então remover a
 * torre, o pavimento ou a tipologia, ou mudar a tipologia de pavimento, faz a chave
 * deixar de existir. A sombra órfã não aparece em lugar nenhum e não entra na soma; ela
 * espera. No dia em que o mesmo pavimento voltar, ela volta junto, com o motivo antigo e
 * cara de decisão de hoje — o mesmo defeito que `semOsLancamentos` evita do outro lado.
 */
function semAsSombrasOrfas(workspace: WorkspaceDraft): WorkspaceDraft {
  if (workspace.unitShadows === undefined) return workspace;

  const existentes = new Set(unitsOf(workspace).map((unit) => unit.key));
  const restantes = Object.fromEntries(
    Object.entries(workspace.unitShadows).filter(([unitKey]) => existentes.has(unitKey)),
  );

  return { ...workspace, unitShadows: restantes };
}

/**
 * A bitola que o item ganha (Fase 27, RN-366): só no CORTE E DOBRA DE FERRAGEM, só fora da mão
 * de obra, e só quando a descrição a escreve com a classe ("CA 50 - 10 MM"). É a mesma
 * leitura da planilha numerada (`buildCatalog`), para o item acrescentado na tela.
 */
function bitolaDoItem(
  serviceName: string,
  kind: ItemKind,
  description: string,
): { readonly steelGauge?: SteelGauge } {
  if (kind === 'MAO_DE_OBRA' || !isSteelPurchaseService(serviceName)) return {};
  const bitola = parseSteelGauge(description);
  return bitola === undefined ? {} : { steelGauge: bitola };
}

/** Um serviço do catálogo, trocado no lugar — o resto do catálogo fica intacto. */
function mapCatalogService(
  workspace: WorkspaceDraft,
  serviceKey: string,
  muda: (servico: CatalogService) => CatalogService,
): WorkspaceDraft {
  if (workspace.catalog === undefined) return workspace;

  return {
    ...workspace,
    catalog: {
      ...workspace.catalog,
      disciplines: workspace.catalog.disciplines.map((disciplina) => ({
        ...disciplina,
        services: disciplina.services.map((servico) =>
          servico.key === serviceKey ? muda(servico) : servico,
        ),
      })),
    },
  };
}

/** O serviço do catálogo pela chave — `undefined` quando o catálogo não o tem. */
function servicoDoCatalogo(
  workspace: WorkspaceDraft,
  serviceKey: string,
): CatalogService | undefined {
  return workspace.catalog?.disciplines
    .flatMap((disciplina) => disciplina.services)
    .find((servico) => servico.key === serviceKey);
}

/** Uma tipologia trocada no lugar, pelo id — as outras ficam intactas. */
function naTipologia(
  workspace: WorkspaceDraft,
  unitTypeId: UnitTypeId,
  muda: (typology: TypologyDraft) => TypologyDraft,
): WorkspaceDraft {
  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) =>
      typology.unitTypeId === unitTypeId ? muda(typology) : typology,
    ),
  };
}

/** A tipologia na mesa. Nunca é `undefined`: a bancada mantém sempre uma. */
export function activeTypology(workspace: WorkspaceDraft): TypologyDraft {
  return (
    workspace.typologies.find((typology) => typology.unitTypeId === workspace.activeId) ??
    workspace.typologies[0]!
  );
}

/** `"Tipologia B"`, `"Tipologia C"` — o próximo nome livre, para não nascer tudo "Nova". */
function nextTypologyName(typologies: readonly TypologyDraft[]): string {
  const usados = new Set(typologies.map((typology) => typology.name));
  for (let indice = 0; indice < 26; indice += 1) {
    const nome = `Tipologia ${String.fromCharCode(65 + indice)}`;
    if (!usados.has(nome)) return nome;
  }
  return `Tipologia ${String(typologies.length + 1)}`;
}

/**
 * Casa duas paredes de tipologias diferentes numa divisa do pavimento.
 *
 * As duas **saem** das suas tipologias e viram uma só, com um lado em cada. O
 * `positionId` de cada lado é o que faz a alvenaria se repartir sozinha depois: é dele
 * que `splitShares` tira meio a meio entre aptos, e inteira do apto quando o vizinho é
 * área comum (RN-163, RN-178).
 */
function casarEntreTipologias(workspace: WorkspaceDraft, keep: Wall, drop: Wall): WorkspaceDraft {
  const daKeep = typologyOfRoom(workspace, keep.sideA.roomId);
  const daDrop = typologyOfRoom(workspace, drop.sideA.roomId);
  if (daKeep === undefined || daDrop === undefined) return workspace;

  const divisa: Wall = {
    ...keep,
    owner: { kind: 'FLOOR_PLAN', floorPlanId: asId<'FloorPlan'>('rascunho-pavimento') },
    sideA: { ...keep.sideA, positionId: positionOfTypology(daKeep.unitTypeId) },
    sideB: { ...drop.sideA, positionId: positionOfTypology(daDrop.unitTypeId) },
    updatedAt: new Date().toISOString(),
  };

  const rotulo = (typology: TypologyDraft, wall: Wall): string =>
    typology.labels[wall.id]?.custom ?? typology.labels[wall.id]?.a ?? 'Parede';

  // Os vãos das duas seguem a parede: a porta continua existindo, e agora desconta uma
  // vez só — nas duas faces, como a obra faz.
  const vaos = [
    ...daKeep.openings.filter((opening) => opening.wallId === keep.id),
    ...daDrop.openings.filter((opening) => opening.wallId === drop.id),
  ].map((opening) => ({ ...opening, wallId: divisa.id }));

  // O pilar e a viga das duas paredes também passam para a divisa. Cada elemento vem com
  // os trechos que estavam naquela parede — os que ele tem em outras ficam onde estão,
  // porque a viga que atravessa continua sendo a mesma em todas elas.
  const daEstrutura = (typology: TypologyDraft, wallId: WallId) => {
    const trechos = typology.elementSegments.filter((segment) => segment.wallId === wallId);
    const ids = new Set(trechos.map((segment) => segment.elementId));
    return {
      elements: typology.elements.filter((element) => ids.has(element.id)),
      segments: trechos.map((segment) => ({ ...segment, wallId: divisa.id })),
    };
  };
  const estruturaA = daEstrutura(daKeep, keep.id);
  const estruturaB = daEstrutura(daDrop, drop.id);

  const semAParede = (typology: TypologyDraft, wallId: WallId): TypologyDraft => {
    const { [wallId]: _fora, ...labels } = typology.labels;
    return {
      ...typology,
      walls: typology.walls.filter((wall) => wall.id !== wallId),
      openings: typology.openings.filter((opening) => opening.wallId !== wallId),
      // A estrutura segue a parede para a bancada, como os vãos: o pilar não deixa de
      // existir porque a parede dele passou a separar dois aptos. O rateio do concreto é
      // o mesmo da alvenaria (RN-163), e quem o faz é a conta, não este corte.
      ...semAsParedes(typology, new Set([wallId])),
      labels,
    };
  };

  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) => {
      if (typology.unitTypeId === daKeep.unitTypeId) return semAParede(typology, keep.id);
      if (typology.unitTypeId === daDrop.unitTypeId) return semAParede(typology, drop.id);
      return typology;
    }),
    partyWalls: [...partyWallsOf(workspace), divisa],
    partyOpenings: [...(workspace.partyOpenings ?? []), ...vaos],
    partyElements: [...partyElementsOf(workspace), ...estruturaA.elements, ...estruturaB.elements],
    partyElementSegments: [
      ...partySegmentsOf(workspace),
      ...estruturaA.segments,
      ...estruturaB.segments,
    ],
    partyLabels: {
      ...(workspace.partyLabels ?? {}),
      [divisa.id]: {
        a: `${daKeep.name} · ${rotulo(daKeep, keep)}`,
        b: `${daDrop.name} · ${rotulo(daDrop, drop)}`,
      },
    },
  };
}

/**
 * Casa com outra tipologia cortando a sobreposição parcial antes (D24 · RN-305).
 *
 * O corte acontece em cada tipologia, com as paredes dela: as sobras continuam livres no
 * apartamento de cada uma, e só o trecho comum sobe para a bancada como divisa. O vão e a
 * peça da parede de lá passam a ser medidos a partir do começo da parede que fica — é o
 * começo da divisa (`medirNaQueFica`).
 */
function casarEntreTipologiasComCorte(
  workspace: WorkspaceDraft,
  keep: Wall,
  drop: Wall,
): WorkspaceDraft {
  const daKeep = typologyOfRoom(workspace, keep.sideA.roomId);
  const daDrop = typologyOfRoom(workspace, drop.sideA.roomId);
  if (daKeep === undefined || daDrop === undefined) return workspace;

  const corte = corteParaCasar(daKeep.rooms, keep, daDrop.rooms, drop);
  if (corte === 'SEM_TRECHO') return comErro(workspace, SEM_TRECHO_COMUM);
  if (corte === undefined) return casarEntreTipologias(workspace, keep, drop);

  const tipologiaKeep = cortarParede(daKeep, keep.id, corte.a);
  const cortadaDrop = cortarParede(daDrop, drop.id, corte.b);
  const keepCortada = tipologiaKeep.walls.find((wall) => wall.id === keep.id) ?? keep;
  const dropCortada = cortadaDrop.walls.find((wall) => wall.id === drop.id) ?? drop;
  const tipologiaDrop = medirNaQueFica(cortadaDrop, dropCortada, keepCortada, tipologiaKeep.rooms);

  return casarEntreTipologias(
    {
      ...workspace,
      typologies: workspace.typologies.map((typology) => {
        if (typology.unitTypeId === daKeep.unitTypeId) return tipologiaKeep;
        if (typology.unitTypeId === daDrop.unitTypeId) return tipologiaDrop;
        return typology;
      }),
    },
    keepCortada,
    dropCortada,
  );
}

/**
 * Tira o elemento da tipologia e o põe na bancada, com os trechos que ele já tinha.
 *
 * Acontece quando uma viga da tipologia passa a cruzar uma divisa: a partir dali ela é
 * vista pelos dois aptos, e o concreto dela se reparte entre eles (RN-163). Se ela já
 * estiver na bancada, não há nada a fazer — e é o caso comum, porque a viga nasce lá
 * quando o primeiro trecho é numa divisa.
 */
function promoverParaBancada(
  workspace: WorkspaceDraft,
  elementId: StructuralElementId,
): WorkspaceDraft {
  if (partyElementsOf(workspace).some((element) => element.id === elementId)) return workspace;

  const dona = workspace.typologies.find((typology) =>
    typology.elements.some((element) => element.id === elementId),
  );
  if (dona === undefined) return workspace;

  const elemento = dona.elements.find((element) => element.id === elementId)!;
  const trechos = dona.elementSegments.filter((segment) => segment.elementId === elementId);

  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) =>
      typology.unitTypeId === dona.unitTypeId
        ? {
            ...typology,
            elements: typology.elements.filter((element) => element.id !== elementId),
            elementSegments: typology.elementSegments.filter(
              (segment) => segment.elementId !== elementId,
            ),
          }
        : typology,
    ),
    partyElements: [...partyElementsOf(workspace), elemento],
    partyElementSegments: [...partySegmentsOf(workspace), ...trechos],
  };
}

/**
 * Desfaz a divisa: cada tipologia recebe de volta a sua parede livre.
 *
 * Os vãos ficam com o lado A — eles precisam ir para algum lugar, e o lado que deu origem
 * à parede é o palpite honesto. Quem quiser o contrário refaz o vão do outro lado.
 */
function desfazerDivisa(workspace: WorkspaceDraft, divisa: Wall): WorkspaceDraft {
  if (divisa.sideB === undefined) return workspace;

  const now = new Date().toISOString();
  const rotulo = workspace.partyLabels?.[divisa.id];
  const vaos = (workspace.partyOpenings ?? []).filter((opening) => opening.wallId === divisa.id);
  const trechos = partySegmentsOf(workspace).filter((segment) => segment.wallId === divisa.id);
  const estrutura = partyElementsOf(workspace).filter((element) =>
    trechos.some((segment) => segment.elementId === element.id),
  );

  const daA = typologyOfRoom(workspace, divisa.sideA.roomId);
  const daB = typologyOfRoom(workspace, divisa.sideB.roomId);
  const { [divisa.id]: _foraRotulo, ...partyLabels } = workspace.partyLabels ?? {};

  /** Devolve a parede à tipologia do lado — com os vãos, quando é o lado que os leva. */
  const devolve = (
    typology: TypologyDraft,
    lado: WallSide,
    nome: string | undefined,
    comVaos: boolean,
  ): TypologyDraft => {
    const criada = createWall({
      id: newId<'Wall'>(),
      owner: { kind: 'UNIT_TYPE', unitTypeId: typology.unitTypeId },
      // Sem a posição, que é da divisa e não da tipologia; com o lado do contorno, que é o
      // que devolve a parede ao lugar dela no desenho (RN-243).
      sideA: {
        roomId: lado.roomId,
        ...(lado.edgeIndex === undefined ? {} : { edgeIndex: lado.edgeIndex }),
        // E com o trecho, quando o lado foi cortado (RN-304): a divisa era só o meio dele.
        ...(lado.span === undefined ? {} : { span: lado.span }),
      },
      length: medidaDoTrecho(typology.rooms, lado) ?? divisa.length,
      height: divisa.height,
      now,
      ...(divisa.thickness === undefined ? {} : { thickness: divisa.thickness }),
    });
    if (!criada.ok) return typology;

    const devolvida: TypologyDraft = {
      ...typology,
      walls: [...typology.walls, criada.value],
      openings: comVaos
        ? [
            ...typology.openings,
            ...vaos.map((opening) => ({ ...opening, wallId: criada.value.id })),
          ]
        : typology.openings,
      // A estrutura fica com o mesmo lado que leva os vãos, e pelo mesmo motivo: ela
      // precisa ir para algum lugar, e o lado que deu origem à parede é o palpite honesto.
      elements: comVaos ? [...typology.elements, ...estrutura] : typology.elements,
      elementSegments: comVaos
        ? [
            ...typology.elementSegments,
            ...trechos.map((segment) => ({ ...segment, wallId: criada.value.id })),
          ]
        : typology.elementSegments,
      labels: { ...typology.labels, [criada.value.id]: { a: nome ?? 'Parede' } },
    };
    // E se junta aos pedaços livres do lado dela, como no descasamento dentro da tipologia.
    return juntarPedacos(devolvida, criada.value.id);
  };

  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) => {
      if (daA !== undefined && typology.unitTypeId === daA.unitTypeId) {
        return devolve(typology, divisa.sideA, rotulo?.a, true);
      }
      if (daB !== undefined && typology.unitTypeId === daB.unitTypeId) {
        return devolve(typology, divisa.sideB!, rotulo?.b, false);
      }
      return typology;
    }),
    partyWalls: partyWallsOf(workspace).filter((wall) => wall.id !== divisa.id),
    partyElements: partyElementsOf(workspace).filter(
      (element) => !estrutura.some((foi) => foi.id === element.id),
    ),
    partyElementSegments: partySegmentsOf(workspace).filter(
      (segment) => segment.wallId !== divisa.id,
    ),
    partyOpenings: (workspace.partyOpenings ?? []).filter(
      (opening) => opening.wallId !== divisa.id,
    ),
    partyLabels,
  };
}

/**
 * A ação mira uma divisa? Então ela é da bancada, e não da tipologia na mesa.
 *
 * Sem este desvio, mexer no comprimento ou no vão de uma divisa cairia no `draftReducer`
 * de uma das duas tipologias — que não tem aquela parede — e não faria nada, calada.
 */
function roteiaParaDivisa(
  workspace: WorkspaceDraft,
  action: DraftAction,
): WorkspaceDraft | undefined {
  const divisas = partyWallsOf(workspace);
  const vaos = workspace.partyOpenings ?? [];
  const acha = (wallId: WallId): Wall | undefined => divisas.find((wall) => wall.id === wallId);

  switch (action.type) {
    case 'UPDATE_WALL': {
      const alvo = acha(action.wallId);
      if (alvo === undefined) return undefined;
      if (
        typeof action.patch.length === 'number' &&
        !(Number.isFinite(action.patch.length) && action.patch.length > 0)
      ) {
        return workspace;
      }
      return {
        ...workspace,
        partyWalls: divisas.map((wall) =>
          wall.id === alvo.id ? { ...wall, ...action.patch, id: wall.id } : wall,
        ),
      };
    }

    case 'UNMERGE_WALL': {
      const alvo = acha(action.wallId);
      return alvo === undefined ? undefined : desfazerDivisa(workspace, alvo);
    }

    case 'TOGGLE_JOIN': {
      const alvo = acha(action.wallId);
      if (alvo === undefined || alvo.sideB === undefined) return undefined;
      if (alvo.joined) {
        return {
          ...workspace,
          partyWalls: divisas.map((wall) =>
            wall.id === alvo.id ? splitWall(wall, new Date().toISOString()) : wall,
          ),
        };
      }

      const salaA = workspace.typologies
        .flatMap((typology) => typology.rooms)
        .find((room) => room.id === alvo.sideA.roomId);
      const salaB = workspace.typologies
        .flatMap((typology) => typology.rooms)
        .find((room) => room.id === alvo.sideB!.roomId);
      if (salaA === undefined || salaB === undefined) return workspace;

      const juntada = joinWall(alvo, salaA, salaB, new Date().toISOString());
      if (!juntada.ok) return workspace;
      return {
        ...workspace,
        partyWalls: divisas.map((wall) => (wall.id === alvo.id ? juntada.value : wall)),
      };
    }

    case 'ADD_OPENING': {
      const alvo = acha(action.wallId);
      if (alvo === undefined) return undefined;
      const criado = createOpening({
        id: newId<'Opening'>(),
        wallId: alvo.id,
        kind: action.kind,
        ...medidasDoVao(action),
      });
      return criado.ok ? { ...workspace, partyOpenings: [...vaos, criado.value] } : workspace;
    }

    case 'UPDATE_OPENING': {
      if (!vaos.some((opening) => opening.id === action.openingId)) return undefined;
      return {
        ...workspace,
        partyOpenings: vaos.map((opening) =>
          opening.id === action.openingId
            ? { ...opening, ...action.patch, id: opening.id }
            : opening,
        ),
      };
    }

    case 'REMOVE_OPENING': {
      if (!vaos.some((opening) => opening.id === action.openingId)) return undefined;
      return {
        ...workspace,
        partyOpenings: vaos.filter((opening) => opening.id !== action.openingId),
      };
    }

    case 'RENAME_WALL': {
      const alvo = acha(action.wallId);
      if (alvo === undefined) return undefined;
      const rotulo = workspace.partyLabels?.[alvo.id];
      if (rotulo === undefined) return workspace;
      const nome = action.name.trim();
      const { custom: _anterior, ...resto } = rotulo;
      return {
        ...workspace,
        partyLabels: {
          ...workspace.partyLabels,
          [alvo.id]: nome === '' ? resto : { ...resto, custom: nome },
        },
      };
    }

    case 'ADD_ELEMENT': {
      const divisa = acha(action.wallId);
      if (divisa === undefined) return undefined;

      const now = new Date().toISOString();
      const elementId = newId<'StructuralElement'>();
      const preset =
        action.kind === 'PILAR'
          ? { sectionWidth: 0.2, sectionDepth: 0.4, length: divisa.height }
          : { sectionWidth: 0.15, sectionDepth: 0.5, length: divisa.length };
      const criado = createStructuralElement({
        id: elementId,
        owner: divisa.owner,
        kind: action.kind,
        ...preset,
        now,
      });
      if (!criado.ok) return workspace;

      const trecho = createElementSegment({
        id: newId<'ElementSegment'>(),
        elementId,
        wallId: divisa.id,
        orientation: action.kind === 'PILAR' ? 'PERPENDICULAR' : 'PARALELA',
      });
      if (!trecho.ok) return workspace;

      return {
        ...workspace,
        partyElements: [...partyElementsOf(workspace), criado.value],
        partyElementSegments: [...partySegmentsOf(workspace), trecho.value],
      };
    }

    case 'ADD_ELEMENT_SEGMENT': {
      const divisa = acha(action.wallId);
      if (divisa === undefined) return undefined;
      if (
        partySegmentsOf(workspace).some(
          (segment) => segment.elementId === action.elementId && segment.wallId === divisa.id,
        )
      ) {
        return workspace;
      }

      const trecho = createElementSegment({
        id: newId<'ElementSegment'>(),
        elementId: action.elementId,
        wallId: divisa.id,
        orientation: 'PERPENDICULAR',
      });
      if (!trecho.ok) return workspace;

      // A viga que passa a cruzar uma divisa **muda de casa**: ela precisa ser vista pelas
      // duas tipologias, e uma viga guardada dentro de uma delas deixaria a outra cobrando
      // bloco onde há concreto. Sobe para a bancada inteira, com os trechos que já tinha.
      const promovida = promoverParaBancada(workspace, action.elementId);
      return {
        ...promovida,
        partyElementSegments: [...partySegmentsOf(promovida), trecho.value],
      };
    }

    case 'UPDATE_ELEMENT': {
      const alvo = partyElementsOf(workspace).find((element) => element.id === action.elementId);
      if (alvo === undefined) return undefined;
      const { freeSpan } = action.patch;
      if (freeSpan !== undefined && !(Number.isFinite(freeSpan) && freeSpan >= 0)) return workspace;
      if (conferirMedidas(action.patch, MEDIDAS_DO_ELEMENTO) !== 'SEGUE') return workspace;

      return {
        ...workspace,
        partyElements: partyElementsOf(workspace).map((element) =>
          element.id === alvo.id
            ? { ...element, ...action.patch, id: element.id, updatedAt: new Date().toISOString() }
            : element,
        ),
      };
    }

    case 'UPDATE_ELEMENT_SEGMENT': {
      const alvo = partySegmentsOf(workspace).find((segment) => segment.id === action.segmentId);
      if (alvo === undefined) return undefined;
      const { advance } = action.patch;
      if (advance !== undefined && !(Number.isFinite(advance) && advance >= 0)) return workspace;
      if (conferirMedidas(action.patch, MEDIDAS_DO_TRECHO) !== 'SEGUE') return workspace;

      return {
        ...workspace,
        partyElementSegments: partySegmentsOf(workspace).map((segment) =>
          segment.id === alvo.id ? { ...segment, ...action.patch, id: segment.id } : segment,
        ),
      };
    }

    case 'REMOVE_ELEMENT_SEGMENT': {
      const alvo = partySegmentsOf(workspace).find((segment) => segment.id === action.segmentId);
      if (alvo === undefined) return undefined;

      const partyElementSegments = partySegmentsOf(workspace).filter(
        (segment) => segment.id !== alvo.id,
      );
      const aindaCruza = partyElementSegments.some(
        (segment) => segment.elementId === alvo.elementId,
      );
      return {
        ...workspace,
        partyElementSegments,
        partyElements: aindaCruza
          ? partyElementsOf(workspace)
          : partyElementsOf(workspace).filter((element) => element.id !== alvo.elementId),
      };
    }

    case 'REMOVE_ELEMENT': {
      if (!partyElementsOf(workspace).some((element) => element.id === action.elementId)) {
        return undefined;
      }
      return {
        ...workspace,
        partyElements: partyElementsOf(workspace).filter(
          (element) => element.id !== action.elementId,
        ),
        partyElementSegments: partySegmentsOf(workspace).filter(
          (segment) => segment.elementId !== action.elementId,
        ),
      };
    }

    case 'SET_EDGE_KIND': {
      // A divisa com outra tipologia mora aqui, e não na tipologia da mesa — que nem a vê.
      // Divisa é sempre parede (RN-269): o lado que ela ocupa não vira guarda-corpo nem
      // aberto. O resto é da tipologia da mesa.
      if (action.kind === 'PAREDE' || action.kind === 'MURETA') return undefined;
      const ocupa = divisas.some(
        (wall) => wallSideOf(wall, action.roomId)?.edgeIndex === action.edgeIndex,
      );
      return ocupa ? comErro(workspace, DIVISA_E_PAREDE) : undefined;
    }

    // Nada disto mira parede: segue para a tipologia da mesa. Enumeradas em vez de um
    // `default` para que uma ação nova não passe calada por aqui.
    //
    // `ADD_TRACED_ELEMENT` está aqui porque o traço é da tipologia na mesa: a divisa não
    // tem contorno próprio — ela é o lado de dois ambientes que já foram traçados. O shaft
    // também: ele é do ambiente, e o ambiente é da tipologia na mesa (ADR-150). E a estaca,
    // que é da área comum da Fundação — a tipologia na mesa (RN-361).
    case 'SET_NAME':
    case 'ADD_ROOM':
    case 'UPDATE_ROOM':
    case 'SET_ROOM_FACTOR':
    case 'REMOVE_ROOM':
    case 'RESET_ROOM_GEOMETRY':
    case 'ADD_TRACED_ROOM':
    case 'ADD_TRACED_ELEMENT':
    case 'SET_ROOM_OUTLINE':
    case 'RESHAPE_ROOM':
    case 'CLEAR_ROOM_OUTLINE':
    case 'SET_EDGE_RAILING_HEIGHT':
    case 'ADD_WALL':
    case 'MERGE_WALLS':
    case 'ADD_SHAFT':
    case 'SET_SHAFT_COUNT':
    case 'REMOVE_SHAFT':
    case 'ADD_PILE':
    case 'PLACE_PILE':
    case 'MOVE_PILE':
    case 'UPDATE_PILE':
    case 'SET_PIECE_FACTOR':
    case 'REMOVE_PILE':
    case 'TOGGLE_PIECE_MARK':
    case 'MARK_ALL_PIECES':
    case 'UNMARK_ALL_PIECES':
    case 'ADD_PILE_CAP':
    case 'UPDATE_PILE_CAP':
    case 'REMOVE_PILE_CAP':
    case 'SET_PILE_CAP_TYPE_OF':
    case 'SET_FOUNDATION_FACTOR':
    case 'SET_PIECE_STEEL_RATES':
    case 'ADD_GROUND_BEAM':
    case 'UPDATE_GROUND_BEAM':
    case 'REMOVE_GROUND_BEAM':
    case 'ADD_RETAINING_WALL':
    case 'UPDATE_RETAINING_WALL':
    case 'REMOVE_RETAINING_WALL':
    case 'ADD_RETAINING_COLUMN':
    case 'UPDATE_RETAINING_COLUMN':
    case 'REMOVE_RETAINING_COLUMN':
    case 'ADD_SLAB':
    case 'UPDATE_SLAB':
    case 'REMOVE_SLAB':
    case 'ADD_SLAB_REGION':
    case 'UPDATE_SLAB_REGION':
    case 'REMOVE_SLAB_REGION':
    case 'ADD_STAIR':
    case 'UPDATE_STAIR':
    case 'REMOVE_STAIR':
    case 'ADD_STAIR_FLIGHT':
    case 'UPDATE_STAIR_FLIGHT':
    case 'REMOVE_STAIR_FLIGHT':
    case 'ADD_STAIR_LANDING':
    case 'UPDATE_STAIR_LANDING':
    case 'REMOVE_STAIR_LANDING':
    case 'SET_ITEM_QUANTITY':
    case 'CLEAR_ITEM_QUANTITY':
    case 'CLEAR_SERVICE_QUANTITIES':
    case 'RESET':
      return undefined;
  }
}

/**
 * A divisa perde um lado quando o ambiente daquele lado some.
 *
 * Ela não pode continuar apontando para quem não existe — seria parede órfã, que a
 * geometria descarta em silêncio. Volta a ser parede livre do lado que sobrou, que é o
 * mesmo que `REMOVE_ROOM` faz dentro da tipologia: o vizinho continua tendo aquela parede.
 */
function podarDivisas(workspace: WorkspaceDraft): WorkspaceDraft {
  const divisas = partyWallsOf(workspace);
  if (divisas.length === 0) return workspace;

  const existentes = new Set(
    workspace.typologies.flatMap((typology) => typology.rooms.map((room) => room.id)),
  );
  const quebradas = divisas.filter(
    (wall) =>
      !existentes.has(wall.sideA.roomId) ||
      wall.sideB === undefined ||
      !existentes.has(wall.sideB.roomId),
  );
  if (quebradas.length === 0) return workspace;

  return quebradas.reduce((atual, divisa) => {
    const semALado = !existentes.has(divisa.sideA.roomId);
    const sobrou = semALado ? divisa.sideB : divisa.sideA;
    const desfeita = desfazerDivisa(atual, divisa);
    // `desfazerDivisa` devolve aos dois lados; o lado que sumiu não tem tipologia, então
    // a devolução dele simplesmente não acha destino e some junto.
    void sobrou;
    return desfeita;
  }, workspace);
}

/**
 * A medida e a posição do vão novo.
 *
 * O cartão só diz o tipo, e o vão nasce com o padrão dele. A prancha diz também a medida
 * que se digitou e onde se clicou — e as duas valem mais que o padrão.
 */
function medidasDoVao(action: {
  readonly kind: OpeningKind;
  readonly width?: number;
  readonly height?: number;
  readonly offset?: number;
}): { width: number; height: number; offset?: number } {
  const padrao =
    action.kind === 'JANELA' ? { width: 1.2, height: 1.2 } : { width: 0.8, height: 2.1 };
  return {
    width: action.width ?? padrao.width,
    height: action.height ?? padrao.height,
    ...(action.offset === undefined ? {} : { offset: action.offset }),
  };
}

/**
 * A peça de nome `de` passa a se chamar `para` numa lista do desenho — pelo nome comparável
 * (`nomeDaPeca`), como a planilha casa com o desenho. A mesma lista quando nada muda.
 */
export function renomearNaLista<P extends { readonly name: string }>(
  lista: readonly P[] | undefined,
  de: string,
  para: string,
): readonly P[] | undefined {
  if (lista === undefined) return undefined;
  const antigo = nomeDaPeca(de);
  if (!lista.some((peca) => nomeDaPeca(peca.name) === antigo)) return lista;
  return lista.map((peca) => (nomeDaPeca(peca.name) === antigo ? { ...peca, name: para } : peca));
}

/**
 * A tipologia com a peça renomeada (RN-457): as quantidades da planilha, a peça desenhada de
 * mesmo nome — de qualquer tipo — e a lembrança que o servidor devolveu (RN-458).
 */
function comPecaRenomeada(
  typology: TypologyDraft,
  de: string,
  para: string,
  aliases: Readonly<Record<string, string>>,
): TypologyDraft {
  const { pieceNameAliases: _antes, ...resto } = typology;
  const pieces = renomearNaLista(typology.pieces, de, para);
  const pileCaps = renomearNaLista(typology.pileCaps, de, para);
  const groundBeams = renomearNaLista(typology.groundBeams, de, para);
  const slabs = renomearNaLista(typology.slabs, de, para);
  const retainingWalls = renomearNaLista(typology.retainingWalls, de, para);
  const retainingColumns = renomearNaLista(typology.retainingColumns, de, para);
  const stairs = renomearNaLista(typology.stairs, de, para);
  return {
    ...resto,
    ...(typology.pieceQuantities === undefined
      ? {}
      : { pieceQuantities: renomearPecaNasQuantidades(typology.pieceQuantities, de, para) }),
    ...(pieces === undefined ? {} : { pieces }),
    ...(pileCaps === undefined ? {} : { pileCaps }),
    ...(groundBeams === undefined ? {} : { groundBeams }),
    ...(slabs === undefined ? {} : { slabs }),
    ...(retainingWalls === undefined ? {} : { retainingWalls }),
    ...(retainingColumns === undefined ? {} : { retainingColumns }),
    ...(stairs === undefined ? {} : { stairs }),
    ...(Object.keys(aliases).length === 0 ? {} : { pieceNameAliases: aliases }),
  };
}

/**
 * A origem de cada taxa que a tela mandou (Fase 40, etapa 3 — RN-533): a que veio com origem fica
 * como veio; a de mesmo número que a gravada guarda a origem e a foto dela; a que mudou de número
 * é digitada, e guarda a foto da média ao lado.
 */
function comAOrigem(
  antes: readonly SteelRate[] | undefined,
  depois: readonly SteelRate[],
): readonly SteelRate[] {
  const gravadas = new Map((antes ?? []).map((taxa) => [taxa.gauge, taxa]));
  return depois.map((taxa) => {
    if (taxa.origin !== undefined) return taxa;
    const anterior = gravadas.get(taxa.gauge);
    if (anterior === undefined) return taxa;
    if (anterior.kgPerM3 === taxa.kgPerM3) {
      return {
        ...taxa,
        ...(anterior.origin === undefined ? {} : { origin: anterior.origin }),
        ...(anterior.average === undefined ? {} : { average: anterior.average }),
      };
    }
    if (anterior.origin === undefined && anterior.average === undefined) return taxa;
    return {
      ...taxa,
      origin: 'DIGITADO',
      ...(anterior.average === undefined ? {} : { average: anterior.average }),
    };
  });
}

/**
 * A composição de um tipo com o número de um item digitado (Fase 42, etapa 4 — RN-583; ADR-191):
 * `withOwnFactor`, guardando a origem. O mesmo número fica como era — inclusive "da média". O
 * número novo numa linha que tinha origem ou foto vira `DIGITADO` e fica com a foto: daí em diante
 * entra na média das outras obras (D5) e não muda com ela, com a média ao lado. Sem número, a
 * linha sai, e a peça do tipo volta ao índice da obra.
 */
function comALinhaDigitada(
  rates: readonly ItemFactor[],
  serviceKey: string,
  itemKey: string,
  factor: number | undefined,
): readonly ItemFactor[] {
  const antes = rates.find((linha) => linha.serviceKey === serviceKey && linha.itemKey === itemKey);
  if (factor !== undefined && antes?.factor === factor) return rates;
  const depois = withOwnFactor(rates, serviceKey, itemKey, factor);
  if (factor === undefined || antes === undefined) return depois;
  if (antes.origin === undefined && antes.typeAverage === undefined) return depois;
  return depois.map((linha) =>
    linha.serviceKey === serviceKey && linha.itemKey === itemKey
      ? {
          ...linha,
          origin: 'DIGITADO' as const,
          ...(antes.typeAverage === undefined ? {} : { typeAverage: antes.typeAverage }),
        }
      : linha,
  );
}

/**
 * A média na composição e na taxa de um tipo (Fase 42, etapa 4 — RN-582, RN-583; ADR-191), o
 * `APPLY_INDEX_AVERAGE` e o `APPLY_STEEL_AVERAGE` das linhas de um tipo:
 *
 * - `follow`: a linha passa a ser a média, `MEDIA`, com a foto — a que o tipo não tinha nasce;
 * - sem `follow`: só a foto muda, e o número fica. Sem a linha, não há digitado a manter.
 *
 * A lista vazia não mexe: o tipo que não ganha taxa da média continua sem ela, ou com a dele. A
 * recusa (fator ou taxa não positivos) volta como texto, para o redutor dizer na tela.
 */
function comAMediaNoTipo(
  tipo: { readonly rates: readonly ItemFactor[]; readonly steelRates?: readonly SteelRate[] },
  media: {
    readonly rates: readonly LinhaDaMediaNoTipo[];
    readonly steelRates: readonly BitolaDaMediaNoTipo[];
  },
): { readonly rates: readonly ItemFactor[]; readonly steelRates?: readonly SteelRate[] } | string {
  let rates = tipo.rates;
  for (const linha of media.rates) {
    const atual = rates.find(
      (candidata) =>
        candidata.serviceKey === linha.serviceKey && candidata.itemKey === linha.itemKey,
    );
    if (!linha.follow && atual === undefined) continue;
    const nova: ItemFactor = linha.follow
      ? {
          serviceKey: linha.serviceKey,
          itemKey: linha.itemKey,
          factor: linha.average.rate,
          origin: 'MEDIA',
          typeAverage: linha.average,
        }
      : { ...atual!, typeAverage: linha.average };
    rates = [...withOwnFactor(rates, linha.serviceKey, linha.itemKey, undefined), nova];
  }
  const conferidas = checkItemFactors(rates);
  if (!conferidas.ok) return conferidas.error.message;
  if (media.steelRates.length === 0) {
    return tipo.steelRates === undefined
      ? { rates: conferidas.value }
      : { rates: conferidas.value, steelRates: tipo.steelRates };
  }
  let taxas: readonly SteelRate[] = tipo.steelRates ?? [];
  for (const bitola of media.steelRates) {
    const atual = taxas.find((taxa) => taxa.gauge === bitola.gauge);
    if (!bitola.follow && atual === undefined) continue;
    const nova: SteelRate = bitola.follow
      ? {
          gauge: bitola.gauge,
          kgPerM3: bitola.average.rate,
          origin: 'MEDIA',
          average: bitola.average,
        }
      : { ...atual!, average: bitola.average };
    taxas = [...taxas.filter((taxa) => taxa.gauge !== bitola.gauge), nova];
  }
  // Na ordem das bitolas, e conferida como a da disciplina: positiva, uma por bitola.
  const normalizadas = normalizeSteelRates(taxas);
  if (!normalizadas.ok) return normalizadas.error.message;
  return { rates: conferidas.value, steelRates: normalizadas.value };
}

/** A recusa dita na tela — o `lastError` da bancada, irmão do da tipologia. */
function comErro(workspace: WorkspaceDraft, mensagem: string): WorkspaceDraft {
  return { ...workspace, lastError: mensagem };
}

const ELEMENTO_NAO_ENCONTRADO = 'Esse pilar ou essa viga não está mais neste desenho.';
const TIPO_DE_ELEMENTO_NAO_ENCONTRADO = 'Esse tipo de pilar ou de viga não está mais nesta obra.';

/** O pilar e a viga de toda a bancada: os das tipologias e os da divisa (Fase 42). */
function elementosDaBancada(workspace: WorkspaceDraft): readonly StructuralElement[] {
  return [
    ...workspace.typologies.flatMap((typology) => typology.elements),
    ...partyElementsOf(workspace),
  ];
}

/**
 * Cada pilar e viga da bancada pela mudança que `mudar` dá (Fase 42, RN-562, RN-563) — os das
 * tipologias e os da divisa, que moram na bancada. A lista sem nenhuma peça mudada fica a mesma,
 * pela identidade: a tipologia que não mudou não refaz conta nenhuma.
 */
function comCadaElemento(
  workspace: WorkspaceDraft,
  mudar: (element: StructuralElement) => StructuralElement,
): WorkspaceDraft {
  const mapear = (lista: readonly StructuralElement[]): readonly StructuralElement[] => {
    const nova = lista.map(mudar);
    return nova.some((element, indice) => element !== lista[indice]) ? nova : lista;
  };
  const daDivisa =
    workspace.partyElements === undefined ? undefined : mapear(workspace.partyElements);
  return {
    ...workspace,
    typologies: workspace.typologies.map((typology) => {
      const elements = mapear(typology.elements);
      return elements === typology.elements ? typology : { ...typology, elements };
    }),
    ...(daDivisa === undefined ? {} : { partyElements: daDivisa }),
  };
}

export function workspaceReducer(
  workspace: WorkspaceDraft,
  action: WorkspaceAction,
): WorkspaceDraft {
  // O erro é da ação que acabou de acontecer, e sai antes da próxima — igual ao `clean`
  // do `draftReducer`. Sobrevivendo, ele acusaria a ação seguinte, que não fez nada de
  // errado. Só reconstrói o objeto quando há erro a tirar, para não invalidar a
  // identidade do rascunho a cada dispatch.
  const limpo =
    workspace.lastError === undefined
      ? workspace
      : (({ lastError: _anterior, ...resto }) => resto)(workspace);

  return decidirNaBancada(limpo, action);
}

function decidirNaBancada(workspace: WorkspaceDraft, action: WorkspaceAction): WorkspaceDraft {
  if (!isBenchAction(action)) {
    // A divisa é do pavimento: mexer nela não passa pela tipologia da mesa.
    const naDivisa = roteiaParaDivisa(workspace, action);
    if (naDivisa !== undefined) return naDivisa;

    // Casar com parede de outra tipologia vira divisa; dentro da mesma, segue como era.
    if (action.type === 'MERGE_WALLS') {
      const ativa = activeTypology(workspace);
      const keep = ativa.walls.find((wall) => wall.id === action.keepId);
      const dropDeFora = workspace.typologies
        .filter((typology) => typology.unitTypeId !== ativa.unitTypeId)
        .flatMap((typology) => typology.walls)
        .find((wall) => wall.id === action.dropId);

      if (keep !== undefined && dropDeFora !== undefined) {
        if (keep.sideB !== undefined || dropDeFora.sideB !== undefined) return workspace;
        return casarEntreTipologiasComCorte(workspace, keep, dropDeFora);
      }
    }

    // Ambiente e parede pertencem à tipologia que está na mesa.
    const ativa = activeTypology(workspace);
    return podarDivisas({
      ...workspace,
      typologies: workspace.typologies.map((typology) =>
        typology.unitTypeId === ativa.unitTypeId
          ? draftReducer(typology, action, workspace.pileTypes, workspace.pileCapTypes)
          : typology,
      ),
    });
  }

  switch (action.type) {
    case 'DISMISS_ERROR': {
      if (workspace.typologies.every((typology) => typology.lastError === undefined)) {
        return workspace;
      }
      return {
        ...workspace,
        typologies: workspace.typologies.map(({ lastError: _recusa, ...tipologia }) => tipologia),
      };
    }

    case 'SET_PLAN':
      return { ...workspace, plan: action.plan };

    case 'CLEAR_PLAN': {
      const { plan: _fora, ...resto } = workspace;
      return resto;
    }

    case 'SET_PLAN_SCALE': {
      if (workspace.plan === undefined) return workspace;

      // Tudo o que saiu da prancha está em metros **derivados de pixel**, e recalibrar
      // muda o que um pixel vale. Sem reescalar junto, o que já foi traçado sai do lugar
      // no desenho e a área muda sozinha — o número mais perigoso do sistema, porque
      // continua parecendo calculado.
      const anterior = workspace.plan.metresPerPixel;
      const fator = anterior === undefined ? 1 : action.metresPerPixel / anterior;

      const comEscala = {
        ...workspace,
        plan: { ...workspace.plan, metresPerPixel: action.metresPerPixel },
      };
      return fator === 1
        ? comEscala
        : reescalarTracado(
            comEscala,
            fator,
            action.unitTypeIds === undefined ? undefined : new Set(action.unitTypeIds),
          );
    }

    case 'ADD_TOWER': {
      const nome = action.name ?? nextTowerName(workspace.towers);
      return {
        ...workspace,
        towers: [...workspace.towers, { id: crypto.randomUUID(), name: nome, floors: [] }],
      };
    }

    case 'RENAME_TOWER':
      return mapTower(workspace, action.towerId, (tower) => ({ ...tower, name: action.name }));

    case 'REMOVE_TOWER':
      return semAsSombrasOrfas({
        ...workspace,
        towers: workspace.towers.filter((tower) => tower.id !== action.towerId),
      });

    case 'ADD_FLOORS': {
      const novos = planFloorPlans(action.spec).map((planejado): FloorDraft => ({
        id: crypto.randomUUID(),
        ...planejado,
      }));
      // Acrescenta ao que já existe e reordena por nível: gerar duas vezes empilha o
      // prédio na ordem certa em vez de embaralhar subsolo com cobertura.
      return mapTower(workspace, action.towerId, (tower) => ({
        ...tower,
        floors: [...tower.floors, ...novos].sort((a, b) => a.level - b.level),
      }));
    }

    case 'TOGGLE_FLOOR_UNITS':
      return mapTower(workspace, action.towerId, (tower) => ({
        ...tower,
        floors: tower.floors.map((floor) =>
          floor.id === action.floorId && !alwaysHasUnits(floor.kind)
            ? { ...floor, hasUnits: !floor.hasUnits }
            : floor,
        ),
      }));

    case 'REMOVE_FLOOR':
      return semAsSombrasOrfas(
        mapTower(workspace, action.towerId, (tower) => ({
          ...tower,
          floors: tower.floors.filter((floor) => floor.id !== action.floorId),
        })),
      );

    case 'ADD_TYPOLOGY': {
      const nova = emptyDraft(
        action.name ?? nextTypologyName(workspace.typologies),
        action.kind ?? 'APARTAMENTO',
        action.floorKind ?? 'TIPO',
      );
      return {
        ...workspace,
        typologies: [...workspace.typologies, nova],
        activeId: nova.unitTypeId,
      };
    }

    case 'HYDRATE_GEOMETRY':
      return action.workspace;

    case 'HYDRATE_CATALOG':
      return { ...workspace, catalog: action.catalog };

    case 'HYDRATE_ITEMS': {
      const doServidor = new Map(
        action.typologies.map((typology) => [typology.unitTypeId, typology]),
      );
      const comDecisoes = (typology: TypologyDraft): TypologyDraft => {
        const {
          sheetName: _aba,
          itemQuantities: _digitadas,
          itemReasons: _motivos,
          serviceQuantities: _doServico,
          pieceQuantities: _dasPecas,
          pieceNameAliases: _apelidos,
          ...resto
        } = typology;
        const lancado = doServidor.get(typology.unitTypeId);
        return {
          ...resto,
          ...(lancado?.sheetName === undefined ? {} : { sheetName: lancado.sheetName }),
          ...(lancado === undefined || Object.keys(lancado.itemQuantities).length === 0
            ? {}
            : { itemQuantities: lancado.itemQuantities }),
          ...(lancado?.itemReasons === undefined || Object.keys(lancado.itemReasons).length === 0
            ? {}
            : { itemReasons: lancado.itemReasons }),
          ...(lancado?.serviceQuantities === undefined ||
          Object.keys(lancado.serviceQuantities).length === 0
            ? {}
            : { serviceQuantities: lancado.serviceQuantities }),
          // As peças também são do servidor (Fase 28): a tipologia que ele não lista com peça
          // fica sem o campo — e a gravação seguinte não mexe nas peças dela (RN-390).
          ...(lancado?.pieceQuantities === undefined
            ? {}
            : { pieceQuantities: lancado.pieceQuantities }),
          ...(lancado?.pieceNameAliases === undefined
            ? {}
            : { pieceNameAliases: lancado.pieceNameAliases }),
        };
      };
      const conhecidas = new Set(workspace.typologies.map((typology) => typology.unitTypeId));
      const novas = action.typologies
        .filter((typology) => !conhecidas.has(typology.unitTypeId))
        .map((typology) =>
          comDecisoes({
            ...emptyDraft(typology.name, typology.kind),
            unitTypeId: typology.unitTypeId,
          }),
        );
      return {
        ...workspace,
        catalog: action.catalog,
        typologies: [...workspace.typologies.map(comDecisoes), ...novas],
      };
    }

    case 'SYNC_TYPOLOGIES': {
      // Obra sem tipologia nenhuma continua com a de rascunho na mesa: a bancada nunca
      // fica vazia, e `activeTypology` conta com isso.
      if (action.unitTypes.length === 0) return workspace;

      const pavimentoDe = new Map(
        action.unitTypes.map((unitType) => [unitType.unitTypeId, unitType.floorKind]),
      );
      const guardadas = new Map(
        workspace.typologies.map((typology) => [typology.unitTypeId, typology]),
      );

      // Nada mudou: devolver o mesmo objeto evita regravar o `localStorage` a cada
      // rebusca da tela, que acontece a cada escrita. O pavimento conta: a peça da
      // Fundação se confere aqui, e a tipologia guardada como `TIPO` a recusaria.
      const iguais =
        workspace.typologies.length === action.unitTypes.length &&
        workspace.typologies.every(
          (typology) => pavimentoDe.get(typology.unitTypeId) === typology.floorKind,
        );
      if (iguais) return workspace;

      // A geometria guardada segue com a tipologia dela; a que a obra não tem mais sai,
      // e com ela a de rascunho que nasceu antes de existir obra.
      const typologies = action.unitTypes.map((unitType) => {
        const guardada = guardadas.get(unitType.unitTypeId);
        if (guardada === undefined) {
          return {
            ...emptyDraft(unitType.name, unitType.kind, unitType.floorKind),
            unitTypeId: unitType.unitTypeId,
          };
        }
        if (guardada.floorKind === unitType.floorKind) return guardada;
        // A recusa guardada era do pavimento errado ("a estaca é traçada na área comum da
        // Fundação", na Fundação): com o pavimento certo, ela não vale mais.
        const { lastError: _recusaAntiga, ...semRecusa } = guardada;
        return { ...semRecusa, floorKind: unitType.floorKind };
      });

      return semAsSombrasOrfas({
        ...workspace,
        typologies,
        activeId: pavimentoDe.has(workspace.activeId)
          ? workspace.activeId
          : typologies[0]!.unitTypeId,
      });
    }

    case 'SELECT_TYPOLOGY':
      return { ...workspace, activeId: action.unitTypeId };

    case 'RENAME_TYPOLOGY':
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) =>
          typology.unitTypeId === action.unitTypeId ? { ...typology, name: action.name } : typology,
        ),
      };

    case 'SET_TYPOLOGY_KIND':
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) =>
          typology.unitTypeId === action.unitTypeId ? { ...typology, kind: action.kind } : typology,
        ),
      };

    case 'SET_TYPOLOGY_FLOOR':
      // As sombras órfãs saem junto: a chave do apto é pavimento × tipologia, e mudar de
      // pavimento faz os aptos antigos deixarem de existir.
      return semAsSombrasOrfas({
        ...workspace,
        typologies: workspace.typologies.map((typology) =>
          typology.unitTypeId === action.unitTypeId
            ? // O número solto sai junto: o multiplicador passa a vir do pavimento novo,
              // e um "acontece 1 vez" herdado do anterior mentiria sobre ele.
              (({ floors: _fora, ...resto }) => ({ ...resto, floorKind: action.floorKind }))(
                typology,
              )
            : typology,
        ),
      });

    case 'SET_TYPOLOGY_SHEET':
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) => {
          if (typology.unitTypeId !== action.unitTypeId) return typology;
          if (action.sheetName === '') {
            const { sheetName: _fora, ...semAba } = typology;
            return semAba;
          }
          return { ...typology, sheetName: action.sheetName };
        }),
      };

    case 'SET_PILE_TYPES':
      return { ...workspace, pileTypes: action.pileTypes };

    case 'ADD_PILE_CAP_TYPE': {
      const tipos = workspace.pileCapTypes ?? [];
      const conferido = checkPileCapTypeFields({ name: action.name, rates: [] }, tipos);
      if (!conferido.ok) return comErro(workspace, conferido.error.message);
      const agora = new Date().toISOString();
      if (action.id !== undefined && tipos.some((tipo) => tipo.id === action.id)) {
        return comErro(workspace, 'Já existe o tipo de bloco com esse identificador nesta obra.');
      }
      const tipo: PileCapType = {
        id: action.id ?? newId<'PileCapType'>(),
        projectId:
          action.projectId ??
          tipos[0]?.projectId ??
          workspace.pileTypes?.[0]?.projectId ??
          asId<'Project'>('obra'),
        name: conferido.value.name,
        rates: [],
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...workspace, pileCapTypes: [...tipos, tipo] };
    }

    case 'RENAME_PILE_CAP_TYPE':
    case 'SET_PILE_CAP_TYPE_RATE': {
      const tipos = workspace.pileCapTypes ?? [];
      const tipo = tipos.find((candidato) => candidato.id === action.typeId);
      if (tipo === undefined) return comErro(workspace, TIPO_DE_BLOCO_NAO_ENCONTRADO);
      // O nome e a composição passam pela mesma conferência do domínio (RN-536): nome único na
      // obra, fator positivo. O número digitado numa linha que seguia a média a torna dele
      // (Fase 42, RN-583): `comALinhaDigitada`.
      const conferido = checkPileCapTypeFields(
        action.type === 'RENAME_PILE_CAP_TYPE'
          ? { name: action.name, rates: tipo.rates }
          : {
              name: tipo.name,
              rates: comALinhaDigitada(
                tipo.rates,
                action.serviceKey,
                action.itemKey,
                action.factor,
              ),
            },
        tipos,
        tipo.id,
      );
      if (!conferido.ok) return comErro(workspace, conferido.error.message);
      return {
        ...workspace,
        pileCapTypes: tipos.map((candidato) =>
          candidato.id === tipo.id
            ? { ...candidato, ...conferido.value, updatedAt: new Date().toISOString() }
            : candidato,
        ),
      };
    }

    case 'SET_PILE_CAP_TYPE_STEEL_RATES': {
      const tipos = workspace.pileCapTypes ?? [];
      if (!tipos.some((tipo) => tipo.id === action.typeId)) {
        return comErro(workspace, TIPO_DE_BLOCO_NAO_ENCONTRADO);
      }
      // A mesma conferência da taxa da disciplina (RN-543): positiva, uma por bitola. A vazia
      // fica, e o bloco do tipo volta à da disciplina. A origem como na da disciplina
      // (`comAOrigem`, Fase 42 — RN-583): a bitola igual fica como era; a mudada vira digitada,
      // com a foto da média ao lado.
      const taxas = normalizeSteelRates(action.rates);
      if (!taxas.ok) return comErro(workspace, taxas.error.message);
      return {
        ...workspace,
        pileCapTypes: tipos.map((tipo) =>
          tipo.id === action.typeId
            ? {
                ...tipo,
                steelRates: comAOrigem(tipo.steelRates, taxas.value),
                updatedAt: new Date().toISOString(),
              }
            : tipo,
        ),
      };
    }

    case 'APPLY_PILE_CAP_TYPE_AVERAGE': {
      const tipos = workspace.pileCapTypes ?? [];
      const tipo = tipos.find((candidato) => candidato.id === action.typeId);
      if (tipo === undefined) return comErro(workspace, TIPO_DE_BLOCO_NAO_ENCONTRADO);
      const comMedia = comAMediaNoTipo(tipo, action);
      if (typeof comMedia === 'string') return comErro(workspace, comMedia);
      return {
        ...workspace,
        pileCapTypes: tipos.map((candidato) =>
          candidato.id === tipo.id
            ? { ...candidato, ...comMedia, updatedAt: new Date().toISOString() }
            : candidato,
        ),
      };
    }

    case 'REMOVE_PILE_CAP_TYPE': {
      const tipos = workspace.pileCapTypes ?? [];
      if (!tipos.some((tipo) => tipo.id === action.typeId)) return workspace;
      const agora = new Date().toISOString();
      // O bloco que apontava o tipo volta a seguir a obra (RN-537): o tipo não fica órfão no
      // bloco, nem o bloco fica sem desenho. O fator próprio dele continua.
      return {
        ...workspace,
        pileCapTypes: tipos.filter((tipo) => tipo.id !== action.typeId),
        typologies: workspace.typologies.map((typology) => {
          if (!(typology.pileCaps ?? []).some((bloco) => bloco.pileCapTypeId === action.typeId)) {
            return typology;
          }
          return {
            ...typology,
            pileCaps: (typology.pileCaps ?? []).map((bloco) => {
              if (bloco.pileCapTypeId !== action.typeId) return bloco;
              const { pileCapTypeId: _fora, ...semTipo } = bloco;
              return { ...semTipo, updatedAt: agora };
            }),
          };
        }),
      };
    }

    case 'ADD_ELEMENT_TYPE': {
      const tipos = workspace.elementTypes ?? [];
      const conferido = checkElementTypeFields(
        { kind: action.kind, name: action.name, rates: [] },
        tipos,
      );
      if (!conferido.ok) return comErro(workspace, conferido.error.message);
      if (action.id !== undefined && tipos.some((tipo) => tipo.id === action.id)) {
        return comErro(workspace, 'Já existe um tipo de pilar ou de viga com esse identificador.');
      }
      const agora = new Date().toISOString();
      const tipo: StructuralElementType = {
        id: action.id ?? newId<'StructuralElementType'>(),
        projectId:
          action.projectId ??
          tipos[0]?.projectId ??
          workspace.pileCapTypes?.[0]?.projectId ??
          workspace.pileTypes?.[0]?.projectId ??
          asId<'Project'>('obra'),
        kind: action.kind,
        name: conferido.value.name,
        rates: [],
        createdAt: agora,
        updatedAt: agora,
      };
      return { ...workspace, elementTypes: [...tipos, tipo] };
    }

    case 'RENAME_ELEMENT_TYPE':
    case 'SET_ELEMENT_TYPE_RATE': {
      const tipos = workspace.elementTypes ?? [];
      const tipo = tipos.find((candidato) => candidato.id === action.typeId);
      if (tipo === undefined) return comErro(workspace, TIPO_DE_ELEMENTO_NAO_ENCONTRADO);
      // O nome e a composição passam pela conferência do domínio (RN-561): nome único na obra
      // dentro do elemento, fator positivo. O número digitado numa linha que seguia a média a
      // torna dele (etapa 4, RN-583), como no tipo de bloco.
      const conferido = checkElementTypeFields(
        action.type === 'RENAME_ELEMENT_TYPE'
          ? { kind: tipo.kind, name: action.name, rates: tipo.rates }
          : {
              kind: tipo.kind,
              name: tipo.name,
              rates: comALinhaDigitada(
                tipo.rates,
                action.serviceKey,
                action.itemKey,
                action.factor,
              ),
            },
        tipos,
        tipo.id,
      );
      if (!conferido.ok) return comErro(workspace, conferido.error.message);
      return {
        ...workspace,
        elementTypes: tipos.map((candidato) =>
          candidato.id === tipo.id
            ? { ...candidato, ...conferido.value, updatedAt: new Date().toISOString() }
            : candidato,
        ),
      };
    }

    case 'REMOVE_ELEMENT_TYPE': {
      const tipos = workspace.elementTypes ?? [];
      if (!tipos.some((tipo) => tipo.id === action.typeId)) return workspace;
      const agora = new Date().toISOString();
      // A peça que apontava o tipo volta a seguir a obra (RN-562), na mesma ação: o servidor
      // recusa o tipo que sai com uma peça ainda apontando para ele. O fator próprio continua.
      return {
        ...comCadaElemento(workspace, (element) => {
          if (element.elementTypeId !== action.typeId) return element;
          const { elementTypeId: _fora, ...semTipo } = element;
          return { ...semTipo, updatedAt: agora };
        }),
        elementTypes: tipos.filter((tipo) => tipo.id !== action.typeId),
      };
    }

    case 'SET_ELEMENT_TYPE_OF': {
      const alvo = new Set<string>(action.elementIds);
      const escolhidos = elementosDaBancada(workspace).filter((element) => alvo.has(element.id));
      if (escolhidos.length !== alvo.size) return comErro(workspace, ELEMENTO_NAO_ENCONTRADO);
      // O tipo é do mesmo elemento de cada peça (RN-257). A recusa é do lote inteiro: dar o tipo
      // a metade dos marcados seria uma meia mudança calada. Sem a lista da obra ainda, a
      // bancada não tem com o que conferir — o servidor confere na gravação.
      if (action.typeId !== undefined && workspace.elementTypes !== undefined) {
        const tipos = new Map(workspace.elementTypes.map((tipo) => [String(tipo.id), tipo]));
        for (const element of escolhidos) {
          const conferido = checkElementTypeOf(
            { kind: element.kind, elementTypeId: action.typeId },
            tipos,
          );
          if (!conferido.ok) return comErro(workspace, conferido.error.message);
        }
      }
      const agora = new Date().toISOString();
      return comCadaElemento(workspace, (element) => {
        if (!alvo.has(element.id)) return element;
        // Sem tipo, a chave sai do objeto: ausente é "segue a obra" para o servidor (RN-562).
        const { elementTypeId: _antes, ...semTipo } = element;
        return action.typeId === undefined
          ? { ...semTipo, updatedAt: agora }
          : { ...semTipo, elementTypeId: action.typeId, updatedAt: agora };
      });
    }

    case 'SET_ELEMENT_FACTOR': {
      const peca = elementosDaBancada(workspace).find((element) => element.id === action.elementId);
      if (peca === undefined) return comErro(workspace, ELEMENTO_NAO_ENCONTRADO);
      // A conta do fator do bloco (`SET_FOUNDATION_FACTOR`): `withOwnFactor` e a conferência do
      // domínio, e a lista vazia fica — é ela que diz ao servidor "nenhum" (RN-563).
      const conferidos = checkItemFactors(
        withOwnFactor(peca.factors, action.serviceKey, action.itemKey, action.factor),
      );
      if (!conferidos.ok) return comErro(workspace, conferidos.error.message);
      const agora = new Date().toISOString();
      return comCadaElemento(workspace, (element) =>
        element.id === peca.id
          ? { ...element, factors: conferidos.value, updatedAt: agora }
          : element,
      );
    }

    case 'SET_ELEMENT_STEEL_RATES': {
      const peca = elementosDaBancada(workspace).find((element) => element.id === action.elementId);
      if (peca === undefined) return comErro(workspace, ELEMENTO_NAO_ENCONTRADO);
      // A conferência da taxa da disciplina (`normalizeSteelRates`): positiva, uma por bitola. A
      // vazia fica — é ela que diz ao servidor "nenhuma", e a peça volta à do tipo ou à da
      // disciplina (RN-568). O pilar e a viga nunca vêm da planilha por peça: não há o caso do
      // aço que já veio pronto (RN-547).
      const taxas = normalizeSteelRates(action.rates);
      if (!taxas.ok) return comErro(workspace, taxas.error.message);
      const agora = new Date().toISOString();
      return comCadaElemento(workspace, (element) =>
        element.id === peca.id
          ? { ...element, steelRates: taxas.value, updatedAt: agora }
          : element,
      );
    }

    case 'SET_ELEMENT_TYPE_STEEL_RATES': {
      const tipos = workspace.elementTypes ?? [];
      if (!tipos.some((tipo) => tipo.id === action.typeId)) {
        return comErro(workspace, TIPO_DE_ELEMENTO_NAO_ENCONTRADO);
      }
      // A mesma conferência do tipo de bloco (RN-567): a vazia fica, e a peça do tipo volta à da
      // disciplina. A origem também (`comAOrigem`, etapa 4 — RN-583).
      const taxas = normalizeSteelRates(action.rates);
      if (!taxas.ok) return comErro(workspace, taxas.error.message);
      return {
        ...workspace,
        elementTypes: tipos.map((tipo) =>
          tipo.id === action.typeId
            ? {
                ...tipo,
                steelRates: comAOrigem(tipo.steelRates, taxas.value),
                updatedAt: new Date().toISOString(),
              }
            : tipo,
        ),
      };
    }

    case 'APPLY_ELEMENT_TYPE_AVERAGE': {
      const tipos = workspace.elementTypes ?? [];
      const tipo = tipos.find((candidato) => candidato.id === action.typeId);
      if (tipo === undefined) return comErro(workspace, TIPO_DE_ELEMENTO_NAO_ENCONTRADO);
      const comMedia = comAMediaNoTipo(tipo, action);
      if (typeof comMedia === 'string') return comErro(workspace, comMedia);
      return {
        ...workspace,
        elementTypes: tipos.map((candidato) =>
          candidato.id === tipo.id
            ? { ...candidato, ...comMedia, updatedAt: new Date().toISOString() }
            : candidato,
        ),
      };
    }

    case 'IMPORT_PILES': {
      const agora = new Date().toISOString();
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) => {
          if (typology.unitTypeId !== action.unitTypeId) return typology;
          const pecas: StructuralPiece[] = [...(typology.pieces ?? [])];
          for (const estaca of action.piles) {
            const conferida = checkPileFields(estaca, pecas);
            if (!conferida.ok) continue;
            pecas.push({
              id: newId<'StructuralPiece'>(),
              unitTypeId: typology.unitTypeId,
              kind: 'ESTACA',
              name: conferida.value.name,
              pileTypeId: conferida.value.pileTypeId,
              createdAt: agora,
              updatedAt: agora,
            });
          }
          return { ...typology, pieces: pecas };
        }),
      };
    }

    case 'IMPORT_PIECE_QUANTITIES':
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) =>
          typology.unitTypeId === action.unitTypeId
            ? {
                ...typology,
                // A peça que veio troca as quantidades dela; a que não veio fica (RN-386). O
                // nome antigo da planilha atualiza a peça renomeada (RN-458).
                pieceQuantities: juntarQuantidadesPorPeca(
                  typology.pieceQuantities,
                  action.quantidades,
                  typology.pieceNameAliases,
                ).quantidades,
              }
            : typology,
        ),
      };

    case 'RENAME_IMPORTED_PIECE':
      return {
        ...workspace,
        typologies: workspace.typologies.map((typology) =>
          typology.unitTypeId === action.unitTypeId
            ? comPecaRenomeada(typology, action.from, action.to, action.aliases)
            : typology,
        ),
      };

    case 'IMPORT_CATALOG':
      return { ...workspace, catalog: action.catalog };

    case 'CLEAR_CATALOG': {
      // O vínculo de cada tipologia some junto: apontar para a aba de uma planilha que
      // não está mais aqui é estado mentindo, e a tela mostraria uma referência vazia
      // como se fosse "zero".
      const { catalog: _fora, ...resto } = workspace;
      return {
        ...resto,
        typologies: workspace.typologies.map((typology) => {
          const { sheetName: _semAba, ...limpa } = typology;
          // As peças importadas saem com os itens a que apontavam. Vazias, e não ausentes: a
          // tipologia continua na gravação das peças, e é assim que as do servidor saem.
          return limpa.pieceQuantities === undefined
            ? limpa
            : { ...limpa, pieceQuantities: semQuantidadesPorPeca() };
        }),
      };
    }

    case 'SET_SERVICE_MEASURE': {
      const antes = servicoDoCatalogo(workspace, action.serviceKey);
      const medida = mapCatalogService(workspace, action.serviceKey, (servico) => {
        // Par que a tabela não conhece não entra (RN-256): o serviço fica como estava, e
        // não com um elemento que ninguém escolheu.
        const elemento = resolveAppliesTo(action.measureBy, action.appliesTo, servico.appliesTo);
        if (!elemento.ok) return servico;
        const { appliesTo: _antigo, ...semElemento } = servico;
        return {
          ...semElemento,
          measureBy: action.measureBy,
          ...(elemento.value === undefined ? {} : { appliesTo: elemento.value }),
          // Escolher na tela é decidir: daqui em diante, reimportar a planilha não mexe
          // mais nesta linha, nem que o palpite mude de opinião.
          measureOrigin: 'DECIDIDO',
        };
      });
      // Trocar de "o desenho mede" para "digitado", ou o contrário, muda o que o medido
      // guardado **é** (fatia 5): a quantidade digitada viraria correção sem motivo, e a
      // correção viraria quantidade. O medido sai das tipologias, e o novo modo começa do
      // zero — do traçado, ou do campo vazio. A quantidade na unidade do serviço fica.
      const depois = servicoDoCatalogo(medida, action.serviceKey);
      if (
        antes === undefined ||
        depois === undefined ||
        serviceMeasuring(antes) === serviceMeasuring(depois)
      ) {
        return medida;
      }
      return {
        ...medida,
        typologies: medida.typologies.map((typology) =>
          typology.serviceQuantities?.[action.serviceKey] === undefined
            ? typology
            : comDecisaoDoServico(typology, action.serviceKey, ({ quantity }) =>
                quantity === undefined ? {} : { quantity },
              ),
        ),
      };
    }

    case 'SET_SERVICE_MEASURED': {
      const servico = servicoDoCatalogo(workspace, action.serviceKey);
      if (servico === undefined) return workspace;
      // A regra é a do domínio, a mesma que o servidor aplica: correção sem motivo não
      // entra, e o número digitado no serviço sem desenho entra sem motivo nenhum.
      const decidido = decideMeasured(servico, action.measured, action.reason);
      if (!decidido.ok) return comErro(workspace, decidido.error.message);
      return naTipologia(workspace, action.unitTypeId, (typology) =>
        comDecisaoDoServico(typology, action.serviceKey, ({ quantity }) => ({
          ...(quantity === undefined ? {} : { quantity }),
          measured: decidido.value.measured,
          ...(decidido.value.reason === undefined ? {} : { reason: decidido.value.reason }),
        })),
      );
    }

    case 'SET_ITEM_INDEX':
    case 'SET_ITEM_LOSS': {
      const valor = action.type === 'SET_ITEM_INDEX' ? action.consumptionIndex : action.lossPercent;
      // O banco recusa o negativo, e a perda de mil por cento é erro de digitação (`0041`):
      // recusar aqui é dizer na tela, em vez de a gravação falhar calada depois.
      const teto = action.type === 'SET_ITEM_INDEX' ? 99_999_999 : 999.999;
      if (valor !== undefined && (!Number.isFinite(valor) || valor < 0 || valor > teto)) {
        return comErro(
          workspace,
          'O índice de consumo e a perda não podem ser negativos, e a perda fica abaixo de 1000%.',
        );
      }
      const campo = action.type === 'SET_ITEM_INDEX' ? 'consumptionIndex' : 'lossPercent';
      return mapCatalogService(workspace, action.serviceKey, (servico) => ({
        ...servico,
        items: servico.items.map((item) => {
          if (item.key !== action.itemKey) return item;
          // Hora de gente não se perde no caminho (D8): a perda da mão de obra não entra.
          if (campo === 'lossPercent' && item.kind === 'MAO_DE_OBRA') return item;
          const { [campo]: _anterior, ...semCampo } = item;
          if (campo === 'lossPercent') {
            return valor === undefined ? semCampo : { ...semCampo, [campo]: valor };
          }
          // O índice digitado é de alguém (Fase 40, RN-520): a média fica ao lado, na foto. Sem
          // índice, não há de onde ele veio.
          const { indexOrigin: _origem, indexAverage: foto, ...semOrigem } = semCampo;
          return valor === undefined
            ? semOrigem
            : {
                ...semOrigem,
                consumptionIndex: valor,
                indexOrigin: 'DIGITADO' as const,
                ...(foto === undefined ? {} : { indexAverage: foto }),
              };
        }),
      }));
    }

    case 'APPLY_INDEX_AVERAGE':
      return mapCatalogService(workspace, action.serviceKey, (servico) => ({
        ...servico,
        items: servico.items.map((item) => {
          if (item.key !== action.itemKey) return item;
          if (!action.follow) {
            // Sem índice não há digitado a manter: a foto sozinha não diz nada.
            return item.consumptionIndex === undefined
              ? item
              : { ...item, indexAverage: action.average };
          }
          return {
            ...item,
            consumptionIndex: action.average.rate,
            indexOrigin: 'MEDIA' as const,
            indexAverage: action.average,
          };
        }),
      }));

    case 'SET_STEEL_RATES': {
      // A taxa conferida pelo domínio: positiva, uma por bitola, na ordem das bitolas. O banco
      // recusaria o zero (`0053`); recusar aqui é dizer na tela, e não falhar calado depois.
      const taxas = normalizeSteelRates(action.rates);
      if (!taxas.ok) return comErro(workspace, taxas.error.message);
      if (workspace.catalog === undefined) return workspace;
      return {
        ...workspace,
        catalog: {
          ...workspace.catalog,
          disciplines: workspace.catalog.disciplines.map((disciplina) =>
            disciplina.key === action.disciplineKey
              ? comAListaDaTaxa(
                  disciplina,
                  action.element,
                  comAOrigem(listaDaTaxa(disciplina, action.element), taxas.value),
                )
              : disciplina,
          ),
        },
      };
    }

    case 'APPLY_STEEL_AVERAGE': {
      if (workspace.catalog === undefined) return workspace;
      return {
        ...workspace,
        catalog: {
          ...workspace.catalog,
          disciplines: workspace.catalog.disciplines.map((disciplina) => {
            if (disciplina.key !== action.disciplineKey) return disciplina;
            const antes = listaDaTaxa(disciplina, action.element) ?? [];
            const atual = antes.find((taxa) => taxa.gauge === action.gauge);
            // Sem a taxa não há digitada a manter: a foto sozinha não diz nada.
            if (!action.follow && atual === undefined) return disciplina;
            const nova: SteelRate = action.follow
              ? {
                  gauge: action.gauge,
                  kgPerM3: action.average.rate,
                  origin: 'MEDIA',
                  average: action.average,
                }
              : { ...atual!, average: action.average };
            const taxas = normalizeSteelRates([
              ...antes.filter((taxa) => taxa.gauge !== action.gauge),
              nova,
            ]);
            return taxas.ok ? comAListaDaTaxa(disciplina, action.element, taxas.value) : disciplina;
          }),
        },
      };
    }

    case 'CLEAR_SERVICE_MEASURED':
      return naTipologia(workspace, action.unitTypeId, (typology) =>
        comDecisaoDoServico(typology, action.serviceKey, ({ quantity }) =>
          quantity === undefined ? {} : { quantity },
        ),
      );

    case 'SET_SERVICE_QUANTITY': {
      const servico = servicoDoCatalogo(workspace, action.serviceKey);
      if (servico === undefined) return workspace;
      const quantidade =
        action.quantity === undefined ? undefined : decideInServiceUnit(servico, action.quantity);
      if (quantidade !== undefined && !quantidade.ok) {
        return comErro(workspace, quantidade.error.message);
      }
      return naTipologia(workspace, action.unitTypeId, (typology) =>
        comDecisaoDoServico(typology, action.serviceKey, ({ quantity: _anterior, ...resto }) => ({
          ...resto,
          ...(quantidade === undefined ? {} : { quantity: quantidade.value }),
        })),
      );
    }

    case 'SET_SERVICE_THICKNESS':
      return mapCatalogService(workspace, action.serviceKey, (servico) => {
        if (action.layerThickness === undefined || action.layerThickness <= 0) {
          const { layerThickness: _fora, ...semEspessura } = servico;
          return semEspessura;
        }
        return { ...servico, layerThickness: action.layerThickness };
      });

    case 'SET_SERVICE_SIDE_MARGIN': {
      const margem = action.sideMargin;
      // O banco recusa a negativa (`0049`): recusar aqui é dizer na tela, em vez de a gravação
      // falhar calada depois. Encurtaria a pedra para dentro do vão, que a obra não faz.
      if (margem !== undefined && (!Number.isFinite(margem) || margem < 0)) {
        return comErro(workspace, 'A margem da soleira e do peitoril não pode ser negativa.');
      }
      return mapCatalogService(workspace, action.serviceKey, (servico) => {
        // Zero é a soleira rente ao vão — o mesmo que não ter margem, e fica sem o campo.
        if (margem === undefined || margem === 0) {
          const { sideMargin: _fora, ...semMargem } = servico;
          return semMargem;
        }
        return { ...servico, sideMargin: margem };
      });
    }

    case 'ADD_SERVICE_ITEM': {
      // Sempre em maiúsculas: é como o catálogo de compras e a planilha do orçamento
      // escrevem, e um item em caixa baixa fica visivelmente de fora numa lista de
      // duzentos. A caixa não entra na identidade — a chave normaliza para minúsculas.
      const descricao = itemDescription(action.description);
      if (descricao === '') return workspace;

      // A disciplina entra no palpite da classe: é ela que separa "FERRAMENTAS" e
      // "EQUIPAMENTOS" do material, na planilha da obra.
      const disciplinaDoServico = workspace.catalog?.disciplines.find((disciplina) =>
        disciplina.services.some((servico) => servico.key === action.serviceKey),
      );

      // O mesmo item não entra duas vezes no mesmo serviço (RN-251), e duas das três
      // identidades dele não estão na descrição: o código de compras e a função. Repetir
      // qualquer uma das duas é o mesmo material, ou a mesma hora, contado de novo.
      const doServico = disciplinaDoServico?.services.find(
        (servico) => servico.key === action.serviceKey,
      );
      const codigo = action.code?.trim();
      const maoDeObra = (action.kind ?? 'MATERIAL') === 'MAO_DE_OBRA';
      if (
        !maoDeObra &&
        codigo !== undefined &&
        codigo !== '' &&
        doServico?.items.some(
          (item) => item.code !== undefined && normalizeName(item.code) === normalizeName(codigo),
        ) === true
      ) {
        return comErro(workspace, `Este serviço já tem um item com o código ${codigo}.`);
      }
      if (
        maoDeObra &&
        action.jobRoleId !== undefined &&
        doServico?.items.some((item) => item.jobRoleId === action.jobRoleId) === true
      ) {
        return comErro(workspace, 'Esta função já está em outro item deste serviço.');
      }

      // A chave sai daqui, e não da tela: é a mesma normalização que decide se o item já
      // existe, e recalculá-la do outro lado seria a mesma regra escrita duas vezes,
      // pronta para divergir na primeira mudança.
      const quantidade = action.quantity;
      const chaveDaQuantidade = itemQuantityKey(action.serviceKey, normalizeName(descricao));
      const comQuantidade = (atual: WorkspaceDraft): WorkspaceDraft => {
        if (quantidade === undefined) return atual;
        // Na tipologia **da mesa**, que é a que está lançando: o catálogo é da obra, a
        // quantidade é de quem a usa.
        const ativa = activeTypology(atual);
        return {
          ...atual,
          typologies: atual.typologies.map((typology) =>
            typology.unitTypeId === ativa.unitTypeId
              ? {
                  ...typology,
                  itemQuantities: { ...typology.itemQuantities, [chaveDaQuantidade]: quantidade },
                }
              : typology,
          ),
        };
      };

      return comQuantidade(
        mapCatalogService(workspace, action.serviceKey, (servico) => {
          const chave = normalizeName(descricao);
          // Já está lá: acrescentar de novo criaria duas linhas do mesmo material dentro
          // do mesmo serviço, e a consolidação somaria o item consigo mesmo.
          if (servico.items.some((item) => item.key === chave)) return servico;

          const kind =
            action.kind ??
            guessItemKind({
              disciplineName: disciplinaDoServico?.name ?? '',
              serviceName: servico.name,
              description: descricao,
              ...(action.unit === undefined || action.unit.trim() === ''
                ? {}
                : { unit: action.unit.trim() }),
            });
          return {
            ...servico,
            items: [
              ...servico.items,
              {
                key: chave,
                description: descricao,
                // Nasce sem quantidade de planilha: ele não veio de aba nenhuma, e é
                // digitando que ele ganha número.
                quantityBySheet: {},
                kind,
                kindOrigin:
                  action.kind === undefined ? ('PALPITE' as const) : ('DECIDIDO' as const),
                ...(action.jobRoleId === undefined || !maoDeObra
                  ? {}
                  : { jobRoleId: action.jobRoleId }),
                ...(action.code === undefined || action.code.trim() === ''
                  ? {}
                  : { code: action.code.trim() }),
                ...(action.unit === undefined || action.unit.trim() === ''
                  ? {}
                  : { unit: action.unit.trim() }),
                // "CA 50 - 10 MM" acrescentado no CORTE E DOBRA é o aço de 10 mm (Fase 27):
                // a mesma leitura da planilha, e só neste serviço (RN-366). A bitola dita por
                // quem acrescenta (a da referência, Fase 39) vence a leitura da descrição.
                ...(action.steelGauge !== undefined &&
                kind !== 'MAO_DE_OBRA' &&
                isSteelPurchaseService(servico.name)
                  ? { steelGauge: action.steelGauge }
                  : bitolaDoItem(servico.name, kind, descricao)),
              },
            ],
          };
        }),
      );
    }

    case 'ADD_DISCIPLINE':
      // Sem catálogo ainda, o cadastro nasce aqui: é a primeira disciplina que cria a
      // bancada de serviços, e não a importação.
      return {
        ...workspace,
        catalog: addDiscipline(workspace.catalog ?? emptyCatalog(), action.name),
      };

    case 'ADD_SERVICE':
      return workspace.catalog === undefined
        ? workspace
        : {
            ...workspace,
            catalog: addService(workspace.catalog, {
              disciplineKey: action.disciplineKey,
              name: action.name,
              ...(action.unit === undefined ? {} : { unit: action.unit }),
            }),
          };

    case 'SET_UNIT_ITEM': {
      const motivo = action.reason.trim();
      // Sem motivo não há divergência: é a única informação que não dá para reconstruir
      // depois, e a regra existe justamente porque ela sempre se perde.
      if (motivo === '') {
        return comErro(
          workspace,
          'Escreva o motivo antes de mudar o número deste apto — é ele que explica a diferença depois (RN-168).',
        );
      }

      const quantidade =
        action.effect === 'REPLACE' && typeof action.quantity === 'number'
          ? // Arredondado como o domínio arredonda: três casas é como se compra concreto,
            // argamassa e tinta, e um 0,30000000000000004 vindo do teclado não é decisão.
            roundQuantity(action.quantity, QUANTITY_DECIMALS)
          : undefined;

      if (action.effect === 'REPLACE' && !(Number.isFinite(quantidade ?? NaN) && quantidade! > 0)) {
        // Zerar não é "quantidade zero": é dizer que ali aquilo não acontece.
        return comErro(
          workspace,
          'Para zerar a quantidade neste apto, use "Não acontece" em vez de lançar zero.',
        );
      }

      const chave = itemQuantityKey(action.serviceKey, action.itemKey);
      const atual = workspace.unitShadows?.[action.unitKey];
      const linha: UnitShadowLine = {
        effect: action.effect,
        reason: motivo,
        by: action.by,
        byName: action.byName,
        at: new Date().toISOString(),
        ...(quantidade === undefined ? {} : { quantity: quantidade }),
      };

      return {
        ...workspace,
        unitShadows: {
          ...workspace.unitShadows,
          [action.unitKey]: { lines: { ...atual?.lines, [chave]: linha } },
        },
      };
    }

    case 'CLEAR_UNIT_ITEM': {
      const atual = workspace.unitShadows?.[action.unitKey];
      if (atual === undefined) return workspace;

      const chave = itemQuantityKey(action.serviceKey, action.itemKey);
      const { [chave]: _fora, ...restantes } = atual.lines;

      // Apto que voltou a seguir a tipologia não guarda registro nenhum: a ausência é o
      // que a leitura entende como "segue", e um objeto vazio seria estado sem sentido.
      const { [action.unitKey]: _semSombra, ...outros } = workspace.unitShadows ?? {};
      return {
        ...workspace,
        unitShadows:
          Object.keys(restantes).length === 0
            ? outros
            : { ...outros, [action.unitKey]: { lines: restantes } },
      };
    }

    case 'REMOVE_DISCIPLINE': {
      if (workspace.catalog === undefined) return workspace;
      const disciplina = workspace.catalog.disciplines.find(
        (item) => item.key === action.disciplineKey,
      );
      if (disciplina === undefined) return workspace;

      return semOsLancamentos(
        {
          ...workspace,
          catalog: {
            ...workspace.catalog,
            disciplines: workspace.catalog.disciplines.filter(
              (item) => item.key !== action.disciplineKey,
            ),
          },
        },
        disciplina.services.map((servico) => servico.key),
      );
    }

    case 'REMOVE_SERVICE': {
      if (workspace.catalog === undefined) return workspace;
      return semOsLancamentos(
        {
          ...workspace,
          catalog: {
            ...workspace.catalog,
            disciplines: workspace.catalog.disciplines.map((disciplina) => ({
              ...disciplina,
              services: disciplina.services.filter((servico) => servico.key !== action.serviceKey),
            })),
          },
        },
        [action.serviceKey],
      );
    }

    case 'SET_ITEM_KIND':
      return mapCatalogService(workspace, action.serviceKey, (servico) => ({
        ...servico,
        items: servico.items.map((item) => {
          if (item.key !== action.itemKey) return item;
          // A função só existe na mão de obra (RN-252): quem troca a classe para material
          // leva a função embora junto — tijolo não tem "quem executa".
          const { jobRoleId: _funcao, ...semFuncao } = item;
          const base = action.kind === 'MAO_DE_OBRA' ? item : semFuncao;
          // E quem vira mão de obra deixa a perda para trás: gente não se perde (D8).
          const { lossPercent: _perda, ...semPerda } = base;
          // E a bitola: hora de gente não é aço (RN-366). Voltando a material no CORTE E
          // DOBRA, a bitola volta pela descrição.
          const { steelGauge: _bitola, ...semBitola } =
            action.kind === 'MAO_DE_OBRA' ? semPerda : base;
          return {
            ...semBitola,
            ...bitolaDoItem(servico.name, action.kind, item.description),
            kind: action.kind,
            kindOrigin: 'DECIDIDO' as const,
          };
        }),
      }));

    case 'SET_ITEM_JOB_ROLE': {
      const servico = workspace.catalog?.disciplines
        .flatMap((disciplina) => disciplina.services)
        .find((candidato) => candidato.key === action.serviceKey);
      if (
        action.jobRoleId !== undefined &&
        servico?.items.some(
          (item) => item.key !== action.itemKey && item.jobRoleId === action.jobRoleId,
        ) === true
      ) {
        return comErro(workspace, 'Esta função já está em outro item deste serviço.');
      }
      return mapCatalogService(workspace, action.serviceKey, (atual) => ({
        ...atual,
        items: atual.items.map((item) => {
          if (item.key !== action.itemKey || item.kind !== 'MAO_DE_OBRA') return item;
          const { jobRoleId: _anterior, ...semFuncao } = item;
          return action.jobRoleId === undefined
            ? semFuncao
            : { ...semFuncao, jobRoleId: action.jobRoleId };
        }),
      }));
    }

    case 'SET_ITEM_CODE': {
      const catalogo = workspace.catalog;
      const servico = servicoDoCatalogo(workspace, action.serviceKey);
      const item = servico?.items.find((candidato) => candidato.key === action.itemKey);
      if (catalogo === undefined || servico === undefined || item === undefined) return workspace;

      const codigo = action.code?.trim() ?? '';
      // Não se compra hora de pedreiro do fornecedor (RN-166, RN-252). Tirar o código da mão
      // de obra passa: é o que limpa o código que uma planilha antiga tenha trazido nela.
      if (codigo !== '' && item.kind === 'MAO_DE_OBRA') {
        return comErro(workspace, 'Mão de obra não tem código de compras.');
      }
      // A mesma recusa do "Novo item": o mesmo código duas vezes no serviço é o mesmo
      // material contado de novo (RN-251).
      if (codigo !== '' && outroItemComCodigo(servico, item.key, codigo) !== undefined) {
        return comErro(workspace, `Este serviço já tem um item com o código ${codigo}.`);
      }

      // Quem recebe: o item, e os da lista que **continuam** valendo agora — a conferência é
      // contra o catálogo de antes da troca, que é o que a pessoa viu ao aceitar (RN-396).
      // Sem código não há o que propagar: tirar é do item, e só dele.
      const origem = itemQuantityKey(action.serviceKey, action.itemKey);
      const validos = new Set(
        codigo === ''
          ? []
          : itensParaPropagarCodigo(catalogo, action.serviceKey, action.itemKey, codigo).map(
              (alvo) => itemQuantityKey(alvo.serviceKey, alvo.itemKey),
            ),
      );
      const recebem = new Set([
        origem,
        ...(action.alsoTo ?? [])
          .map((alvo) => itemQuantityKey(alvo.serviceKey, alvo.itemKey))
          .filter((chave) => validos.has(chave)),
      ]);

      // A chave do item não muda, e por isso nada se rechaveia: `itemQuantities`,
      // `itemReasons`, `pieceQuantities` e as sombras dos aptos continuam achando o item.
      const comCodigo = (atual: CatalogItem): CatalogItem => {
        const { code: _anterior, ...semCodigo } = atual;
        return codigo === '' ? semCodigo : { ...semCodigo, code: codigo };
      };
      // O serviço e a disciplina que não recebem nada saem os mesmos objetos: a tela que
      // memoriza por identidade não se refaz por uma troca que não é dela.
      const noServico = (candidato: CatalogService): CatalogService =>
        candidato.items.some((doServico) =>
          recebem.has(itemQuantityKey(candidato.key, doServico.key)),
        )
          ? {
              ...candidato,
              items: candidato.items.map((doServico) =>
                recebem.has(itemQuantityKey(candidato.key, doServico.key))
                  ? comCodigo(doServico)
                  : doServico,
              ),
            }
          : candidato;
      return {
        ...workspace,
        catalog: {
          ...catalogo,
          disciplines: catalogo.disciplines.map((disciplina) => {
            const services = disciplina.services.map(noServico);
            return services.every(
              (servicoNovo, indice) => servicoNovo === disciplina.services[indice],
            )
              ? disciplina
              : { ...disciplina, services };
          }),
        },
      };
    }

    case 'REMOVE_SERVICE_ITEM': {
      const semItem = mapCatalogService(workspace, action.serviceKey, (servico) => ({
        ...servico,
        items: servico.items.filter((item) => item.key !== action.itemKey),
      }));

      // A decisão de quantidade morre com o item. Deixá-la para trás guardaria um número
      // pendurado em nada, que ressuscitaria com aparência de escolha no dia em que
      // alguém recadastrasse o material com o mesmo nome.
      const chave = itemQuantityKey(action.serviceKey, action.itemKey);
      return {
        ...semItem,
        typologies: semItem.typologies.map((typology) => {
          // A quantidade das peças importadas também (Fase 28): a peça continua, sem o item.
          const semPeca = semAsPecasDe(
            typology,
            (doItem) => doItem === chave,
            () => false,
          );
          if (semPeca.itemQuantities?.[chave] === undefined) return semPeca;
          const { [chave]: _fora, ...restantes } = semPeca.itemQuantities;
          const { [chave]: _motivo, ...outrosMotivos } = semPeca.itemReasons ?? {};
          return {
            ...semPeca,
            itemQuantities: restantes,
            ...(semPeca.itemReasons === undefined ? {} : { itemReasons: outrosMotivos }),
          };
        }),
      };
    }

    case 'REMOVE_TYPOLOGY': {
      const restantes = workspace.typologies.filter(
        (typology) => typology.unitTypeId !== action.unitTypeId,
      );
      // Bancada vazia não existe: tirar a última devolve uma em branco, e não uma tela
      // sem lugar nenhum para digitar.
      if (restantes.length === 0) return emptyWorkspace();
      // As divisas que encostavam nela ficam sem um dos lados: voltam a ser parede livre
      // de quem sobrou, em vez de apontar para ambiente que não existe mais. As sombras
      // dos aptos dela saem pelo mesmo motivo — sem a tipologia não há apto a divergir.
      return semAsSombrasOrfas(
        podarDivisas({
          ...workspace,
          typologies: restantes,
          activeId:
            workspace.activeId === action.unitTypeId
              ? restantes[0]!.unitTypeId
              : workspace.activeId,
        }),
      );
    }
  }
}
